/* File: config.js
 * Path: ananunadm/src/config.js
 */
import { T } from "./helpers/translator";
import { filterMenu } from "./helpers/menuFilter";
import { isUserAnanunaki } from "./helpers/platformMenuAccess";
import { hasPermission } from "./helpers/permissionResolver";
import { themeSettings } from "./theme/themeSettings";

const desktopRuntimeConfig =
	typeof window !== "undefined" && window.apitDesktopRuntimeConfig
		? window.apitDesktopRuntimeConfig
		: {};
const runtimeApiUrl = String(
	desktopRuntimeConfig.apiUrl || process.env.REACT_APP_API_URL || "https://api.apit.am"
).trim();
const runtimeApiVersion = String(
	desktopRuntimeConfig.apiVersion || process.env.REACT_APP_API_VERSION || "v3"
).trim();
const runtimeMainAppUrl = String(
	desktopRuntimeConfig.webOrigin || process.env.REACT_APP_MAIN_APP_URL || ""
)
	.trim()
	.replace(/\/$/, "");

const api = {
	booking: "booking",
	// Global search (cross-module, RBAC-gated per collection)
	search: {
		global: "search/global"
	},

	// Chat
	chat: {
		chatGroup: "/chat/chat-group", // For CRUD operations
		chatsWithMessages: "/chat/chats-with-messages", // For optimized fetch
		message: "/chat/message",
		messageReadReceipt: "/chat/message-read-receipt",
		messageSearch: "/chat/message-search",
		polls: "/chat/polls",
		pollsBatch: "/chat/polls/batch",
		locations: "/chat/locations",
		locationsBatch: "/chat/locations/batch",
		globalSettings: "/chat/global-settings",
		users: "/chat/users",
		userState: "/chat/user-state",
		clearHistory: (chatId) => `/chat/${chatId}/clear-history`,
		deleteForMe: (chatId) => `/chat/${chatId}/delete-for-me`,
		blockUser: (chatId) => `/chat/${chatId}/block-user`,
		unblockUser: (chatId) => `/chat/${chatId}/unblock-user`,
		blockedContacts: "/chat/blocked-contacts"
	},

	websiteChat: {
		root: "website-chat",
		stats: "website-chat/stats",
		settings: "website-chat/settings",
		sites: "website-chat/sites",
		conversations: "website-chat/conversations",
		departments: "website-chat/departments",
		cannedReplies: "website-chat/canned-replies"
	},

	// AI
	ai: {
		conversation: "ai/conversation",
		message: "ai/message",
		chat: "ai/chat",
		assistant: {
			status: "ai/status",
			message: "ai/assistant/message",
			overview: "ai/assistant/overview",
			sessions: "ai/assistant/sessions",
			session: (id) => `ai/assistant/sessions/${id}`,
			sessionUpdate: (id) => `ai/assistant/sessions/${id}`,
			sessionDelete: (id) => `ai/assistant/sessions/${id}`,
			executeTool: "ai/tools/execute",
			adminLogs: "ai/admin/logs",
			adminSettings: "ai/admin/settings",
			approve: (id) => `ai/approvals/${id}/approve`,
			reject: (id) => `ai/approvals/${id}/reject`,
			transcribeStatus: "ai/assistant/transcribe/status",
			transcribe: "ai/assistant/transcribe"
		}
	},

	// Content management
	cm: {
		// Address
		address: {
			map: "cm/address/map",
			state: "cm/address/state",
			city: "cm/address/city",
			zipCode: "cm/address/zip-code"
		},

		// Blog
		blog: {
			page: "cm/blog/page",
			list: "cm/blog/list"
		},

		// Events
		events: {
			page: "cm/events/page",
			list: "cm/events/list"
		},

		// Price List
		priceList: {
			page: "cm/price-list/page",
			list: "cm/price-list/list"
		},

		// For Business (same structure as blog)
		forBusiness: {
			page: "cm/for-business/page",
			list: "cm/for-business/list"
		},

		// Coin
		coin: {
			ground: "cm/coin/ground",
			step: "cm/coin/step",
			page: "cm/coin/page"
		},

		// Contact
		contact: {
			page: "cm/contact/page",
			part: "cm/contact/part",
			social: "cm/contact/social",
			form: "cm/contact/form",
			// Instagram
			instagram: {
				page: "cm/instagram/page",
				list: "cm/instagram"
			}
		},

		// Banner
		banner: {
			item: "cm/banner/item",
			slide: "cm/banner/slide"
		},

		// About us
		about: {
			page: "cm/about-us/page",
			item: "cm/about-us/item",
			whoWeAre: "cm/about-us/who-we-are",
			ourMission: "cm/about-us/our-mission"
		},

		// FAQ
		faq: {
			page: "cm/faq/page",
			question: "cm/faq/question"
		},

		// Pages
		pages: {
			certificatePromoCode: "cm/certificate-promo-code",
			deliveryAndPayment: "cm/delivery-and-payment",
			privacyPolicy: "cm/privacy-policy",
			termsAndConditions: "cm/terms-and-conditions",
			cookiePolicy: "cm/cookie-policy"
		},

		// Service
		service: {
			page: "cm/service/page",
			list: "cm/service/list"
		},

		// What You Get
		whatYouGet: "cm/what-you-get",

		// Services and Prices
		servicesAndPrices: {
			list: "cm/services-and-prices/list"
		},

		// Subscribe
		subscribe: {
			faq: "cm/subscribe/faq",
			step: "cm/subscribe/step",
			page: "cm/subscribe/page"
		},

		// Reviews
		review: "cm/review",

		// Companies
		company: "cm/company"
	},

	// CRM
	crm: {
		contact: "crm/contact",
		contactGroup: "crm/contact-group"
	},

	// Finance
	finance: {
		analytics: "finance/analytics",
		settings: "finance/settings",
		transactions: "finance/transaction",
		budgets: "finance/budget",
		goals: "finance/goal",
		cardIssuer: "finance/card-issuer",
		cards: "finance/card",
		accounts: "finance/account",
		categories: "finance/category",
		loans: "finance/loan",
		loanInstallments: "finance/loan-installment",
		loanPayments: "finance/loan-payment",
		workspaces: "finance/workspaces",
		budgetAllocations: "finance/budget-allocation",
		goalContributions: "finance/goal-contribution",
		receipts: "finance/receipt",
		receiptItems: "finance/receipt-item",
		categorizationRules: "finance/categorization-rule",
		recurringItems: "finance/recurring-item",
		vehicles: "finance/vehicle",
		insights: "finance/insight",
		workspaceProducts: "finance/workspace-product",
		shoppingLists: "finance/shopping-list",
		shoppingListItems: "finance/shopping-list-item",
		shoppingListPurchases: "finance/shopping-list-purchase",
		priceObservations: "finance/price-observation",
		platform: "finance/platform"
	},

	// E-Commerce
	ecommerce: {
		brand: "ecommerce/brand",
		category: "ecommerce/category",
		certificate: "ecommerce/certificate",
		field: "ecommerce/field",
		order: "ecommerce/order",
		shopping: "ecommerce/shop-purchase",
		store: "ecommerce/store",
		product: "ecommerce/product",
		promoCode: "ecommerce/promo-code",
		sale: "ecommerce/sale",
		tag: "ecommerce/tag"
	},

	// HR
	hr: {
		user: "hr/user",
		department: "hr/department",
		position: "hr/position",
		payroll: "hr/payroll",
		payrollCalculate: "hr/payroll/calculate",
		payrollApprove: "hr/payroll/approve",
		payrollPaymentEvents: "hr/payroll/payment-events",
		payrollPay: "hr/payroll/pay",
		payrollDashboard: "hr/payroll-dashboard",
		payrollEmployeeSummaryBase: "hr/payroll/employee",
		payrollEmployeePayslips: "hr/payroll/employee",
		salaryHistory: "hr/payroll/salary-history",
		attendanceLog: "hr/attendance-log",
		attendanceDayViewQuery: "hr/attendance-log/day-view/query",
		attendanceLiveOverview: "hr/attendance-log/live-overview",
		attendanceCurrentlyInOffice: "hr/attendance-log/currently-in-office",
		attendanceSyncDevices: "hr/attendance-log/sync-devices",
		attendancePeriodSummaryQuery: "hr/attendance-log/period-summary/query",
			attendanceDashboardInsightsQuery: "hr/attendance-log/dashboard-insights/query",
			attendanceOvertimeComment: "hr/attendance-log/overtime-comment",
			vacationPlans: "hr/attendance-log/vacation-plans",
			vacationPlansQuery: "hr/attendance-log/vacation-plans/query",
			attendanceExplanation: "hr/attendance-explanation",
		leave: "hr/leave",
		leaveBalance: "hr/leave/balance",
		hrSettings: "hr/hr-settings",
		bonusPenalty: "hr/bonus-penalty",
		attendanceManualStatus: "hr/attendance-manual-status",
		recruitmentJobs: "hr/recruitment/jobs",
		recruitmentApplications: "hr/recruitment/applications",
		recruitmentInterviews: "hr/recruitment/interviews",
		recruitmentScorecards: "hr/recruitment/scorecards",
		recruitmentAssessments: "hr/recruitment/assessments",
		recruitmentAssessmentResults: "hr/recruitment/assessments/results",
		recruitmentAi: "hr/recruitment/ai",
		recruitmentOffers: "hr/recruitment/offers",
		recruitmentOnboarding: "hr/recruitment/onboarding",
		recruitmentDashboard: "hr/recruitment/dashboard"
	},

	// IT — room is now managed by the Office module
	it: {
		room: "office/rooms",
		cpu: "it/cpu",
		gpu: "it/gpu",
		ram: "it/ram",
		rom: "it/rom",
		warrantyProvider: "it/warranty-provider",
		keyboard: "it/keyboard",
		monitor: "it/monitor",
		mouse: "it/mouse",
		pc: "it/pc",
		laptop: "it/laptop",
		printer: "it/printer",
		router: "it/router",
		telephone: "it/telephone",
		attendanceDevice: "it/attendance-device",
		smb: "it/smb",
		software: "it/software",
		switch: "it/switch"
	},

	// Media
	media: {
		album: "media/album",
		photo: "media/photo",
		video: "media/video",
		files: "media/files",
		gallery: "media/gallery"
	},

	files: {
		root: "files",
		providers: "files/providers",
		mounts: "files/mounts",
		browse: "files/browse",
		activity: "files/activity"
	},

	// Database
	database: {
		branch: "database/branch",
		city: "database/city",
		community: "database/community",
		project: "database/project",
		projectType: "database/type",
		projectPath: "database/project-path",
		stepTemplate: "database/step-template",
		projectEvent: "database/project-event",
		projectCounter: "database/project-counter",
		stepCounter: "database/step-counter",
		projectAutomationRule: "database/project-automation-rule",
		projectWithSelectedSteps: "database/project/with-selected-steps",
		village: "database/village",
		step: "database/step",
		voltage: "database/voltage"
	},

	// Radio
	radio: {
		track: "radio/track",
		stream: "radio/stream",
		playlist: "radio/playlist",
		artist: "radio/artist"
	},

	// Scheduling (Time-based music scheduling)
	scheduling: {
		tracks: "scheduling/tracks",
		trackEnergy: "scheduling/tracks", // PUT /:trackId/energy
		bulkEnergy: "scheduling/tracks/bulk-energy",
		playlistEnergy: "scheduling/playlists", // PUT /:playlistId/set-all-energy
		streams: "scheduling/streams", // GET /:streamId
		streamEnable: "scheduling/streams", // PUT /:streamId/enable
		streamDefaultEnergy: "scheduling/streams", // PUT /:streamId/default-energy
		rules: "scheduling/streams", // POST/PUT/DELETE /:streamId/rules
		examples: "scheduling/examples",
		energyDistribution: "scheduling/energy-distribution",
		preview: "scheduling/preview" // GET /:streamId
	},

	// System
	system: {
		role: "system/role",
		permission: "system/permission",
		roleEditorBundle: "system/role-editor-bundle",
		platformAdmin: "system/platform-admin",
		dataRetention: "system/data-retention",
		notification: "system/notification",
		upload: "system/upload",
		user: "system/user",
		activityTracker: "system/activity-tracker",
		events: "system/events"
	},

	// Ticket system
	ticketSystem: {
		item: "ticket-system/ticket",
		problem: "ticket-system/problem",
		status: "ticket-system/status",
		ticket: "ticket-system/ticket",
		ticketSettings: "ticket-system/ticket-settings",
		problemGroup: "ticket-system/problem-group",
		ticketDomain: "ticket-system/ticket-domain",
		relatedList: "ticket-system/related-list",
		smbAgent: "ticket-system/smb-agent",
		priority: "ticket-system/priority",
		ticketType: "ticket-system/ticket-type"
	},

	// Todo
	todo: {
		board: "todo/board",
		category: "todo/category",
		task: "todo/task",
		tag: "todo/tag",
		label: "todo/label",
		boardGroup: "todo/board-group",
		categoryGroup: "todo/category-group",
		workloadMatrix: "todo/workload-matrix"
	},

	// Calendar (universal module, separate menu item)
	calendar: {
		event: "calendar/event",
		calendar: "calendar/calendar"
	},
	calendarIntegrations: "calendar-integrations",
	googleServices: "google-services",
	meetings: "meetings",
	reminders: "reminders",

	// Office Map (standalone module)
	officeMap: {
		map: "office-map",
		room: "office-map/room",
		networkCable: "office-map/network-cable",
		office360: "office-360"
	},

	// World Map (standalone module)
	worldMap: {
		layers: "world-map/layers"
	},

	// Office / Workplace module
	office: {
		dashboard: "office/dashboard",
		organizations: "office/organizations",
		locations: "office/locations",
		floors: "office/floors",
		rooms: "office/rooms",
		desks: "office/desks",
		resources: "office/resources",
		bookingPolicies: "office/booking-policies",
		bookings: "office/room-bookings",
		bookingTimeline: "office/room-bookings/timeline",
		bookingUseNow: "office/room-bookings/use-now",
		suggestRooms: "office/room-bookings/suggest-rooms",
		checkConflict: "office/room-bookings/check-conflict",
		roomAvailability: "office/rooms",
		floorMap: "office/map",
		inventory: "office/inventory",
		inventoryDashboard: "office/inventory/dashboard",
		inventoryCategories: "office/inventory/categories",
		inventoryTransactions: "office/inventory/transactions",
		inventoryMaintenance: "office/inventory/maintenance",
		serviceRequests: "office/service-requests",
		inventoryStorageLocations: "office/inventory/storage-locations",
		inventorySettings: "office/inventory/settings",
		inventoryExport: "office/inventory/export.xlsx",
		inventoryImportTemplate: "office/inventory/import-template.xlsx",
		inventoryImportPreview: "office/inventory/import/preview",
		inventoryImportConfirm: "office/inventory/import/confirm",
		inventoryQrLabels: "office/inventory/qr-labels"
	},

	// Security module
	security: {
		overview: "security/overview",
		surveillanceDashboard: "security/surveillance-system/dashboard",
		clients: "security/clients",
		devices: "security/devices",
		statuses: "security/devices/statuses",
		cameras: "security/cameras",
		nvr: "security/nvr",
		policies: "security/policies",
		ipPolicy: "system/ip-policy",
		securitySettings: "security/settings",
		events: "security/events",
		media: "security/media",
		alerts: "security/alerts",
		sessions: "security/sessions",
		healthLogs: "security/health-logs",
		face: "security/face",
		faceStatus: "security/face/status",
		faceEvents: "security/face/events",
		faceUserProfile: (userId) => `security/face/users/${userId}/profile`,
		faceUserEnroll: (userId) => `security/face/users/${userId}/enroll`,
		faceCameraSettings: (cameraId) => `security/face/cameras/${cameraId}/settings`,
		faceCameraTest: (cameraId) => `security/face/cameras/${cameraId}/test`
	},

	knowledge: {
		dashboard: "knowledge",
		articles: "knowledge/articles",
		categories: "knowledge/categories",
		search: "knowledge/search",
		autoDocs: "knowledge/auto-docs",
		settings: "knowledge/settings"
	},

	// Forms & Surveys
	forms: {
		list: "forms",
		templates: "forms/templates",
		active: "forms/active",
		byId: (id) => `forms/${id}`,
		publish: (id) => `forms/${id}/publish`,
		archive: (id) => `forms/${id}/archive`,
		duplicate: (id) => `forms/${id}/duplicate`,
		status: (id, action) => `forms/${id}/status/${action}`,
		responses: (id) => `forms/${id}/responses`,
		response: (id, responseId) => `forms/${id}/responses/${responseId}`,
		analytics: (id) => `forms/${id}/analytics`,
		exportCsv: (id) => `forms/${id}/export.csv`,
		answer: (id) => `forms/answer/${id}`,
		// public (no auth)
		public: (slug) => `public/forms/${slug}`,
		publicVerify: (slug) => `public/forms/${slug}/verify-password`,
		publicAnswer: (slug) => `public/forms/${slug}/answer`
	},

	// Company Automation Center
	cac: {
		rules: "cac/rules",
		catalog: "cac/catalog",
		logs: "cac/logs"
	},

	// API
	MAIN_APP_URL: runtimeMainAppUrl,
	login: "/login/private",
	logout: "/login/private/logout",
	changePassword: "/login/private/change-password",
	resetPasswordValidate: "/login/private/password-reset/validate",
	resetPasswordComplete: "/login/private/password-reset/complete",
	verify: "/verify/private",
	tokenAddress: "private-token",
	API_URL: runtimeApiUrl,
	API_Version: runtimeApiVersion,
	ONLYOFFICE_SERVER_URL: process.env.REACT_APP_ONLYOFFICE_SERVER_URL || "http://localhost:8082",
	PEERJS_PORT: process.env.REACT_APP_PEERJS_PORT
};

const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

const session = {
	oneMonthMs: ONE_MONTH_MS,
	inactivityLogoutMs: Number(process.env.REACT_APP_INACTIVITY_LOGOUT_MS) || ONE_MONTH_MS,
	inactivityWarningMs: Number(process.env.REACT_APP_INACTIVITY_WARNING_MS) || 60 * 1000
};

const maps = {
	googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
};

const steps = [
	{
		_id: 1,
		name: {
			hy: "Տեղազննում",
			en: "Visitation",
			ru: "Посещение",
			tr: "Ziyaret",
			ar: "زيارة",
			zh: "访问",
			fa: "بازدید",
			he: "ביקור",
			fr: "Visite",
			es: "Visita",
			pt: "Visitação"
		}
	},
	{
		_id: 2,
		name: {
			hy: "Գեոլոգիական եզրակացություն",
			en: "Geological exploration",
			ru: "Геологическое исследование",
			tr: "Jeolojik keşif",
			ar: "استكشاف جيولوجي",
			zh: "地质勘探",
			fa: "اکتشاف زمین شناسی",
			he: "חקירה גאולוגית",
			fr: "Exploration géologique",
			es: "Exploración geológica",
			pt: "Exploração geológica"
		},
		bypass: true
	},
	{
		_id: 3,
		name: {
			hy: "Նախագծողի ընտրություն",
			en: "Selection of the project developer",
			ru: "Выбор застройщика",
			tr: "Proje geliştiricisinin seçimi",
			ar: "اختيار مطور المشروع",
			zh: "选择项目开发商",
			fa: "انتخاب توسعه دهنده پروژه",
			he: "בחירת מפתח הפרויקט",
			fr: "Sélection du développeur de projet",
			es: "Selección del desarrollador del proyecto",
			pt: "Seleção do desenvolvedor do projeto"
		}
	},
	{
		_id: 4,
		name: {
			hy: "Գծագրում",
			en: "Design",
			ru: "Проектирование",
			tr: "Tasarım",
			ar: "تصميم",
			zh: "设计",
			fa: "طرح",
			he: "עיצוב",
			fr: "Conception",
			es: "Diseño",
			pt: "Desenho"
		}
	},
	{
		_id: 5,
		name: {
			hy: "Գծագրի ստուգում",
			en: "Checking the design",
			ru: "Проверка проекта",
			tr: "Tasarımın kontrolü",
			ar: "فحص التصميم",
			zh: "设计检查",
			fa: "بررسی طرح",
			he: "בדיקת עיצוב",
			fr: "Vérification de la conception",
			es: "Comprobación del diseño",
			pt: "Verificação do design"
		}
	},
	{
		_id: 6,
		name: {
			hy: "Տրամադրում համաձայնեցման",
			en: "Agreement",
			ru: "Согласование",
			tr: "Anlaşma",
			ar: "اتفاق",
			zh: "协议",
			fa: "توافق",
			he: "הסכמה",
			fr: "Accord",
			es: "Acuerdo",
			pt: "Acordo"
		}
	},
	{
		_id: 7,
		name: {
			hy: "ԷՀԳ",
			en: "EHGS",
			ru: "ЭХГС",
			tr: "EHGS",
			ar: "EHGS",
			zh: "EHGS",
			fa: "EHGS",
			he: "EHGS",
			fr: "EHGS",
			es: "EHGS",
			pt: "EHGS"
		},
		bypass: true
	},
	{
		_id: 8,
		name: {
			hy: "Գեոդեզիա",
			en: "Geodesy",
			ru: "Геодезия",
			tr: "Jeodezi",
			ar: "المساحة",
			zh: "大地测量学",
			fa: "ژئودزی",
			he: "גאודזיה",
			fr: "Géodésie",
			es: "Geodesia",
			pt: "Geodésia"
		},
		bypass: true
	},
	{
		_id: 9,
		name: {
			hy: "ԷՀԳ եզրակացություն",
			en: "EHGS exploration",
			ru: "ЭХГС исследование",
			tr: "EHGS keşfi",
			ar: "استكشاف EHGS",
			zh: "EHGS 探索",
			fa: "اکتشاف EHGS",
			he: "חקירת EHGS",
			fr: "Exploration EHGS",
			es: "Exploración EHGS",
			pt: "Exploração EHGS"
		},
		bypass: true
	},
	{
		_id: 10,
		name: {
			hy: "Համաձայնեցման համակարգում",
			en: "Agreement system",
			ru: "Система согласования",
			tr: "Anlaşma sistemi",
			ar: "نظام الاتفاق",
			zh: "协议系统",
			fa: "سیستم توافق",
			he: "מערכת הסכמה",
			fr: "Système d'accord",
			es: "Sistema de acuerdo",
			pt: "Sistema de acordo"
		}
	},
	{
		_id: 11,
		name: {
			hy: "Շահագրգիռների համաձայնեցում",
			en: "Agreement on equipment",
			ru: "Согласование оборудования",
			tr: "Ekipman üzerinde anlaşma",
			ar: "اتفاق على المعدات",
			zh: "设备协议",
			fa: "توافق بر تجهیزات",
			he: "הסכמה על ציוד",
			fr: "Accord sur l'équipement",
			es: "Acuerdo sobre el equipo",
			pt: "Acordo sobre o equipamento"
		},
		bypass: true
	},
	{
		_id: 12,
		name: {
			hy: "Շահագրգիռների տրամադրում",
			en: "Supply of equipment",
			ru: "Поставка оборудования",
			tr: "Ekipman tedariği",
			ar: "توريد المعدات",
			zh: "设备供应",
			fa: "تأمین تجهیزات",
			he: "אספקת ציוד",
			fr: "Fourniture d'équipement",
			es: "Suministro de equipos",
			pt: "Fornecimento de equipamentos"
		},
		bypass: true
	},
	{
		_id: 13,
		name: {
			hy: "ՏԻՄ դիմում",
			en: "TIM application",
			ru: "TIM заявка",
			tr: "TIM başvurusu",
			ar: "طلب TIM",
			zh: "TIM 申请",
			fa: "درخواست TIM",
			he: "בקשת TIM",
			fr: "Demande TIM",
			es: "Solicitud TIM",
			pt: "Aplicação TIM"
		}
	},
	{
		_id: 14,
		name: {
			hy: "ՏԻՄ համաձայնեցում",
			en: "TIM agreement",
			ru: "TIM согласование",
			tr: "TIM anlaşması",
			ar: "اتفاق TIM",
			zh: "TIM 协议",
			fa: "توافق TIM",
			he: "הסכם TIM",
			fr: "Accord TIM",
			es: "Acuerdo TIM",
			pt: "Acordo TIM"
		}
	},
	{
		_id: 15,
		name: {
			hy: "Նախնակաան ZIP Փաթեթի տրամադրում",
			en: "Provision of preliminary ZIP package",
			ru: "Предварительная поставка ZIP пакета",
			tr: "Ön ZIP paketi sağlanması",
			ar: "توفير حزمة ZIP الأولية",
			zh: "提供初步的ZIP包",
			fa: "تأمین بسته ZIP اولیه",
			he: "אספקת חבילת ZIP ראשונית",
			fr: "Fourniture du paquet ZIP préliminaire",
			es: "Provisión del paquete ZIP preliminar",
			pt: "Fornecimento do pacote ZIP preliminar"
		}
	},
	{
		_id: 16,
		name: {
			hy: "Նախնակաան ՀԷՑ հանձնում",
			en: "Provision of preliminary EHGS",
			ru: "Предварительная ЭХГС поставка",
			tr: "Ön EHGS sağlanması",
			ar: "توفير EHGS الأولية",
			zh: "توفير EHGS الأولية",
			fa: "تأمین EHGS اولیه",
			he: "אספקת EHGS ראשונית",
			fr: "Fourniture préliminaire EHGS",
			es: "Provisión preliminar EHGS",
			pt: "Fornecimento preliminar EHGS"
		}
	},
	{
		_id: 17,
		name: {
			hy: "Գանձապետարանի դիմում",
			en: "Application for a building permit",
			ru: "Заявление на строительное разрешение",
			tr: "Başvuru inşaat izni",
			ar: "طلب تصريح بناء",
			zh: "建筑许可申请",
			fa: "درخواست مجوز ساخت و ساز",
			he: "בקשה להיתר בנייה",
			fr: "Demande de permis de construire",
			es: "Solicitud de permiso de construcción",
			pt: "Pedido de licença de construção"
		},
		bypass: true
	},
	{
		_id: 18,
		name: {
			hy: "Գանձապետարանի հաստատում",
			en: "Approval of the building permit",
			ru: "Утверждение строительного разрешения",
			tr: "Onaylama inşaat izni",
			ar: "الموافقة على تصريح البناء",
			zh: "الموافقة على تصريح البناء",
			fa: "تأیید مجوز ساخت و ساز",
			he: "אישור היתר בנייה",
			fr: "Approbation du permis de construire",
			es: "Aprobación del permiso de construcción",
			pt: "Aprovação da licença de construção"
		},
		bypass: true
	},
	{
		_id: 19,
		name: {
			hy: "Շին թույլտվության դիմում",
			en: "Application for construction permission",
			ru: "Заявление на разрешение на строительство",
			tr: "İnşaat izni başvurusu",
			ar: "طلب تصريح بناء",
			zh: "建筑许可申请",
			fa: "درخواست مجوز ساخت و ساز",
			he: "בקשה להיתר בנייה",
			fr: "Demande de permis de construire",
			es: "Solicitud de permiso de construcción",
			pt: "Pedido de licença de construção"
		},
		bypass: true
	},
	{
		_id: 20,
		name: {
			hy: "Շին թույլտվության տրամադրում",
			en: "Granting of construction permission",
			ru: "Предоставление разрешения на строительство",
			tr: "İnşaat izninin verilmesi",
			ar: "منح تصريح البناء",
			zh: "授予建筑许可",
			fa: "صدور مجوز ساخت و ساز",
			he: "מתן היתר בנייה",
			fr: "Octroi du permis de construire",
			es: "Concesión del permiso de construcción",
			pt: "Concessão da licença de construção"
		},
		bypass: true
	},
	{
		_id: 21,
		name: {
			hy: "Ծավալի կազմում",
			en: "Volume formation",
			ru: "Формирование объема",
			tr: "Hacim oluşumu",
			ar: "تشكيل الحجم",
			zh: "体积形成",
			fa: "تشکیل حجم",
			he: "יצירת נפח",
			fr: "Formation de volume",
			es: "Formación de volumen",
			pt: "Formação de volume"
		}
	},
	{
		_id: 22,
		name: {
			hy: "Շինարարական բաժնին առաջադրանքի տրամադրում",
			en: "Provision of a contract to the construction department",
			ru: "Предоставление договора в строительный отдел",
			tr: "İnşaat departmanına sözleşme sağlanması",
			ar: "توفير عقد لقسم البناء",
			zh: "向建筑部门提供合同",
			fa: "تأمین قرارداد به بخش ساخت و ساز",
			he: "מתן חוזה למחלקת הבנייה",
			fr: "Fourniture d'un contrat au département de la construction",
			es: "Provisión de un contrato al departamento de construcción",
			pt: "Fornecimento de um contrato ao departamento de construção"
		},
		bypass: true
	},
	{
		_id: 23,
		name: {
			hy: "Շինարարական առաջադրանքի հանձնում",
			en: "Provision of a construction contract",
			ru: "Предоставление строительного договора",
			tr: "İnşaat sözleşmesi sağlanması",
			ar: "توفير عقد البناء",
			zh: "提供建筑合同",
			fa: "تأمین قرارداد ساخت و ساز",
			he: "מתן חוזה בנייה",
			fr: "Fourniture d'un contrat de construction",
			es: "Provisión de un contrato de construcción",
			pt: "Fornecimento de um contrato de construção"
		},
		bypass: true
	},
	{
		_id: 24,
		name: {
			hy: "Ծավալի ստուգում",
			en: "Checking the volume",
			ru: "Проверка объема",
			tr: "Hacim kontrolü",
			ar: "التحقق من الحجم",
			zh: "检查体积",
			fa: "بررسی حجم",
			he: "בדיקת הנפח",
			fr: "Vérification du volume",
			es: "Comprobación del volumen",
			pt: "Verificação do volume"
		}
	},
	{
		_id: 25,
		name: {
			hy: "Ծավալի ուղղում",
			en: "Volume adjustment",
			ru: "Регулировка объема",
			tr: "Hacim ayarı",
			ar: "تعديل الحجم",
			zh: "体积调整",
			fa: "تنظیم حجم",
			he: "התאמת נפח",
			fr: "Ajustement du volume",
			es: "Ajuste de volumen",
			pt: "Ajuste de volume"
		}
	},
	{
		_id: 26,
		name: {
			hy: "Ծավալի կրկնակի ստուգում",
			en: "Checking the volume of the duplicate",
			ru: "Проверка объема дубликата",
			tr: "Yinelenen hacim kontrolü",
			ar: "التحقق من حجم النسخة المكررة",
			zh: "检查重复的体积",
			fa: "بررسی حجم نسخه تکراری",
			he: "בדיקת נפח הכפילות",
			fr: "Vérification du volume du duplicata",
			es: "Comprobación del volumen del duplicado",
			pt: "Verificação do volume do duplicado"
		}
	},
	{
		_id: 27,
		name: {
			hy: "Ծավալների տրամադրում նախահաշվի",
			en: "Provision of volumes in advance",
			ru: "Предоставление объемов вперед",
			tr: "Hacimlerin önceden sağlanması",
			ar: "توفير الأحجام مسبقًا",
			zh: "提前提供体积",
			fa: "تأمین حجم ها به صورت پیشاپیش",
			he: "מתן נפחים מראש",
			fr: "Fourniture des volumes à l'avance",
			es: "Provisión de volúmenes por adelantado",
			pt: "Fornecimento de volumes com antecedência"
		}
	},
	{
		_id: 28,
		name: {
			hy: "Նախահաշվի համակարգում",
			en: "Preliminary accounting system",
			ru: "Система предварительного учета",
			tr: "Ön muhasebe sistemi",
			ar: "نظام المحاسبة الأولية",
			zh: "初步会计系统",
			fa: "سیستم حسابداری مقدماتی",
			he: "מערכת חשבונאות ראשונית",
			fr: "Système de comptabilité préliminaire",
			es: "Sistema de contabilidad preliminar",
			pt: "Sistema de contabilidade preliminar"
		}
	},
	{
		_id: 29,
		name: {
			hy: "Նախահաշվի սկիզբ",
			en: "Start of preliminary accounting",
			ru: "Начало предварительного учета",
			tr: "Ön muhasebenin başlaması",
			ar: "بدء المحاسبة الأولية",
			zh: "初步会计的开始",
			fa: "شروع حسابداری مقدماتی",
			he: "התחלת חשבונאות ראשונית",
			fr: "Début de la comptabilité préliminaire",
			es: "Inicio de la contabilidad preliminar",
			pt: "Início da contabilidade preliminar"
		},
		bypass: true
	},
	{
		_id: 30,
		name: {
			hy: "Նախահաշվի ստուգողի ընտրությունը",
			en: "Selection of the preliminary accounting checker",
			ru: "Выбор проверяющего предварительный учет",
			tr: "Ön muhasebe denetleyicisinin seçimi",
			ar: "اختيار مدقق المحاسبة الأولية",
			zh: "选择初步会计检查员",
			fa: "انتخاب بازرس حسابداری مقدماتی",
			he: "בחירת בודק החשבונאות הראשונית",
			fr: "Sélection du vérificateur de la comptabilité préliminaire",
			es: "Selección del verificador de la contabilidad preliminar",
			pt: "Seleção do verificador da contabilidade preliminar"
		},
		bypass: true
	},
	{
		_id: 31,
		name: {
			hy: "Նախահաշվի ստուգում",
			en: "Checking the preliminary accounting",
			ru: "Проверка предварительного учета",
			tr: "Ön muhasebenin kontrolü",
			ar: "التحقق من المحاسبة الأولية",
			zh: "检查初步会计",
			fa: "بررسی حسابداری مقدماتی",
			he: "בדיקת החשבונאות הראשונית",
			fr: "Vérification de la comptabilité préliminaire",
			es: "Comprobación de la contabilidad preliminar",
			pt: "Verificação da contabilidade preliminar"
		},
		bypass: true
	},
	{
		_id: 32,
		name: {
			hy: "նախահաշվողի հաստատողի ընտրություն",
			en: "Selection of the preliminary accounting approver",
			ru: "Выбор утверждающего предварительный учет",
			tr: "Ön muhasebe onaylayıcısının seçimi",
			ar: "اختيار الموافق على المحاسبة الأولية",
			zh: "选择初步会计批准人",
			fa: "انتخاب تایید کننده حسابداری مقدماتی",
			he: "בחירת מאשר החשבונאות הראשונית",
			fr: "Sélection de l'approbateur de la comptabilité préliminaire",
			es: "Selección del aprobador de la contabilidad preliminar",
			pt: "Seleção do aprovador da contabilidade preliminar"
		},
		bypass: true
	},
	{
		_id: 33,
		name: {
			hy: "Նախահաշվի հաստատում",
			en: "Approval of preliminary accounting",
			ru: "Утверждение предварительного учета",
			tr: "Ön muhasebenin onayı",
			ar: "الموافقة على المحاسبة الأولية",
			zh: "初步会计的批准",
			fa: "تأیید حسابداری مقدماتی",
			he: "אישור החשבונאות הראשונית",
			fr: "Approbation de la comptabilité préliminaire",
			es: "Aprobación de la contabilidad preliminar",
			pt: "Aprovação da contabilidade preliminar"
		},
		bypass: true
	},
	{
		_id: 34,
		name: {
			hy: "Փաթեթի կազմում",
			en: "Compilation of the package",
			ru: "Составление пакета",
			tr: "Paketin derlenmesi",
			ar: "تجميع الحزمة",
			zh: "打包编译",
			fa: "ترکیب بسته",
			he: "הרכבת החבילה",
			fr: "Compilation du paquet",
			es: "Compilación del paquete",
			pt: "Compilação do pacote"
		}
	},
	{
		_id: 35,
		name: {
			hy: "Փաթեթի ստուգում",
			en: "Checking the package",
			ru: "Проверка пакета",
			tr: "Paketin kontrolü",
			ar: "التحقق من الحزمة",
			zh: "检查包",
			fa: "بررسی بسته",
			he: "בדיקת החבילה",
			fr: "Vérification du paquet",
			es: "Comprobación del paquete",
			pt: "Verificação do pacote"
		}
	},
	{
		_id: 36,
		name: {
			hy: "Տրամադրում բազմացման",
			en: "Granting of multiplication",
			ru: "Предоставление умножения",
			tr: "Çoğaltma verilmesi",
			ar: "منح التكرار",
			zh: "授予乘法",
			fa: "اعطای تکثیر",
			he: "מתן כפל",
			fr: "Octroi de la multiplication",
			es: "Concesión de la multiplicación",
			pt: "Concessão da multiplicação"
		}
	},
	{
		_id: 37,
		name: {
			hy: "Նախագծի բազմացում",
			en: "Multiplication of the project",
			ru: "Умножение проекта",
			tr: "Projenin Çoğaltılması",
			ar: "تكرار المشروع",
			zh: "项目的复制",
			fa: "تکثیر پروژه",
			he: "כפילות הפרויקט",
			fr: "Multiplication du projet",
			es: "Multiplicación del proyecto",
			pt: "Multiplicação do projeto"
		}
	},
	{
		_id: 38,
		name: {
			hy: "ZIP տրամադրում ՀԷՑ",
			en: "ZIP provision to EHGS",
			ru: "Предоставление ZIP ЭХГС",
			tr: "ZIP'nin EHGS'ye Sağlanması",
			ar: "توفير ZIP إلى EHGS",
			zh: "向EHGS提供ZIP",
			fa: "ارائه ZIP به EHGS",
			he: "מתן ZIP ל-EHGS",
			fr: "Fourniture de ZIP à EHGS",
			es: "Provisión de ZIP a EHGS",
			pt: "Fornecimento de ZIP para EHGS"
		}
	},
	{
		_id: 39,
		name: {
			hy: "Գանձապետարանի դիմումի համակարգում",
			en: "Building permit system",
			ru: "Система строительного разрешения",
			tr: "İnşaat izin sistemi",
			ar: "نظام تصريح البناء",
			zh: "建筑许可系统",
			fa: "سیستم مجوز ساخت و ساز",
			he: "מערכת היתר בנייה",
			fr: "Système de permis de construire",
			es: "Sistema de permiso de construcción",
			pt: "Sistema de licença de construção"
		},
		bypass: true
	},
	{
		_id: 40,
		name: {
			hy: "Գանձապետարանի դիմում",
			en: "Application for a building permit",
			ru: "Заявление на строительное разрешение",
			tr: "Başvuru inşaat izni",
			ar: "طلب تصريح البناء",
			zh: "建筑许可申请",
			fa: "درخواست مجوز ساخت و ساز",
			he: "בקשה להיתר בנייה",
			fr: "Demande de permis de construire",
			es: "Solicitud de permiso de construcción",
			pt: "Pedido de licença de construção"
		},
		bypass: true
	},
	{
		_id: 41,
		name: {
			hy: "Գանձապետարանի հաստատում",
			en: "Approval of the building permit",
			ru: "Утверждение строительного разрешения",
			tr: "Onaylama inşaat izni",
			ar: "الموافقة على تصريح البناء",
			zh: "建筑许可的批准",
			fa: "تأیید مجوز ساخت و ساز",
			he: "אישור היתר בנייה",
			fr: "Approbation du permis de construire",
			es: "Aprobación del permiso de construcción"
		},
		bypass: true
	},
	{
		_id: 42,
		name: {
			hy: "Շին թույլտվության դիմում",
			en: "Application for construction permission",
			ru: "Заявление на разрешение на строительство",
			tr: "İnşaat izni başvurusu",
			ar: "طلب إذن البناء",
			zh: "建筑许可申请",
			fa: "درخواست مجوز ساخت و ساز",
			he: "בקשה לאישור בנייה",
			fr: "Demande de permis de construire",
			es: "Solicitud de permiso de construcción"
		},
		bypass: true
	},
	{
		_id: 43,
		name: {
			hy: "Շին թույլտվության տրամադրում",
			en: "Granting of construction permission",
			ru: "Предоставление разрешения на строительство",
			tr: "İnşaat izninin verilmesi",
			ar: "منح إذن البناء",
			zh: "授予建筑许可",
			fa: "صدور مجوز ساخت و ساز",
			he: "מתן אישור בנייה",
			fr: "Octroi de permis de construire",
			es: "Concesión de permiso de construcción",
			pt: "Concessão de permissão de construção"
		},
		bypass: true
	},
	{
		_id: 44,
		name: {
			hy: "ՇԹ-ի տրամադրում ՀԷՑ",
			en: "Granting of STP to EHGS",
			ru: "Предоставление СТП ЭХГС",
			tr: "STP'nin EHGS'ye Verilmesi",
			ar: "منح STP إلى EHGS",
			zh: "向EHGS授予STP",
			fa: "اعطای STP به EHGS",
			he: "מתן STP ל-EHGS",
			fr: "Attribution de STP à EHGS",
			es: "Concesión de STP a EHGS",
			pt: "Concessão de STP para EHGS"
		},
		bypass: true
	},
	{
		_id: 45,
		name: {
			hy: "Ակտ",
			en: "Act",
			ru: "Акт",
			tr: "Eylem",
			ar: "فعل",
			zh: "行动",
			fa: "عمل",
			he: "פעולה",
			fr: "Acte",
			es: "Acto",
			pt: "Ato"
		}
	}
];

const brand = {
	name: "APIT Admin Panel",
	link: "/home",
	logo: "/logo.svg"
};

const menu = [
	{
		id: "menu-0",
		title: {
			en: "Database",
			ru: "База данных",
			hy: "Տվյալների բազա",
			tr: "Veritabanı",
			ar: "قاعدة البيانات",
			zh: "数据库",
			fa: "پایگاه داده",
			he: "מסד נתונים",
			fr: "Base de données",
			es: "Base de datos",
			pt: "Base de dados"
		},
		color: "#0263fc",
		route: "database",
		icon: "menu/project.png",
		menu: [
			{
				id: "menu-0-0",
				name: {
					en: "PROJECT Dashboard",
					ru: "PROJECT Панель",
					hy: "PROJECT Վահանակ",
					tr: "PROJE Panosu",
					ar: "لوحة مشروع",
					zh: "项目仪表板",
					fa: "داشبورد پروژه",
					he: "לוח מחוונים של פרויקט",
					fr: "Tableau de bord du projet",
					es: "Panel del proyecto",
					pt: "Painel do projeto"
				},
				icon: "menu/project-dashboard.png",
				route: "database/dashboard",
				permissionModel: "projectDashboard"
			},
			{
				id: "menu-0-1",
				name: {
					en: "Find anything",
					ru: "Найти что-нибудь",
					hy: "Ընդհանուր ֆիլտր",
					tr: "Her şeyi bul",
					ar: "ابحث عن أي شيء",
					zh: "查找任何内容",
					fa: "هر چیزی را پیدا کنید",
					he: "מצא הכל",
					fr: "Trouver n'importe quoi",
					es: "Encontrar cualquier cosa",
					pt: "Encontrar qualquer coisa"
				},
				icon: "menu/project-find-anything.png",
				route: "database/search",
				permissionModel: "project-search"
			},
			{
				id: "menu-0-2",
				name: {
					en: "Branch",
					ru: "Филиал электронной сети",
					hy: "Մասնաճյուղ և էլ․ ցանցեր",
					tr: "Şube",
					ar: "فرع",
					zh: "分支",
					fa: "شاخه",
					he: "סניף",
					fr: "Branche",
					es: "Rama",
					pt: "Ramo"
				},
				icon: "menu/project-branch.png",
				route: "database/branch",
				permissionModel: "branch"
			},
			{
				id: "menu-0-3",
				name: {
					en: "Voltage level",
					ru: "Уровень напряжения",
					hy: "Լարման մակարդակ",
					tr: "Gerilim seviyesi",
					ar: "مستوى الجهد",
					zh: "电压等级",
					fa: "سطح ولتاژ",
					he: "רמת מתח",
					fr: "Niveau de tension",
					es: "Nivel de voltaje",
					pt: "Nível de voltagem"
				},
				icon: "menu/project-voltage-level.png",
				route: "database/voltage",
				permissionModel: "voltage"
			},
			{
				id: "menu-0-4",
				name: {
					en: "Project Type",
					ru: "Тип проекта",
					hy: "Նախագծի տեսակը",
					tr: "Proje Türü",
					ar: "نوع المشروع",
					zh: "项目类型",
					fa: "نوع پروژه",
					he: "סוג פרויקט",
					fr: "Type de projet",
					es: "Tipo de proyecto",
					pt: "Tipo de projeto"
				},
				icon: "menu/project-type.png",
				route: "database/type",
				permissionModel: "projectType"
			},
			{
				id: "menu-0-5",
				name: {
					en: "Community",
					ru: "Сообщество",
					hy: "Համայնք",
					tr: "Topluluk",
					ar: "المجتمع",
					zh: "社区",
					fa: "جامعه",
					he: "קהילה",
					fr: "Communauté",
					es: "Comunidad",
					pt: "Comunidade"
				},
				icon: "menu/project-community.png",
				route: "database/community",
				permissionModel: "community"
			},
			{
				id: "menu-0-6",
				name: {
					en: "City",
					ru: "Город",
					hy: "Քաղաք",
					tr: "Şehir",
					ar: "مدينة",
					zh: "城市",
					fa: "شهر",
					he: "עיר",
					fr: "Ville",
					es: "Ciudad",
					pt: "Cidade"
				},
				icon: "menu/project-city.png",
				route: "database/city",
				permissionModel: "city"
			},
			{
				id: "menu-0-7",
				name: {
					en: "Village",
					ru: "Деревня",
					hy: "Գյուղ",
					tr: "Köy",
					ar: "قرية",
					zh: "村庄",
					fa: "روستا",
					he: "כפר",
					fr: "Village",
					es: "Pueblo",
					pt: "Vila"
				},
				icon: "menu/project-village.png",
				route: "database/village",
				permissionModel: "village"
			}
		]
	},
	{
		id: "menu-1",
		title: {
			en: "Human Resources",
			ru: "Кадры",
			hy: "Անձնակազմ",
			tr: "İnsan Kaynakları",
			ar: "الموارد البشرية",
			zh: "人力资源",
			fa: "منابع انسانی",
			he: "משאבי אנוש",
			fr: "Ressources humaines",
			es: "Recursos humanos",
			pt: "Recursos Humanos"
		},
		color: "#006373",
		route: "hr",
		icon: "menu/hr.png",
		menu: [
			{
				id: "menu-1-0",
				name: {
					en: "HR Dashboard",
					ru: "HR Панель",
					hy: "HR Վահանակ",
					tr: "İK Panosu",
					ar: "لوحة الموارد البشرية",
					zh: "人力资源仪表板",
					fa: "داشبورد منابع انسانی",
					he: "לוח מחוונים למשאבי אנוש",
					fr: "Tableau de bord RH",
					es: "Panel de RRHH",
					pt: "Painel de RH"
				},
				icon: "menu/hr-dashboard.png",
				route: "hr/hr-dashboard",
				permissionModel: "hrDashboard"
			},
			{
				name: {
					en: "Attendance",
					ru: "Посещаемость",
					hy: "Հաճախելիություն",
					tr: "Devamsızlık",
					ar: "الحضور",
					zh: "出勤",
					fa: "حضور و غیاب",
					he: "נוכחות",
					fr: "Présence",
					es: "Asistencia",
					pt: "Frequência"
				},
				icon: "menu/hr-attendance.png",
				route: "hr/attendance",
				permissionModel: "attendanceDashboard"
			},
			{
				name: {
					en: "Attendance statuses",
					ru: "Статусы посещаемости (ручные)",
					hy: "Հաճախելիության կարգավիճակներ",
					tr: "Devam durumları",
					ar: "حالات الحضور",
					zh: "出勤状态",
					fa: "وضعیت‌های حضور",
					he: "סטטוסי נוכחות",
					fr: "Statuts de présence",
					es: "Estados de asistencia",
					pt: "Status de frequência"
				},
				icon: "menu/hr-attendance-statuses.png",
				route: "hr/attendance-manual-status",
				permissionModel: "attendanceManualStatus"
			},
			{
				name: {
					en: "Org Tree",
					ru: "Организационная древовидная структура",
					hy: "Անձնակազմի կառուցվածք",
					tr: "Org Ağacı",
					ar: "شجرة المنظمة",
					zh: "组织结构树",
					fa: "درخت سازمانی",
					he: "עץ ארגוני",
					fr: "Arbre organisationnel",
					es: "Árbol organizacional",
					pt: "Árvore organizacional"
				},
				icon: "menu/hr-org-tree.png",
				route: "hr/org-tree",
				permissionModel: "orgTree"
			},
			{
				id: "menu-1-2",
				name: { en: "Employees", ru: "Сотрудники", hy: "Աշխատակիցներ" },
				icon: "menu/hr-employee.png",
				route: "hr/users/working",
				permissionModel: "user"
			},
			{
				id: "menu-1-3",
				name: {
					en: "Probation",
					ru: "Испытательный срок",
					hy: "Փորձաշրջան",
					tr: "Deneme Süresi",
					ar: "فترة تجريبية",
					zh: "试用期",
					fa: "دوره آزمایشی",
					he: "תקופת ניסיון",
					fr: "Période d'essai",
					es: "Período de prueba",
					pt: "Período de Experiência"
				},
				icon: "menu/hr-probation.png",
				iconColor: "cyan", // Color for the Probation folder icon
				subMenu: [
					{
						id: "menu-1-3-0",
						name: {
							en: "Interviewed",
							ru: "Собеседование",
							hy: "Հարցազրույց",
							tr: "Görüşülen",
							ar: "تمت مقابلته",
							zh: "已面试",
							fa: "مصاحبه شده",
							he: "נראיין",
							fr: "Interviewé",
							es: "Entrevistado",
							pt: "Entrevistado"
						},
						icon: "menu/hr-interviewed.png",
						iconColor: "teal", // Gradient color 1
						route: "hr/users/interview1",
						permissionModel: "userInterview1"
					},
					{
						id: "menu-1-3-1",
						name: {
							en: "Interviewed 2",
							ru: "Знакомство",
							hy: "Ծանոթություն",
							tr: "Görüşülen 2",
							ar: "تمت مقابلته 2",
							zh: "已面试 2",
							fa: "مصاحبه شده 2",
							he: "נראיין 2",
							fr: "Interviewé 2",
							es: "Entrevistado 2",
							pt: "Entrevistado 2"
						},
						icon: "menu/hr-interviewed-2.png",
						iconColor: "green", // Gradient color 2
						route: "hr/users/interview2",
						permissionModel: "userInterview2"
					},
					{
						id: "menu-1-3-2",
						name: {
							en: "Training",
							ru: "Обучение",
							hy: "Ուսուցում",
							tr: "Eğitim",
							ar: "تدريب",
							zh: "培训",
							fa: "آموزش",
							he: "הכשרה",
							fr: "Formation",
							es: "Capacitación",
							pt: "Treinamento"
						},
						icon: "menu/hr-training.png",
						iconColor: "yellow", // Gradient color 3
						route: "hr/users/interview3",
						permissionModel: "userInterview3"
					},
					{
						id: "menu-1-3-3",
						name: {
							en: "Internship",
							ru: "Стажирование",
							hy: "Փորձաշրջան",
							tr: "Staj",
							ar: "تدريب داخلي",
							zh: "实习",
							fa: "کارآموزی",
							he: "התמחות",
							fr: "Stage",
							es: "Pasantía",
							pt: "Estágio"
						},
						icon: "menu/hr-internship.png",
						iconColor: "orange", // Gradient color 4
						route: "hr/users/interview4",
						permissionModel: "userInterview4"
						// You can continue with "red" or other colors if you have more items
					}
				]
			},
			{
				id: "menu-1-4",
				name: {
					en: "Departments",
					ru: "Отделения",
					hy: "Բաժիններ",
					tr: "Bölümler",
					ar: "الأقسام",
					zh: "部门",
					fa: "دپارتمان‌ها",
					he: "מחלקות",
					fr: "Départements",
					es: "Departamentos",
					pt: "Departamentos"
				},
				icon: "menu/hr-departments.png",
				route: "hr/department",
				permissionModel: "department"
			},
			{
				id: "menu-1-5",
				name: {
					en: "Positions",
					ru: "Должности",
					hy: "Հաստիքներ",
					tr: "Pozisyonlar",
					ar: "المناصب",
					zh: "职位",
					fa: "موقعیت‌ها",
					he: "תפקידים",
					fr: "Postes",
					es: "Posiciones",
					pt: "Posições"
				},
				icon: "menu/hr-positions.png",
				route: "hr/position",
				permissionModel: "position"
			},
			{
				id: "menu-1-7",
				name: {
					en: "Archive",
					ru: "Архив",
					hy: "Արխիվ",
					tr: "Arşiv",
					ar: "الأرشيف",
					zh: "档案",
					fa: "بایگانی",
					he: "ארכיון",
					fr: "Archives",
					es: "Archivo",
					pt: "Arquivo"
				},
				icon: "menu/hr-archive.png",
				route: "hr/users/archive",
				permissionModel: "userArchive"
			},
			{
				id: "menu-1-8",
				name: {
					en: "Fired",
					ru: "Уволенные",
					hy: "Պայմանագրի լուծում",
					tr: "İşten Çıkarılanlar",
					ar: "المفصولون",
					zh: "被解雇的",
					fa: "اخراج شده‌ها",
					he: "מפוטרים",
					fr: "Licenciés",
					es: "Despedido",
					pt: "Demitidos"
				},
				icon: "menu/hr-fired.png",
				route: "hr/users/fired",
				permissionModel: "userFired"
			},
			{
				id: "menu-1-9",
				name: {
					en: "Maternity Leave",
					ru: "Декретный отпуске",
					hy: "Դեկրետ",
					tr: "Doğum İzni",
					ar: "إجازة الأمومة",
					zh: "产假",
					fa: "مرخصی زایمان",
					he: "חופשת לידה",
					fr: "Congé de maternité",
					es: "Licencia de maternidad",
					pt: "Licença maternidade"
				},
				icon: "menu/hr-maternity-leave.png",
				route: "hr/users/maternityLeave",
				permissionModel: "userLeave"
			},

			{
				id: "menu-1-10",
				name: {
					en: "Recruitment",
					ru: "Рекрутинг",
					hy: "Կադրերի ընտրություն",
					tr: "İşe Alım",
					ar: "التوظيف",
					zh: "招聘",
					fa: "استخدام",
					he: "גיוס",
					fr: "Recrutement",
					es: "Reclutamiento",
					pt: "Recrutamento"
				},
				icon: "menu/hr-recruitment.png",
				iconColor: "grape",
				route: null,
				subMenu: [
					{
						id: "menu-1-10-0",
						name: {
							en: "Job Postings",
							ru: "Вакансии",
							hy: "Աշխատատեղեր",
							tr: "İş İlanları",
							ar: "الوظائف المتاحة",
							zh: "职位发布",
							fa: "فرصت‌های شغلی",
							he: "משרות פתוחות",
							fr: "Offres d'emploi",
							es: "Ofertas de empleo",
							pt: "Ofertas de emprego"
						},
						icon: "menu/hr-job-posting.png",
						route: "hr/recruitment/jobs",
						permissionModel: "recruitment"
					},
					{
						id: "menu-1-10-1",
						name: {
							en: "Applications",
							ru: "Заявки",
							hy: "Դիմումներ",
							tr: "Başvurular",
							ar: "الطلبات",
							zh: "申请",
							fa: "درخواست‌ها",
							he: "בקשות",
							fr: "Candidatures",
							es: "Solicitudes",
							pt: "Candidaturas"
						},
						icon: "menu/hr-applications.png",
						route: "hr/recruitment/applications",
						permissionModel: "recruitmentApplication"
					},
					{
						id: "menu-1-10-2",
						name: {
							en: "Interviews",
							ru: "Интервью",
							hy: "Հարցազրույցներ",
							tr: "Mülakatlar",
							ar: "المقابلات",
							zh: "面试",
							fa: "مصاحبه‌ها",
							he: "ראיונות",
							fr: "Entretiens",
							es: "Entrevistas",
							pt: "Entrevistas"
						},
						icon: "menu/hr-interviews.png",
						route: "hr/recruitment/interviews",
						permissionModel: "recruitmentInterview"
					},
					{
						id: "menu-1-10-3",
						name: {
							en: "Assessments",
							ru: "Тестирование",
							hy: "Գնահատումներ",
							tr: "Değerlendirmeler",
							ar: "التقييمات",
							zh: "评估",
							fa: "ارزیابی‌ها",
							he: "הערכות",
							fr: "Évaluations",
							es: "Evaluaciones",
							pt: "Avaliações"
						},
						icon: "menu/hr-assessments.png",
						route: "hr/recruitment/assessments",
						permissionModel: "recruitmentAssessment"
					},
					{
						id: "menu-1-10-4",
						name: {
							en: "Offers",
							ru: "Предложения",
							hy: "Առաջարկներ",
							tr: "Teklifler",
							ar: "العروض",
							zh: "报价",
							fa: "پیشنهادها",
							he: "הצעות",
							fr: "Offres",
							es: "Ofertas",
							pt: "Ofertas"
						},
						icon: "menu/hr-offers.png",
						route: "hr/recruitment/offers",
						permissionModel: "recruitmentOffer"
					},
					{
						id: "menu-1-10-5",
						name: {
							en: "Onboarding",
							ru: "Адаптация",
							hy: "Onboarding",
							tr: "Başlangıç",
							ar: "التدريب",
							zh: "入职",
							fa: "پذیرش",
							he: "הכנסה",
							fr: "Intégration",
							es: "Integración",
							pt: "Integração"
						},
						icon: "menu/hr-onboarding.png",
						route: "hr/recruitment/onboarding",
						permissionModel: "recruitmentOnboarding"
					},
					{
						id: "menu-1-10-6",
						name: {
							en: "Dashboard",
							ru: "Панель",
							hy: "Վահանակ",
							tr: "Pano",
							ar: "لوحة التحكم",
							zh: "仪表板",
							fa: "داشبورد",
							he: "לוח בקרה",
							fr: "Tableau de bord",
							es: "Panel",
							pt: "Painel"
						},
						icon: "menu/hr-recruitment-dashboard.png",
						route: "hr/recruitment/dashboard",
						permissionModel: "recruitmentDashboard"
					}
				]
			},

			{
				name: {
					en: "All",
					ru: "Все",
					hy: "Բոլորը",
					tr: "Hepsi",
					ar: "الكل",
					zh: "全部",
					fa: "همه",
					he: "הכל",
					fr: "Tous",
					es: "Todos",
					pt: "Todos"
				},
				icon: "menu/hr-all.png",
				route: "hr/users/all",
				permissionModel: "userAll"
			}
		]
	},
	{
		id: "menu-2",
		title: {
			en: "IT",
			ru: "ИТ",
			hy: "ՏՏ",
			tr: "IT",
			ar: "IT",
			zh: "IT",
			fa: "IT",
			he: "IT",
			fr: "IT",
			es: "IT",
			pt: "IT"
		},
		color: "#0050f3",
		route: "it",
		icon: "menu/it.png",
		menu: [
			{
				id: "menu-2-0",
				name: {
					en: "IT Dashboard",
					ru: "IT Панель",
					hy: "IT Վահանակ",
					tr: "IT Panosu",
					ar: "لوحة IT",
					zh: "IT仪表板",
					fa: "داشبورد IT",
					he: "לוח מחוונים של IT",
					fr: "Tableau de bord IT",
					es: "Panel de IT",
					pt: "Painel de TI"
				},
				icon: "menu/it-dashboard.png",
				route: "it/it-dashboard",
				permissionModel: "itDashboard"
			},
			{
				name: {
					en: "PC",
					ru: "ПК",
					hy: "Համակարգիչներ",
					tr: "PC",
					ar: "الكمبيوترات",
					zh: "个人电脑",
					fa: "کامپیوترها",
					he: "מחשבים אישיים",
					fr: "PC",
					es: "PC",
					pt: "PC"
				},
				icon: "menu/it-pc.png",
				route: "it/pc",
				permissionModel: "pc"
			},
			{
				name: {
					en: "Laptop",
					ru: "Ноутбук",
					hy: "Դյուրակիր",
					tr: "Dizüstü",
					ar: "كمبيوتر محمول",
					zh: "笔记本电脑",
					fa: "لپ‌تاپ",
					he: "מחשב נייד",
					fr: "Ordinateur portable",
					es: "Portátil",
					pt: "Notebook"
				},
				icon: "menu/it-laptop.png",
				route: "it/laptop",
				permissionModel: "laptop"
			},
			{
				name: {
					en: "PC Components",
					ru: "Компоненты ПК",
					hy: "Համակարգչի բաղադրիչներ",
					tr: "PC Bileşenleri",
					ar: "مكونات الكمبيوتر",
					zh: "PC组件",
					fa: "قطعات کامپیوتر",
					he: "רכיבי מחשב",
					fr: "Composants PC",
					es: "Componentes de PC",
					pt: "Componentes de PC"
				},
				icon: "menu/it-pc-components.png",
				route: "it/cpu",
				permissionModel: "pcComponents",
				permissionGroup: "pcComponents",
				subMenu: [
					{
						name: {
							en: "CPUs",
							ru: "Процессоры",
							hy: "Պրոցեսորներ",
							tr: "CPU'lar",
							ar: "وحدات المعالجة المركزية",
							zh: "中央处理器",
							fa: "پردازنده‌ها",
							he: "מעבדים",
							fr: "Processeurs",
							es: "CPU",
							pt: "CPUs"
						},
						icon: "menu/it-cpus.png",
						route: "it/cpu",
						permissionModel: "cpu"
					},
					{
						name: {
							en: "GPUs",
							ru: "Видеокарты",
							hy: "Տեսաքարտեր",
							tr: "GPU'lar",
							ar: "وحدات معالجة الرسومات",
							zh: "图形处理器",
							fa: "کارت‌های گرافیک",
							he: "כרטיסי גרפיקה",
							fr: "Cartes graphiques",
							es: "GPU",
							pt: "GPUs"
						},
						icon: "menu/it-gpus.png",
						route: "it/gpu",
						permissionModel: "gpu"
					},
					{
						name: {
							en: "RAM",
							ru: "Оперативная память",
							hy: "Օպերատիվ հիշողություն",
							tr: "RAM",
							ar: "ذاكرة الوصول العشوائي",
							zh: "内存",
							fa: "رم",
							he: "זיכרון RAM",
							fr: "RAM",
							es: "RAM",
							pt: "RAM"
						},
						icon: "menu/it-ram.png",
						route: "it/ram",
						permissionModel: "ram"
					},
					{
						name: {
							en: "Storage (ROM)",
							ru: "Хранилище (ROM)",
							hy: "Հիշողություն (ROM)",
							tr: "Depolama (ROM)",
							ar: "التخزين (ROM)",
							zh: "存储（ROM）",
							fa: "ذخیره‌سازی (ROM)",
							he: "אחסון (ROM)",
							fr: "Stockage (ROM)",
							es: "Almacenamiento (ROM)",
							pt: "Armazenamento (ROM)"
						},
						icon: "menu/it-storage.png",
						route: "it/rom",
						permissionModel: "rom"
					},
					{
						name: {
							en: "Warranty Providers",
							ru: "Поставщики гарантий",
							hy: "Երաշխիքի տրամադրողներ",
							tr: "Garanti Sağlayıcıları",
							ar: "مزودو الضمان",
							zh: "保修提供商",
							fa: "ارائه‌دهندگان گارانتی",
							he: "ספקי אחריות",
							fr: "Fournisseurs de garantie",
							es: "Proveedores de garantía",
							pt: "Fornecedores de garantia"
						},
						icon: "menu/it-warranty-providers.png",
						route: "it/warranty-provider",
						permissionModel: "warrantyProvider"
					},
					{
						name: {
							en: "Monitors",
							ru: "Мониторы",
							hy: "Մոնիտորներ",
							tr: "Monitörler",
							ar: "الشاشات",
							zh: "显示器",
							fa: "مانیتورها",
							he: "מסכים",
							fr: "Moniteurs",
							es: "Monitores",
							pt: "Monitores"
						},
						icon: "menu/it-monitors.png",
						route: "it/monitor",
						permissionModel: "monitor"
					}
				]
			},
			{
				name: {
					en: "Network Devices",
					ru: "Сетевые устройства",
					hy: "Ցանցային սարքեր",
					tr: "Ağ Cihazları",
					ar: "أجهزة الشبكة",
					zh: "网络设备",
					fa: "دستگاه‌های شبکه",
					he: "התקני רשת",
					fr: "Appareils réseau",
					es: "Dispositivos de red",
					pt: "Dispositivos de rede"
				},
				icon: "menu/it-network-devices.png",
				route: "it/router",
				permissionModel: "router",
				subMenu: [
					{
						name: {
							en: "Routers",
							ru: "Маршрутизаторы",
							hy: "Հեռահաղորդակցություն",
							tr: "Yönlendiriciler",
							ar: "الموجهات",
							zh: "路由器",
							fa: "روترها",
							he: "נתבים",
							fr: "Routeurs",
							es: "Enrutadores",
							pt: "Roteadores"
						},
						icon: "menu/it-routers.png",
						route: "it/router",
						permissionModel: "router"
					},
					{
						name: {
							en: "Switch",
							ru: "Коммутатор",
							hy: "Անջատիչ",
							tr: "Anahtar",
							ar: "المفتاح",
							zh: "交换机",
							fa: "سوئیچ",
							he: "מתג",
							fr: "Commutateur",
							es: "Interruptor",
							pt: "Switch"
						},
						icon: "menu/it-switch.png",
						route: "it/switch",
						permissionModel: "switch"
					},
					{
						name: {
							en: "Telephone",
							ru: "Телефон",
							hy: "Հեռախոս",
							tr: "Telefon",
							ar: "هاتف",
							zh: "电话",
							fa: "تلفن",
							he: "טלפון",
							fr: "Téléphone",
							es: "Teléfono",
							pt: "Telefone"
						},
						icon: "menu/it-telephone.png",
						route: "it/telephone",
						permissionModel: "telephone"
					},
					{
						name: {
							en: "Attendance Devices",
							ru: "Устройства посещаемости",
							hy: "Հաճախելիության սարքեր",
							tr: "Devam Cihazları",
							ar: "أجهزة الحضور",
							zh: "考勤设备",
							fa: "دستگاه‌های حضور و غیاب",
							he: "מכשירי נוכחות",
							fr: "Appareils de présence",
							es: "Dispositivos de asistencia",
							pt: "Dispositivos de presença"
						},
						icon: "menu/it-attendance-devices.png",
						route: "it/attendance-device",
						permissionModel: "pc"
					}
				]
			},
			{
				name: {
					en: "Printers",
					ru: "Принтеры",
					hy: "Տպիչներ",
					tr: "Yazıcılar",
					ar: "الطابعات",
					zh: "打印机",
					fa: "چاپگرها",
					he: "מדפסות",
					fr: "Imprimantes",
					es: "Impresoras",
					pt: "Impressoras"
				},
				icon: "menu/it-printers.png",
				route: "it/printer",
				permissionModel: "printer"
			},
			{
				name: {
					ru: "Программные обеспечения",
					en: "Software",
					hy: "Ծրագիր",
					tr: "Yazılım",
					ar: "البرمجيات",
					zh: "软件",
					fa: "نرم‌افزار",
					he: "תוכנה",
					fr: "Logiciel",
					es: "Software",
					pt: "Software"
				},
				icon: "menu/it-software.png",
				route: "it/software",
				permissionModel: "software"
			}
		]
	},
	{
		id: "menu-3",
		title: {
			en: "Chat",
			ru: "Чат",
			hy: "Չատ",
			tr: "Sohbet",
			ar: "محادثة",
			zh: "聊天",
			fa: "چت",
			he: "צ'אט",
			fr: "Chat",
			es: "Chat",
			pt: "Chat"
		},
		color: "#0a79ff",
		icon: "menu/chat.png",
		route: "chat",
		permissionModel: "chat",
		menu: []
	},
	{
		id: "menu-website-chat",
		title: {
			en: "Website Chat",
			ru: "Чат сайта",
			hy: "Կայքի չատ",
			tr: "Web Sitesi Sohbeti",
			ar: "دردشة الموقع",
			zh: "网站聊天",
			fa: "چت وب‌سایت",
			he: "צ'אט אתר",
			fr: "Chat du site",
			es: "Chat del sitio",
			pt: "Chat do site"
		},
		color: "#0f9f8f",
		icon: "menu/chat.png",
		route: "website-chat",
		permissionModel: "websiteChat",
		menu: [
			{
				id: "menu-website-chat-inbox",
				name: {
					en: "Inbox",
					ru: "Входящие",
					hy: "Մուտքային",
					tr: "Gelen Kutusu",
					ar: "الوارد",
					zh: "收件箱",
					fa: "صندوق ورودی",
					he: "תיבת דואר",
					fr: "Boîte de réception",
					es: "Bandeja",
					pt: "Caixa de entrada"
				},
				icon: "message-circle-chat-svgrepo-com.svg",
				route: "website-chat",
				permissionModel: "websiteChat"
			},
			{
				id: "menu-website-chat-sites",
				name: {
					en: "Websites",
					ru: "Сайты",
					hy: "Կայքեր",
					tr: "Web Siteleri",
					ar: "المواقع",
					zh: "网站",
					fa: "وب‌سایت‌ها",
					he: "אתרים",
					fr: "Sites web",
					es: "Sitios web",
					pt: "Sites"
				},
				icon: "search-globe-svgrepo-com.svg",
				route: "website-chat/sites",
				permissionModel: "websiteChat"
			},
			{
				id: "menu-website-chat-canned-replies",
				name: {
					en: "Canned Replies",
					ru: "Быстрые ответы",
					hy: "Արագ պատասխաններ",
					tr: "Hazır Yanıtlar",
					ar: "الردود الجاهزة",
					zh: "快捷回复",
					fa: "پاسخ‌های آماده",
					he: "תגובות מוכנות",
					fr: "Réponses rapides",
					es: "Respuestas rápidas",
					pt: "Respostas rápidas"
				},
				icon: "message-circle-chat-svgrepo-com.svg",
				route: "website-chat/canned-replies",
				permissionModel: "websiteChat"
			},
			{
				id: "menu-website-chat-statistics",
				name: {
					en: "Statistics",
					ru: "Статистика",
					hy: "Վիճակագրություն",
					tr: "İstatistikler",
					ar: "الإحصائيات",
					zh: "统计",
					fa: "آمار",
					he: "סטטיסטיקה",
					fr: "Statistiques",
					es: "Estadísticas",
					pt: "Estatísticas"
				},
				icon: "status-svgrepo-com.svg",
				route: "website-chat/statistics",
				permissionModel: "websiteChat"
			}
		]
	},
	{
		id: "menu-meetings",
		title: {
			en: "Meetings",
			ru: "Встречи",
			hy: "Հանդիպումներ",
			tr: "Toplantılar",
			ar: "الاجتماعات",
			zh: "会议",
			fa: "جلسات",
			he: "פגישות",
			fr: "Réunions",
			es: "Reuniones",
			pt: "Reuniões"
		},
		color: "#8100e7",
		icon: "menu/meetings.png",
		route: "meetings",
		permissionModel: "meetings",
		menu: [
			{
				id: "menu-meetings-list",
				name: {
					en: "Meetings",
					ru: "Встречи",
					hy: "Հանդիպումներ",
					tr: "Toplantılar",
					ar: "الاجتماعات",
					zh: "会议",
					fa: "جلسات",
					he: "פגישות",
					fr: "Réunions",
					es: "Reuniones",
					pt: "Reuniões"
				},
				icon: "menu/meetings-list.png",
				route: "meetings",
				permissionModel: "meetings"
			}
		]
	},
	{
		id: "menu-5",
		title: {
			en: "Ticket System",
			ru: "Система билетов",
			hy: "Բիլետների համակարգ",
			tr: "Bilet Sistemi",
			ar: "نظام التذاكر",
			zh: "票务系统",
			fa: "سیستم تذکرها",
			he: "מערכת תזרים",
			fr: "Système de billets",
			es: "Sistema de billetes",
			pt: "Sistema de bilhetes"
		},
		color: "#fe8700",
		route: "ticket-system",
		icon: "menu/ticket-system.png",
		menu: [
			{
				id: "menu-5-0",
				name: {
					en: "Dashboard",
					ru: "Панель управления",
					hy: "Վահանակ",
					tr: "Kontrol Paneli",
					ar: "لوحة التحكم",
					zh: "控制面板",
					fa: "پیشخوان",
					he: "לוח בקרה",
					fr: "Tableau de bord",
					es: "Panel de control",
					pt: "Painel de controle"
				},
				icon: "menu/ticket-system-dashboard.png",
				route: "ticket-system/ticket-dashboard",
				permissionModel: "ticketDashboard"
			},
			{
				id: "menu-5-setup",
				name: {
					en: "Setup",
					ru: "Настройка",
					hy: "Կարգավորում",
					tr: "Kurulum",
					ar: "الإعداد",
					zh: "设置",
					fa: "تنظیمات",
					he: "הגדרות",
					fr: "Configuration",
					es: "Configuración",
					pt: "Configuração"
				},
				icon: "menu/ticket-system-setup.png",
				subMenu: [
					{
						id: "menu-5-ticket-domains",
						name: {
							en: "Domains",
							ru: "Домены",
							hy: "Դոմեններ",
							tr: "Alanlar",
							ar: "المجالات",
							zh: "域",
							fa: "دامنه‌ها",
							he: "דומיינים",
							fr: "Domaines",
							es: "Dominios",
							pt: "Domínios"
						},
						icon: "menu/ticket-system-domains.png",
						route: "ticket-system/ticket-domain",
						permissionModel: "ticketDomain"
					},
					{
						name: {
							en: "Problem Groups",
							ru: "Группы проблем",
							hy: "Խնդիրների խմբեր",
							tr: "Sorun Grupları",
							ar: "مجموعات المشاكل",
							zh: "问题组",
							fa: "گروه‌های مشکل",
							he: "קבוצות בעיות",
							fr: "Groupes de problèmes",
							es: "Grupos de problemas",
							pt: "Grupos de problemas"
						},
						icon: "menu/ticket-system-problem-groups.png",
						route: "ticket-system/problem-group",
						permissionModel: "problemGroup"
					},
					{
						id: "menu-5-problems",
						name: {
							en: "Problems",
							ru: "Проблемы",
							hy: "Խնդիրներ",
							tr: "Sorunlar",
							ar: "المشاكل",
							zh: "问题",
							fa: "مشکلات",
							he: "בעיות",
							fr: "Problèmes",
							es: "Problemas",
							pt: "Problemas"
						},
						icon: "menu/ticket-system-problems.png",
						route: "ticket-system/problem",
						permissionModel: "problem"
					},

					{
						id: "menu-5-related-lists",
						name: {
							en: "Related lists",
							ru: "Связанные списки",
							hy: "Կապակցված ցուցակներ",
							tr: "İlgili listeler",
							ar: "قوائم ذات صلة",
							zh: "关联列表",
							fa: "فهرست‌های مرتبط",
							he: "רשימות קשורות",
							fr: "Listes associées",
							es: "Listas relacionadas",
							pt: "Listas relacionadas"
						},
						icon: "menu/ticket-system-related-list.png",
						route: "ticket-system/related-list",
						permissionModel: "ticketRelatedList"
					}
				]
			},
			{
				id: "menu-5-modules",
				name: {
					en: "Modules",
					ru: "Модули",
					hy: "Մոդուլներ",
					tr: "Modüller",
					ar: "الوحدات",
					zh: "模块",
					fa: "ماژول‌ها",
					he: "מודולים",
					fr: "Modules",
					es: "Módulos",
					pt: "Módulos"
				},
				icon: "menu/ticket-system-modules.png",
				subMenu: [
					{
						id: "menu-5-priorities",
						name: {
							en: "Priorities",
							ru: "Приоритеты",
							hy: "Առաջնահերթություններ",
							tr: "Öncelikler",
							ar: "الأولويات",
							zh: "优先级",
							fa: "اولویت‌ها",
							he: "עדיפויות",
							fr: "Priorités",
							es: "Prioridades",
							pt: "Prioridades"
						},
						icon: "menu/ticket-system-priorities.png",
						route: "ticket-system/priority",
						permissionModel: "ticketPriority"
					},
					{
						id: "menu-5-ticket-types",
						name: {
							en: "Ticket Types",
							ru: "Типы тикетов",
							hy: "Տոմսերի տեսակներ",
							tr: "Bilet Türleri",
							ar: "أنواع التذاكر",
							zh: "票据类型",
							fa: "انواع تذکره",
							he: "סוגי כרטיסים",
							fr: "Types de tickets",
							es: "Tipos de tickets",
							pt: "Tipos de tickets"
						},
						icon: "menu/ticket-system-types.png",
						route: "ticket-system/ticket-type",
						permissionModel: "ticketType"
					}
				]
			}
		]
	},
	{
		id: "menu-6",
		title: {
			en: "Internet Shop",
			ru: "Интернет магазин",
			hy: "Ինտերնետ խանութ",
			tr: "İnternet mağaza",
			ar: "متجر الإنترنت",
			zh: "网上商店",
			fa: "فروشگاه اینترنتی",
			he: "חנות באינטרנט",
			fr: "Boutique en ligne",
			es: "Tienda en línea",
			pt: "Loja online"
		},
		color: "#0c5a57",
		route: "ecommerce",
		icon: "menu/internet-shop.png",
		menu: [
			{
				id: "menu-6-0",
				name: {
					en: "Dashboard",
					ru: "Панель управления",
					hy: "Վահանակ",
					tr: "Kontrol Paneli",
					ar: "لوحة التحكم",
					zh: "控制面板",
					fa: "پیشخوان",
					he: "לוח בקרה",
					fr: "Tableau de bord",
					es: "Panel de control",
					pt: "Painel de controle"
				},
				icon: "menu/internet-shop-dashboard.png",
				route: "ecommerce/dashboard",
				permissionModel: "ecommerceDashboard"
			},
			{
				name: {
					en: "Sales",
					ru: "Продажи",
					hy: "Վաճառքներ",
					tr: "Satışlar",
					ar: "المبيعات",
					zh: "销售",
					fa: "فروش‌ها",
					he: "מכירות",
					fr: "Ventes",
					es: "Ventas",
					pt: "Vendas"
				},
				icon: "menu/internet-shop-sales.png",
				route: null,
				subMenu: [
					{
						name: {
							en: "List",
							ru: "Список",
							hy: "Ցանկ",
							tr: "Liste",
							ar: "القائمة",
							zh: "列表",
							fa: "فهرست",
							he: "רשימה",
							fr: "Liste",
							es: "Lista",
							pt: "Lista"
						},
						icon: "menu/internet-shop-sales.png",
						route: "ecommerce/order",
						permissionModel: "order"
					},
					{
						name: {
							en: "Products",
							ru: "Продукты",
							hy: "Ապրանքներ",
							tr: "Ürünler",
							ar: "منتجات",
							zh: "产品",
							fa: "محصولات",
							he: "מוצרים",
							fr: "Produits",
							es: "Productos",
							pt: "Produtos"
						},
						icon: "menu/internet-shop-products.png",
						route: "ecommerce/sale-product",
						permissionModel: "product"
					}
				]
			},
			{
				name: {
					en: "Shopping",
					ru: "Закупки",
					hy: "Գնումներ",
					tr: "Alışveriş",
					ar: "التسوق",
					zh: "采购",
					fa: "خرید",
					he: "קניות",
					fr: "Achats",
					es: "Compras",
					pt: "Compras"
				},
				icon: "menu/internet-shop-shopping.png",
				route: null,
				subMenu: [
					{
						name: {
							en: "List",
							ru: "Список",
							hy: "Ցանկ",
							tr: "Liste",
							ar: "القائمة",
							zh: "列表",
							fa: "فهرست",
							he: "רשימה",
							fr: "Liste",
							es: "Lista",
							pt: "Lista"
						},
						icon: "menu/internet-shop-shopping.png",
						route: "ecommerce/shop-purchase",
						permissionModel: "shopPurchase"
					},
					{
						name: {
							en: "Products",
							ru: "Продукты",
							hy: "Ապրանքներ",
							tr: "Ürünler",
							ar: "منتجات",
							zh: "产品",
							fa: "محصولات",
							he: "מוצרים",
							fr: "Produits",
							es: "Productos",
							pt: "Produtos"
						},
						icon: "menu/internet-shop-products.png",
						route: "ecommerce/shop-product",
						permissionModel: "product"
					},
					{
						name: { en: "Stores", ru: "Магазины", hy: "Խանութներ" },
						icon: "menu/internet-shop-shopping.png",
						route: "ecommerce/store",
						permissionModel: "company"
					}
				]
			},
			{
				name: {
					en: "Categories",
					ru: "Категории",
					hy: "Կատեգորիաներ",
					tr: "Kategoriler",
					ar: "الفئات",
					zh: "类别",
					fa: "دسته‌بندی‌ها",
					he: "קטגוריות",
					fr: "Catégories",
					es: "Categorías",
					pt: "Categorias"
				},
				icon: "menu/internet-shop-categories.png",
				route: "ecommerce/category",
				permissionModel: "category"
			},
			{
				name: {
					en: "Tags",
					ru: "Теги",
					hy: "Պիտակներ",
					tr: "Etiketler",
					ar: "العلامات",
					zh: "标签",
					fa: "برچسب‌ها",
					he: "תגים",
					fr: "Étiquettes",
					es: "Etiquetas",
					pt: "Etiquetas"
				},
				icon: "menu/internet-shop-tags.png",
				route: "ecommerce/tag",
				permissionModel: "tag"
			},
			{
				name: {
					en: "Sale",
					ru: "Распродажа",
					hy: "Վաճառք",
					tr: "Satış",
					ar: "بيع",
					zh: "销售",
					fa: "فروش",
					he: "מכירה",
					fr: "Vente",
					es: "Venta",
					pt: "Venda"
				},
				icon: "menu/internet-shop-sale.png",
				route: "ecommerce/sale",
				permissionModel: "sale"
			},
			{
				name: {
					en: "Fields",
					ru: "Поля",
					hy: "Դաշտեր",
					tr: "Alanlar",
					ar: "المجالات",
					zh: "领域",
					fa: "زمینه‌ها",
					he: "תחומים",
					fr: "Champs",
					es: "Campos",
					pt: "Campos"
				},
				icon: "menu/internet-shop-fields.png",
				route: "ecommerce/field",
				permissionModel: "field"
			},
			{
				name: {
					en: "Promo Codes",
					ru: "Промо коды",
					hy: "Պրոմո կոդեր",
					tr: "Promo Kodları",
					ar: "رموز الترويج",
					zh: "促销代码",
					fa: "کد تبلیغاتی",
					he: "קודי קידום",
					fr: "Codes promotionnels",
					es: "Códigos promocionales",
					pt: "Códigos promocionais"
				},
				icon: "menu/internet-shop-promo-codes.png",
				route: "ecommerce/promo-code",
				permissionModel: "promoCode"
			},
			{
				name: {
					en: "Certificates",
					ru: "Сертификаты",
					hy: "Սերտիֆիկատներ",
					tr: "Sertifikalar",
					ar: "شهادات",
					zh: "证书",
					fa: "گواهی‌نامه‌ها",
					he: "תעודות",
					fr: "Certificats",
					es: "Certificados",
					pt: "Certificados"
				},
				icon: "menu/internet-shop-certificate.png",
				route: "ecommerce/certificate",
				permissionModel: "certificate"
			}
		]
	},
	{
		id: "menu-7",
		title: {
			en: "CRM",
			ru: "CRM",
			hy: "CRM",
			tr: "CRM",
			ar: "CRM",
			zh: "CRM",
			fa: "CRM",
			he: "CRM",
			fr: "CRM",
			es: "CRM",
			pt: "CRM"
		},
		color: "#ff0d59",
		route: "crm",
		icon: "menu/crm.png",
		menu: [
			{
				id: "menu-7-0",
				name: {
					en: "Dashboard",
					ru: "Панель управления",
					hy: "Վահանակ",
					tr: "Kontrol Paneli",
					ar: "لوحة التحكم",
					zh: "控制面板",
					fa: "پیشخوان",
					he: "לוח בקרה",
					fr: "Tableau de bord",
					es: "Panel de control",
					pt: "Painel de controle"
				},
				icon: "menu/crm-dashboard.png",
				route: "crm/crm-dashboard",
				permissionModel: "crmDashboard"
			},
			{
				name: {
					en: "Contacts",
					ru: "Контакты",
					hy: "Կոնտակտներ",
					tr: "Kişiler",
					ar: "جهات الاتصال",
					zh: "联系人",
					fa: "مخاطبین",
					he: "אנשי קשר",
					fr: "Contacts",
					es: "Contactos",
					pt: "Contatos"
				},
				icon: "menu/crm-contacts.png",
				permissionModel: "contact",
				route: "crm/contact"
			},
			{
				name: {
					en: "Contact Groups",
					ru: "Группы контактов",
					hy: "Կոնտակտների խմբեր",
					tr: "Kişi Grupları",
					ar: "مجموعات جهات الاتصال",
					zh: "联系人群组",
					fa: "گروه‌های مخاطب",
					he: "קבוצות אנשי קשר",
					fr: "Groupes de contacts",
					es: "Grupos de contactos",
					pt: "Grupos de contatos"
				},
				icon: "menu/crm-contact-groups.png",
				route: "crm/contact-group",
				permissionModel: "contactGroup"
			}
		]
	},
	{
		id: "menu-8",
		title: {
			en: "Finance Platform",
			ru: "Финансы",
			hy: "Ֆինանսներ",
			tr: "Finans",
			ar: "المالية",
			zh: "财务",
			fa: "مالی",
			he: "פיננסים",
			fr: "Finance",
			es: "Finanzas",
			pt: "Finanças"
		},
		color: "#1d8c00",
		route: "finance",
		icon: "menu/finance-platform.png",
		menu: [
			{
				id: "menu-8-0",
				name: {
					en: "Finance Dashboard",
					ru: "Финансовая панель",
					hy: "Ֆինանսական վահանակ",
					tr: "Finans Panosu",
					ar: "لوحة المالية",
					zh: "财务仪表板",
					fa: "داشبورد مالی",
					he: "לוח מחוונים פיננסי",
					fr: "Tableau de bord financier",
					es: "Panel financiero",
					pt: "Painel financeiro"
				},
				icon: "menu/finance-platform-dashboard.png",
				route: "finance/dashboard",
				permissionModel: "financeDashboard"
			},
			{
				id: "menu-8-0a",
				name: {
					en: "Payroll Dashboard",
					ru: "Панель зарплат",
					hy: "Աշխատ. վահանակ",
					tr: "Maaş Panosu",
					ar: "لوحة الرواتب",
					zh: "工资仪表板",
					fa: "داشبورد حقوق",
					he: "לוח שכר",
					fr: "Tableau de paie",
					es: "Panel de nómina",
					pt: "Painel de folha"
				},
				icon: "menu/finance-platform-payroll-dashboard.png",
				route: "finance/payroll-dashboard",
				permissionModel: "payrollDashboard"
			},
			{
				id: "menu-8-0b",
				name: {
					en: "Payroll",
					ru: "Зарплата",
					hy: "Աշխատավարձ",
					tr: "Maaş Bordrosu",
					ar: "كشف الرواتب",
					zh: "工资单",
					fa: "حقوق و دستمزد",
					he: "משכורת",
					fr: "Paie",
					es: "Nómina",
					pt: "Folha de pagamento"
				},
				icon: "menu/finance-platform-payroll.png",
				route: "finance/payroll",
				permissionModel: "payroll"
			},
			{
				name: {
					en: "Budgets & Transactions",
					ru: "Бюджеты и транзакции",
					hy: "Բյուջեներ և գործարքներ",
					tr: "Bütçeler ve İşlemler",
					ar: "الميزانيات والمعاملات",
					zh: "预算和交易",
					fa: "بودجه‌ها و تراکنش‌ها",
					he: "תקציבים ועסקאות",
					fr: "Budgets et transactions",
					es: "Presupuestos y transacciones",
					pt: "Orçamentos e transações"
				},
				icon: "menu/finance-platform-budgets-and-transactions.png",
				route: "finance/budgets",
				permissionModel: "financeBudget"
			},
			{
				name: {
					en: "Analytics & Forecasts",
					ru: "Аналитика и прогнозы",
					hy: "Վերլուծություններ և կանխատեսումներ",
					tr: "Analitikler ve Tahminler",
					ar: "التحليلات والتوقعات",
					zh: "分析与预测",
					fa: "تحلیل‌ها و پیش‌بینی‌ها",
					he: "אנליטיקה ותחזיות",
					fr: "Analyses et prévisions",
					es: "Análisis y pronósticos",
					pt: "Análises e previsões"
				},
				icon: "menu/finance-platform-analytics-forecast.png",
				route: "finance/analytics",
				permissionModel: "financeAnalytics"
			},
			{
				name: {
					en: "Cards & Controls",
					ru: "Карты и лимиты",
					hy: "Քարտեր և սահմանաչափեր",
					tr: "Kartlar ve Kontroller",
					ar: "البطاقات والضوابط",
					zh: "卡片与控制",
					fa: "کارت‌ها و کنترل‌ها",
					he: "כרטיסים ובקרות",
					fr: "Cartes et contrôles",
					es: "Tarjetas y controles",
					pt: "Cartões e controles"
				},
				icon: "menu/finance-platform-cards-controls.png",
				route: "finance/cards",
				permissionModel: "financeCard"
			},
			{
				name: {
					en: "Card Issuers",
					ru: "Эмитенты карт",
					hy: "Քարտերի թողարկիչներ",
					tr: "Kart Verenler",
					ar: "مصدرات البطاقات",
					zh: "发卡机构",
					fa: "صادرکنندگان کارت",
					he: "מנפיקי כרטיסים",
					fr: "Émetteurs de cartes",
					es: "Emisores de tarjetas",
					pt: "Emissores de cartões"
				},
				icon: "menu/finance-platform-card-issuers.png",
				route: "finance/card-issuers",
				permissionModel: "financeCardIssuer"
			},
			{
				name: {
					en: "Categories",
					ru: "Категории",
					hy: "Կատեգորիաներ",
					tr: "Kategoriler",
					ar: "الفئات",
					zh: "类别",
					fa: "دسته‌بندی‌ها",
					he: "קטגוריות",
					fr: "Catégories",
					es: "Categorías",
					pt: "Categorias"
				},
				icon: "menu/finance-platform-categories.png",
				route: "finance/categories",
				permissionModel: "financeCategory"
			},
			{
				name: {
					en: "Transactions",
					ru: "Транзакции",
					hy: "Գործարքներ",
					tr: "İşlemler",
					ar: "المعاملات",
					zh: "交易",
					fa: "تراکنش‌ها",
					he: "עסקאות",
					fr: "Transactions",
					es: "Transacciones",
					pt: "Transações"
				},
				icon: "menu/finance-platform-transactions.png",
				route: "finance/transactions",
				permissionModel: "financeTransaction"
			},
			{
				name: {
					en: "Accounts",
					ru: "Счета",
					hy: "Հաշիվներ",
					tr: "Hesaplar",
					ar: "الحسابات",
					zh: "账户",
					fa: "حساب‌ها",
					he: "חשבונות",
					fr: "Comptes",
					es: "Cuentas",
					pt: "Contas"
				},
				icon: "menu/finance-platform-accounts.png",
				route: "finance/accounts",
				permissionModel: "financeAccount"
			},
			{
				name: {
					en: "Goals",
					ru: "Цели",
					hy: "Նպատakner",
					tr: "Hedefler",
					ar: "الأهداف",
					zh: "目标",
					fa: "اهداف",
					he: "יעדים",
					fr: "Objectifs",
					es: "Metas",
					pt: "Metas"
				},
				icon: "menu/finance-platform-goals.png",
				route: "finance/goals",
				permissionModel: "financeGoal"
			},
			{
				name: {
					en: "Loans",
					ru: "Кредиты",
					hy: "Վarqner",
					tr: "Krediler",
					ar: "القروض",
					zh: "贷款",
					fa: "وام‌ها",
					he: "הלוואות",
					fr: "Prêts",
					es: "Préstamos",
					pt: "Empréstimos"
				},
				icon: "menu/finance-platform-loans.png",
				route: "finance/loans",
				permissionModel: "financeLoan"
			},
			{
				name: {
					en: "Finance Workspaces",
					ru: "Финансовые пространства",
					hy: "Ֆինանսական տարածքներ",
					tr: "Finans Çalışma Alanları",
					ar: "مساحات العمل المالية",
					zh: "财务工作区",
					fa: "فضاهای کاری مالی",
					he: "סביבות עבודה פיננסיות",
					fr: "Espaces financiers",
					es: "Espacios financieros",
					pt: "Espaços financeiros"
				},
				icon: "menu/finance-platform-accounts.png",
				route: "finance/workspaces",
				permissionModel: "financeWorkspace"
			},
			{
				name: {
					en: "Receipts",
					ru: "Чеки",
					hy: "Կտրոններ",
					tr: "Fişler",
					ar: "الإيصالات",
					zh: "收据",
					fa: "رسیدها",
					he: "קבלות",
					fr: "Reçus",
					es: "Recibos",
					pt: "Recibos"
				},
				icon: "menu/finance-platform-transactions.png",
				route: "finance/receipts",
				permissionModel: "financeReceipt"
			},
			{
				name: {
					en: "Recurring Bills",
					ru: "Регулярные платежи",
					hy: "Կրկնվող վճարումներ",
					tr: "Düzenli Ödemeler",
					ar: "الفواتير المتكررة",
					zh: "定期账单",
					fa: "قبض‌های تکراری",
					he: "חשבונות חוזרים",
					fr: "Factures récurrentes",
					es: "Facturas recurrentes",
					pt: "Contas recorrentes"
				},
				icon: "menu/finance-platform-budgets-and-transactions.png",
				route: "finance/recurring",
				permissionModel: "financeRecurring"
			},
			{
				name: {
					en: "Categorization Rules",
					ru: "Правила категоризации",
					hy: "Դասակարգման կանոններ",
					tr: "Kategorilendirme Kuralları",
					ar: "قواعد التصنيف",
					zh: "分类规则",
					fa: "قوانین دسته‌بندی",
					he: "כללי סיווג",
					fr: "Règles de catégorisation",
					es: "Reglas de categorización",
					pt: "Regras de categorização"
				},
				icon: "menu/finance-platform-categories.png",
				route: "finance/rules",
				permissionModel: "financeCategory"
			},
			{
				name: {
					en: "Shopping & Prices",
					ru: "Покупки и цены",
					hy: "Գնումներ և գներ",
					tr: "Alışveriş ve Fiyatlar",
					ar: "التسوق والأسعار",
					zh: "购物与价格",
					fa: "خرید و قیمت‌ها",
					he: "קניות ומחירים",
					fr: "Courses et prix",
					es: "Compras y precios",
					pt: "Compras e preços"
				},
				icon: "menu/finance-platform-cards-controls.png",
				route: "finance/shopping",
				permissionModel: "financeShopping"
			},
			{
				name: {
					en: "Finance Settings",
					ru: "Настройки финансов",
					hy: "Ֆինանսական կարգավորումներ",
					tr: "Finans Ayarları",
					ar: "إعدادات المالية",
					zh: "财务设置",
					fa: "تنظیمات مالی",
					he: "הגדרות פיננסים",
					fr: "Paramètres financiers",
					es: "Ajustes financieros",
					pt: "Configurações financeiras"
				},
				icon: "menu/finance-platform-analytics-forecast.png",
				route: "finance/settings",
				permissionModel: "financeSettings"
			},
			{
				name: {
					en: "Platform Operations",
					ru: "Операции платформы",
					hy: "Հարթակի գործողություններ",
					tr: "Platform İşlemleri",
					ar: "عمليات المنصة",
					zh: "平台运营",
					fa: "عملیات پلتفرم",
					he: "תפעול הפלטפורמה",
					fr: "Opérations de la plateforme",
					es: "Operaciones de la plataforma",
					pt: "Operações da plataforma"
				},
				icon: "menu/finance-platform-card-issuers.png",
				route: "finance/platform",
				permissionModel: "financePlatform"
			}
		]
	},
	{
		id: "menu-9",
		title: {
			en: "CM",
			ru: "CM",
			hy: "CM",
			tr: "CM",
			ar: "CM",
			zh: "CM",
			fa: "CM",
			he: "CM",
			fr: "CM",
			es: "CM",
			pt: "CM"
		},
		color: "#6900cd",
		route: "cm",
		icon: "menu/cm.png",
		menu: [
			{
				id: "menu-9-0",
				name: {
					en: "Dashboard",
					ru: "Панель управления",
					hy: "Վահանակ",
					tr: "Kontrol Paneli",
					ar: "لوحة التحكم",
					zh: "控制面板",
					fa: "پیشخوان",
					he: "לוח בקרה",
					fr: "Tableau de bord",
					es: "Panel de control",
					pt: "Painel de controle"
				},
				icon: "menu/cm-dashboard.png",
				route: "cm/cm-dashboard",
				permissionModel: "cmDashboard"
			},
			{
				name: {
					en: "Address",
					ru: "Адрес",
					hy: "Հասցե",
					tr: "Adres",
					ar: "العنوان",
					zh: "地址",
					fa: "آدرس",
					he: "כתובת",
					fr: "Adresse",
					es: "Dirección",
					pt: "Endereço"
				},
				icon: "menu/cm-location-address.png",
				permissionModel: "address",
				subMenu: [
					{
						name: {
							en: "Maps",
							ru: "Карты",
							hy: "Քարտեզներ",
							tr: "Haritalar",
							ar: "خرائط",
							zh: "地图",
							fa: "نقشه‌ها",
							he: "מפות",
							fr: "Cartes",
							es: "Mapas",
							pt: "Mapas"
						},
						route: "cm/address/map",
						icon: "menu/cm-location-maps.png",
						permissionModel: "map"
					},
					{
						name: {
							en: "State",
							ru: "Штат",
							hy: "Պետություն",
							tr: "Eyalet",
							ar: "دولة",
							zh: "州",
							fa: "ایالت",
							he: "מדינה",
							fr: "État",
							es: "Estado",
							pt: "Estado"
						},
						route: "cm/address/state",
						icon: "menu/cm-location-state.png",
						permissionModel: "state"
					},
					{
						name: {
							en: "City",
							ru: "Город",
							hy: "Քաղաք",
							tr: "Şehir",
							ar: "مدينة",
							zh: "城市",
							fa: "شهر",
							he: "עיר",
							fr: "Ville",
							es: "Ciudad",
							pt: "Cidade"
						},
						route: "cm/address/city",
						icon: "menu/cm-location-city.png",
						permissionModel: "city"
					},
					{
						name: {
							en: "Zip Code",
							ru: "Почтовый индекс",
							hy: "Փոստային ինդեքս",
							tr: "Posta Kodu",
							ar: "الرمز البريدي",
							zh: "邮政编码",
							fa: "کد پستی",
							he: "מיקוד",
							fr: "Code postal",
							es: "Código postal",
							pt: "Código postal"
						},
						route: "cm/address/zip-code",
						icon: "menu/cm-location-zip-code.png",
						permissionModel: "zipCode"
					}
				]
			},
			{
				name: {
					en: "Contacts Folder",
					ru: "Контакты",
					hy: "Կոնտակտներ",
					tr: "İletişim",
					ar: "جهات الاتصال",
					zh: "联系人",
					fa: "مخاطبین",
					he: "אנשי קשר",
					fr: "Contacts",
					es: "Contactos",
					pt: "Contatos"
				},
				icon: "menu/cm-contacts-folder.png",
				permissionModel: "contacts",
				subMenu: [
					{
						name: {
							en: "Contact Parts",
							ru: "Части контакта",
							hy: "Կոնտակտի մասեր",
							tr: "İletişim Parçaları",
							ar: "أجزاء الاتصال",
							zh: "联系人部分",
							fa: "قسمت‌های مخاطب",
							he: "חלקי קשר",
							fr: "Parties de contact",
							es: "Partes de contacto",
							pt: "Partes de contato"
						},
						route: "cm/contact/part",
						icon: "menu/cm-contacts-parts.png",
						permissionModel: "contactPart"
					},
					{
						name: {
							en: "Contact Socials",
							ru: "Социальные контакты",
							hy: "Սոցիալ կոնտակտներ",
							tr: "Sosyal İletişim",
							ar: "جهات اتصال اجتماعية",
							zh: "社交联系人",
							fa: "مخاطبین اجتماعی",
							he: "אנשי קשר חברתיים",
							fr: "Contacts sociaux",
							es: "Contactos sociales",
							pt: "Contatos sociais"
						},
						route: "cm/contact/social",
						icon: "menu/cm-contacts-socials.png",
						permissionModel: "contactSocial"
					}
				]
			},
			{
				name: {
					en: "Blog folder",
					ru: "Блог",
					hy: "Բլոգ",
					tr: "Blog",
					ar: "مدونة",
					zh: "博客",
					fa: "وبلاگ",
					he: "בלוג",
					fr: "Blog",
					es: "Blog",
					pt: "Blog"
				},
				icon: "menu/cm-blog-folder.png",
				permissionModel: "blog",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/blog/page",
						icon: "menu/cm-blog-page.png",
						permissionModel: "blog"
					},
					{
						name: {
							en: "List",
							ru: "Список",
							hy: "Ցուցակ",
							tr: "Liste",
							ar: "قائمة",
							zh: "清单",
							fa: "لیست",
							he: "רשימה",
							fr: "Liste",
							es: "Lista",
							pt: "Lista"
						},
						route: "cm/blog/list",
						icon: "menu/cm-blog-list.png",
						permissionModel: "blog"
					}
				]
			},
			{
				name: {
					en: "Events folder",
					ru: "Мероприятия",
					hy: "Իdelays",
					tr: "Etkinlikler",
					ar: "فعاليات",
					zh: "活动",
					fa: "رویدادها",
					he: "אירועים",
					fr: "Événements",
					es: "Eventos",
					pt: "Eventos"
				},
				icon: "menu/cm-events-folder.png",
				permissionModel: "events",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/events/page",
						icon: "menu/cm-events-event-page.png",
						permissionModel: "events"
					},
					{
						name: {
							en: "List",
							ru: "Список",
							hy: "Ցուցակ",
							tr: "Liste",
							ar: "قائمة",
							zh: "清单",
							fa: "لیست",
							he: "רשימה",
							fr: "Liste",
							es: "Lista",
							pt: "Lista"
						},
						route: "cm/events/list",
						icon: "menu/cm-events-event-list.png",
						permissionModel: "events"
					}
				]
			},
			{
				name: {
					en: "Price List folder",
					ru: "Прайс-лист",
					hy: "Գնացուցակ",
					tr: "Fiyat Listesi",
					ar: "قائمة الأسعار",
					zh: "价格列表",
					fa: "لیست قیمت",
					he: "רשימת מחירים",
					fr: "Liste des prix",
					es: "Lista de precios",
					pt: "Lista de preços"
				},
				icon: "menu/cm-price-list-folder.png",
				permissionModel: "priceList",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/price-list/page",
						icon: "menu/cm-price-list-page.png",
						permissionModel: "priceList"
					},
					{
						name: {
							en: "List",
							ru: "Список",
							hy: "Ցուցակ",
							tr: "Liste",
							ar: "قائمة",
							zh: "清单",
							fa: "لیست",
							he: "רשימה",
							fr: "Liste",
							es: "Lista",
							pt: "Lista"
						},
						route: "cm/price-list/list",
						icon: "menu/cm-price-list-list.png",
						permissionModel: "priceList"
					}
				]
			},
			{
				name: {
					en: "For Business folder",
					ru: "Для бизнеса",
					hy: "Բիզնեսի համար",
					tr: "İşletmeler İçin",
					ar: "للأعمال",
					zh: "商业服务",
					fa: "برای کسب و کار",
					he: "לעסקים",
					fr: "Pour les entreprises",
					es: "Para empresas",
					pt: "Para empresas"
				},
				icon: "menu/cm-for-bussiness-folder.png",
				permissionModel: "forBusiness",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/for-business/page",
						icon: "menu/cm-for-bussiness-page.png",
						permissionModel: "forBusiness"
					},
					{
						name: {
							en: "List",
							ru: "Список",
							hy: "Ցուցակ",
							tr: "Liste",
							ar: "قائمة",
							zh: "清单",
							fa: "لیست",
							he: "רשימה",
							fr: "Liste",
							es: "Lista",
							pt: "Lista"
						},
						route: "cm/for-business/list",
						icon: "menu/cm-for-bussiness-list.png",
						permissionModel: "forBusiness"
					}
				]
			},
			{
				name: {
					en: "FAQ",
					ru: "FAQ",
					hy: "FAQ",
					tr: "SSS",
					ar: "الأسئلة الشائعة",
					zh: "常见问题",
					fa: "سوالات متداول",
					he: "שאלות נפוצות",
					fr: "FAQ",
					es: "Preguntas frecuentes",
					pt: "Perguntas frequentes"
				},
				icon: "menu/cm-faq-faq.png",
				permissionModel: "faq",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/faq/page",
						icon: "menu/cm-faq-page.png",
						permissionModel: "faqPage"
					},
					{
						name: {
							en: "Questions",
							ru: "Вопросы",
							hy: "Հարցեր",
							tr: "Sorular",
							ar: "أسئلة",
							zh: "问题",
							fa: "سوالات",
							he: "שאלות",
							fr: "Questions",
							es: "Preguntas",
							pt: "Perguntas"
						},
						route: "cm/faq/question",
						icon: "menu/cm-faq-questions.png",
						permissionModel: "faqQuestion"
					}
				]
			},
			{
				name: {
					en: "Services",
					ru: "Услуги",
					hy: "Ծառայություններ",
					tr: "Servisler",
					ar: "الخدمات",
					zh: "服务",
					fa: "خدمات",
					he: "שירותים",
					fr: "Services",
					es: "Servicios",
					pt: "Serviços"
				},
				icon: "menu/cm-services-services.png",
				permissionModel: "service",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/service/page",
						icon: "menu/cm-services-page.png",
						permissionModel: "servicePage"
					},
					{
						name: {
							en: "List",
							ru: "Список",
							hy: "Ցուցակ",
							tr: "Liste",
							ar: "قائمة",
							zh: "清单",
							fa: "لیست",
							he: "רשימה",
							fr: "Liste",
							es: "Lista",
							pt: "Lista"
						},
						route: "cm/service/list",
						icon: "menu/cm-services-list.png",
						permissionModel: "service"
					}
				]
			},
			{
				name: {
					en: "Subscribe",
					ru: "Подписка",
					hy: "Բաժանորդագրություն",
					tr: "Abonelik",
					ar: "اشتراك",
					zh: "订阅",
					fa: "اشتراک",
					he: "הרשמה",
					fr: "Abonnement",
					es: "Suscripción",
					pt: "Inscrição"
				},
				icon: "menu/cm-subscribe-subscribe.png",
				permissionModel: "subscribePage",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/subscribe/page",
						icon: "menu/cm-subscribe-page.png",
						permissionModel: "subscribePage"
					},
					{
						name: {
							en: "Step",
							ru: "Шаг",
							hy: "Ժամանակ",
							tr: "Adım",
							ar: "خطوة",
							zh: "步骤",
							fa: "مرحله",
							he: "צעד",
							fr: "Étape",
							es: "Paso",
							pt: "Passo"
						},
						route: "cm/subscribe/step",
						icon: "menu/cm-subscribe-step.png",
						permissionModel: "subscribeStep"
					},
					{
						name: {
							en: "FAQ",
							ru: "FAQ",
							hy: "FAQ",
							tr: "SSS",
							ar: "الأسئلة الشائعة",
							zh: "常见问题",
							fa: "سوالات متداول",
							he: "שאלות נפוצות",
							fr: "FAQ",
							es: "Preguntas frecuentes",
							pt: "Perguntas frequentes"
						},
						route: "cm/subscribe/faq",
						icon: "menu/cm-subscribe-faq.png",
						permissionModel: "subscribeFAQ"
					}
				]
			},
			{
				name: {
					en: "Services and Prices",
					ru: "Услуги и цены",
					hy: "Ծառայություններ և գներ",
					tr: "Hizmetler ve Fiyatlar",
					ar: "الخدمات والأسعار",
					zh: "服务和价格",
					fa: "خدمات و قیمت‌ها",
					he: "שירותים ומחירים",
					fr: "Services et prix",
					es: "Servicios y precios",
					pt: "Serviços e preços"
				},
				icon: "menu/cm-services-and-prices.png",
				permissionModel: "servicesAndPrices",
				subMenu: [
					{
						name: {
							en: "List",
							ru: "Список",
							hy: "Ցուցակ",
							tr: "Liste",
							ar: "قائمة",
							zh: "清单",
							fa: "لیست",
							he: "רשימה",
							fr: "Liste",
							es: "Lista",
							pt: "Lista"
						},
						route: "cm/services-and-prices/list",
						icon: "menu/cm-services-and-prices-list.png",
						permissionModel: "servicesAndPrices"
					}
				]
			},
			{
				name: {
					en: "What You Get",
					ru: "Что вы получаете",
					hy: "Ինչ եք ստանում",
					tr: "Ne Alırsınız",
					ar: "ما تحصل عليه",
					zh: "您得到什么",
					fa: "چه چیزی دریافت می‌کنید",
					he: "מה אתה מקבל",
					fr: "Ce que vous obtenez",
					es: "Lo que obtienes",
					pt: "O que você obtém"
				},
				icon: "menu/cm-about-what-you-get.png",
				route: "cm/what-you-get",
				permissionModel: "whatYouGet"
			},
			{
				name: {
					en: "Reviews",
					ru: "Отзывы",
					hy: "Կոնտակտներ",
					tr: "Yorumlar",
					ar: "المراجعات",
					zh: "评论",
					fa: "نظرات",
					he: "ביקורות",
					fr: "Avis",
					es: "Reseñas",
					pt: "Avaliações"
				},
				icon: "menu/cm-reviews.png",
				route: "cm/review",
				permissionModel: "review"
			},
			{
				name: {
					en: "Companies",
					ru: "Компании",
					hy: "Ընկերներ",
					tr: "Şirketler",
					ar: "الشركات",
					zh: "公司",
					fa: "شرکت‌ها",
					he: "חברות",
					fr: "Entreprises",
					es: "Empresas",
					pt: "Empresas"
				},
				icon: "menu/cm-companies.png",
				route: "cm/company",
				permissionModel: "company"
			},
			{
				name: {
					en: "About",
					ru: "О нас",
					hy: "Մեր մասին",
					tr: "Hakkında",
					ar: "حول",
					zh: "关于",
					fa: "درباره",
					he: "אודות",
					fr: "Sur",
					es: "Acerca de",
					pt: "Sobre"
				},
				route: "cm/about",
				icon: "menu/cm-about.png",
				permissionModel: "about",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/about-us/page",
						icon: "menu/cm-about-page.png",
						permissionModel: "about"
					},
					{
						name: {
							en: "Items",
							ru: "Элементы",
							hy: "Էլեմենտներ",
							tr: "Öğeler",
							ar: "عناصر",
							zh: "元素",
							fa: "عناصر",
							he: "אלמנטים",
							fr: "Éléments",
							es: "Elementos",
							pt: "Elementos"
						},
						icon: "menu/cm-about-items.png",
						route: "cm/about-us/item",
						permissionModel: "aboutUsItem"
					},
					{
						name: {
							en: "Who we are",
							ru: "Кто мы",
							hy: "Մենք որպես",
							tr: "Kim olduğumuz",
							ar: "من نحن",
							zh: "我们是谁",
							fa: "ما کی هستیم",
							he: "מי אנחנו",
							fr: "Qui sommes-nous",
							es: "Quiénes somos",
							pt: "Quem somos"
						},
						icon: "menu/cm-who-we-are.png",
						route: "cm/about-us/who-we-are",
						permissionModel: "who-we-are"
					},
					{
						name: {
							en: "Our mission",
							ru: "Наша миссия",
							hy: "Մեր առաջինը",
							tr: "Vizyonumuz",
							ar: "رسالتنا",
							zh: "我们的使命",
							fa: "ماموریت ما",
							he: "המשימה שלנו",
							fr: "Notre mission",
							es: "Nuestra misión",
							pt: "Nossa missão"
						},
						icon: "menu/cm-our-mission.png",
						route: "cm/about-us/our-mission",
						permissionModel: "our-mission"
					}
				]
			},
			{
				name: {
					en: "Instagram",
					ru: "Инстаграм",
					hy: "Instagram",
					tr: "Instagram",
					ar: "Instagram",
					zh: "Instagram",
					fa: "اینستاگرام",
					he: "אינסטגרם",
					fr: "Instagram",
					es: "Instagram",
					pt: "Instagram"
				},
				icon: "menu/cm-instagram.png",
				permissionModel: "instagramSection",
				subMenu: [
					{
						name: {
							en: "Page",
							ru: "Страница",
							hy: "Էջ",
							tr: "Sayfa",
							ar: "صفحة",
							zh: "页面",
							fa: "صفحه",
							he: "עמוד",
							fr: "Page",
							es: "Página",
							pt: "Página"
						},
						route: "cm/instagram/page",
						icon: "menu/cm-instagram-page.png",
						permissionModel: "instagramSection"
					},
					{
						name: {
							en: "Slide",
							ru: "Слайд",
							hy: "Սլայդ",
							tr: "Kaydırıcı",
							ar: "شريحة",
							zh: "幻灯片",
							fa: "اسلاید",
							he: "שקופית",
							fr: "Diapositive",
							es: "Diapositiva",
							pt: "Slide"
						},
						route: "cm/instagram",
						icon: "menu/cm-instagram-slide.png",
						permissionModel: "instagramSection"
					}
				]
			},
			{
				name: {
					en: "Banners",
					ru: "Баннеры",
					hy: "Բաներ",
					tr: "Pankartlar",
					ar: "لافتات",
					zh: "横幅",
					fa: "بنرها",
					he: "באנרים",
					fr: "Bannières",
					es: "Banners",
					pt: "Banners"
				},
				icon: "menu/cm-banners.png",
				permissionModel: "banner",
				subMenu: [
					{
						name: {
							en: "Item",
							ru: "Элемент",
							hy: "Էլեմենտ",
							tr: "Öğe",
							ar: "عنصر",
							zh: "元素",
							fa: "عنصر",
							he: "אלמנט",
							fr: "Élément",
							es: "Elemento",
							pt: "Elemento"
						},
						route: "cm/banner/item",
						icon: "menu/cm-banner-item.png",
						permissionModel: "banner"
					},
					{
						name: {
							en: "Slide",
							ru: "Слайд",
							hy: "Սլայդ",
							tr: "Kaydırıcı",
							ar: "شريحة",
							zh: "幻灯片",
							fa: "اسلاید",
							he: "שקופית",
							fr: "Diapositive",
							es: "Diapositiva",
							pt: "Slide"
						},
						route: "cm/banner/slide",
						icon: "menu/cm-banner-slide.png",
						permissionModel: "slide"
					}
				]
			},
			{
				name: {
					en: " Privacy Policy",
					ru: "Политика конфиденциальности",
					hy: "Գաղտնիության քաղաքականություն",
					tr: "Gizlilik Politikası",
					ar: "سياسة الخصوصية",
					zh: "隐私政策",
					fa: "سیاست حفظ حریم خصوصی",
					he: "מדיניות פרטיות",
					fr: "Politique de confidentialité",
					es: "Política de privacidad",
					pt: "Política de Privacidade"
				},
				icon: "menu/cm-privacy-policy.png",
				route: "cm/privacy-policy",
				permissionModel: "privacyPolicy"
			},
			{
				name: {
					en: "Terms and Conditions",
					ru: "Условия и положения",
					hy: "Պայմաններ և պայմաններ",
					tr: "Şartlar ve Koşullar",
					ar: "البنود والشروط",
					zh: "条款和条件",
					fa: "شرایط و ضوابط",
					he: "תנאים והגבלות",
					fr: "Termes et conditions",
					es: "Términos y condiciones",
					pt: "Termos e Condições"
				},
				icon: "menu/cm-terms-and-conditions.png",
				route: "cm/terms-and-conditions",
				permissionModel: "termsAndConditions"
			},
			{
				name: {
					en: "Cookie Policy",
					ru: "Политика использования файлов cookie",
					hy: "Cookie-ների քաղաքականություն",
					tr: "Çerez Politikası",
					ar: "سياسة ملفات تعريف الارتباط",
					zh: "Cookie 政策",
					fa: "خط‌مشی کوکی",
					he: "מדיניות עוגיות",
					fr: "Politique de cookies",
					es: "Política de cookies",
					pt: "Política de Cookies"
				},
				icon: "menu/cm-privacy-policy.png",
				route: "cm/cookie-policy",
				permissionModel: "cookiePolicy"
			},
			{
				name: {
					en: "Certificate and Promocode Page",
					ru: "Страница сертификата и промокода",
					hy: "Սերտիֆիկատի և պրոմոկոդի էջ",
					tr: "Sertifika ve Promosyon Kodu Sayfası",
					ar: "صفحة الشهادة ورمز الترويج",
					zh: "证书和促销代码页面",
					fa: "صفحه گواهی‌نامه و کد تبلیغاتی",
					he: "דף תעודה וקוד קידום",
					fr: "Page de certificat et de code promotionnel",
					es: "Página de certificado y código promocional",
					pt: "Página de certificado e código promocional"
				},
				route: "cm/certificate-promo-code",
				icon: "menu/cm-certificate-and-promocode-page.png",
				permissionModel: "certificatePromoCode"
			},
			{
				name: {
					en: "Delivery and Payment",
					ru: "Доставка и оплата",
					hy: "Առաքում և վճարում",
					tr: "Teslimat ve Ödeme",
					ar: "التسليم والدفع",
					zh: "交付和付款",
					fa: "تحویل و پرداخت",
					he: "משלוח ותשלום",
					fr: "Livraison et paiement",
					es: "Envío y pago",
					pt: "Envio e pagamento"
				},
				route: "cm/delivery-and-payment",
				icon: "menu/cm-delivery-and-payment.png",
				permissionModel: "deliveryAndPayment"
			}
		]
	},
	{
		id: "menu-10",
		title: {
			en: "Radio",
			ru: "Радио",
			hy: "Ռադիո",
			tr: "Radyo",
			ar: "راديو",
			zh: "广播",
			fa: "رادیو",
			he: "רדיו",
			fr: "Radio",
			es: "Radio",
			pt: "Rádio"
		},
		color: "#f51711",
		route: "radio",
		icon: "menu/radio-radio.png",
		menu: [
			{
				id: "menu-10-0",
				name: {
					en: "Dashboard",
					ru: "Панель управления",
					hy: "Վահանակ",
					tr: "Kontrol Paneli",
					ar: "لوحة التحكم",
					zh: "控制面板",
					fa: "پیشخوان",
					he: "לוח בקרה",
					fr: "Tableau de bord",
					es: "Panel de control",
					pt: "Painel de controle"
				},
				icon: "menu/radio-dashboard.png",
				route: "radio/dashboard",
				permissionModel: "radioDashboard"
			},
			{
				name: {
					en: "Artists",
					ru: "Исполнители",
					hy: "Արտիստներ",
					tr: "Sanatçılar",
					ar: "فنانين",
					zh: "艺术家",
					fa: "هنرمندان",
					he: "אמנים",
					fr: "Artistes",
					es: "Artistas",
					pt: "Artistas"
				},
				icon: "menu/radio-artist.png",
				route: "radio/artist",
				permissionModel: "artist"
			},
			{
				name: {
					en: "Tracks",
					ru: "Треки",
					hy: "Երաժշտական գործեր",
					tr: "Parçalar",
					ar: "المسارات",
					zh: "曲目",
					fa: "قطعات",
					he: "שירים",
					fr: "Pistes",
					es: "Pistas",
					pt: "Pistas"
				},
				icon: "menu/radio-tracks.png",
				route: "radio/track",
				permissionModel: "track"
			},
			{
				name: {
					en: "Playlists",
					ru: "Плейлисты",
					hy: "Նվագարկիչներ",
					tr: "Çalma Listeleri",
					ar: "قوائم التشغيل",
					zh: "播放列表",
					fa: "لیست‌های پخش",
					he: "פלייליסטים",
					fr: "Listes de lecture",
					es: "Listas de reproducción",
					pt: "Listas de reprodução"
				},
				icon: "menu/radio-playlists.png",
				route: "radio/playlist",
				permissionModel: "playlist"
			},
			{
				name: {
					en: "Advertisements",
					ru: "Реклама",
					hy: "Հայտարարություններ",
					tr: "Reklamlar",
					ar: "إعلانات",
					zh: "广告",
					fa: "تبلیغات",
					he: "פרסומות",
					fr: "Publicités",
					es: "Anuncios",
					pt: "Anúncios"
				},
				icon: "menu/radio-advertisements.png",
				route: "radio/advertisement",
				permissionModel: "advertisement"
			},
			{
				name: {
					en: "Advertisement Playlists",
					ru: "Плейлисты рекламы",
					hy: "Հայտարարությունների նվագարկիչներ",
					tr: "Reklam Çalma Listeleri",
					ar: "قوائم تشغيل الإعلانات",
					zh: "广告播放列表",
					fa: "لیست‌های پخش تبلیغات",
					he: "פלייליסטים לפרסומות",
					fr: "Listes de lecture publicitaire",
					es: "Listas de reproducción de anuncios",
					pt: "Listas de reprodução de anúncios"
				},
				icon: "menu/radio-advertisement-playlist.png",
				route: "radio/advertisement-playlist",
				permissionModel: "advertisementPlaylist"
			},
			{
				name: {
					en: "Streams",
					ru: "Потоки",
					hy: "Հոսքեր",
					tr: "Akışlar",
					ar: "تدفقات",
					zh: "流",
					fa: "جریان‌ها",
					he: "זרמים",
					fr: "Flux",
					es: "Flujos",
					pt: "Fluxos"
				},
				icon: "menu/readio-streams.png",
				route: "radio/stream",
				permissionModel: "stream"
			}
		]
	},
	{
		id: "menu-11",
		title: {
			en: "Files",
			ru: "Файлы",
			hy: "Ֆայլեր",
			tr: "Dosyalar",
			ar: "الملفات",
			zh: "文件",
			fa: "فایل‌ها",
			he: "קבצים",
			fr: "Fichiers",
			es: "Archivos",
			pt: "Arquivos"
		},
		color: "#fb970f",
		route: "files",
		icon: "menu/files.png",
		menu: [
			{
				id: "menu-11-files-0",
				name: {
					en: "Files",
					ru: "Файлы",
					hy: "Ֆայլեր",
					tr: "Dosyalar",
					ar: "الملفات",
					zh: "文件",
					fa: "فایل‌ها",
					he: "קבצים",
					fr: "Fichiers",
					es: "Archivos",
					pt: "Arquivos"
				},
				icon: "menu/files-main.png",
				route: "files",
				permissionModel: "files"
			}
		]
	},
	{
		id: "menu-11-media",
		title: {
			en: "Media",
			ru: "Медиа",
			hy: "Մեդիա",
			tr: "Medya",
			ar: "وسائط",
			zh: "媒体",
			fa: "رسانه",
			he: "מדיה",
			fr: "Média",
			es: "Medios",
			pt: "Mídia"
		},
		color: "#02789a",
		route: "media",
		icon: "menu/media-media.png",
		menu: [
			{
				name: {
					en: "Photos",
					ru: "Фото",
					hy: "Լուսանկարներ",
					tr: "Fotoğraflar",
					ar: "الصور",
					zh: "照片",
					fa: "عکس‌ها",
					he: "תמונות",
					fr: "Photos",
					es: "Fotos",
					pt: "Fotos"
				},
				icon: "menu/media-gallery.png",
				route: "media/photos",
				permissionModel: "photos"
			},
			{
				name: {
					en: "Places",
					ru: "Места",
					hy: "Վայրեր",
					tr: "Yerler",
					ar: "الأماكن",
					zh: "地点",
					fa: "مکان‌ها",
					he: "מקומות",
					fr: "Lieux",
					es: "Lugares",
					pt: "Lugares"
				},
				icon: "menu/media-map.png",
				route: "media/places",
				permissionModel: "galleryMap"
			},
			{
				id: "menu-11-0",
				name: {
					en: "Gallery",
					ru: "Галерея",
					hy: "Պատկերասրահ",
					tr: "Galeri",
					ar: "المعرض",
					zh: "画廊",
					fa: "گالری",
					he: "גלריה",
					fr: "Galerie",
					es: "Galería",
					pt: "Galeria"
				},
				icon: "menu/media-gallery.png",
				route: "media/gallery",
				permissionModel: "gallery"
			},
			{
				name: {
					en: "Map",
					ru: "Карта",
					hy: "Քարտեզ",
					tr: "Harita",
					ar: "الخريطة",
					zh: "地图",
					fa: "خریطه",
					he: "מפה",
					fr: "Carte",
					es: "Mapa",
					pt: "Mapa"
				},
				icon: "menu/media-map.png",
				route: "media/map",
				permissionModel: "galleryMap"
			},
			{
				name: {
					en: "People",
					ru: "Люди",
					hy: "Անուններ",
					tr: "İsimler",
					ar: "الأسماء",
					zh: "人名",
					fa: "نام‌ها",
					he: "שמות",
					fr: "Noms",
					es: "Nombres",
					pt: "Nomes"
				},
				icon: "menu/media-people.png",
				route: "media/people",
				permissionModel: "galleryPeople"
			}
		]
	},
	{
		id: "menu-12",
		title: {
			en: "Todo",
			ru: "Todo",
			hy: "Todo",
			tr: "Todo",
			ar: "Todo",
			zh: "Todo",
			fa: "Todo",
			he: "Todo",
			fr: "Todo",
			es: "Todo",
			pt: "Todo"
		},
		color: "#72ad0d",
		route: "todo",
		icon: "menu/todo.png",
		menu: [
			{
				id: "menu-12-0",
				name: {
					en: "Boards",
					ru: "Доски",
					hy: "Տախտակներ",
					tr: "Panolar",
					ar: "اللوحات",
					zh: "看板",
					fa: "بردها",
					he: "לוחות",
					fr: "Tableaux",
					es: "Tableros",
					pt: "Quadros"
				},
				icon: "menu/todo-kanban.png", // or use existing icon
				route: "todo/boards",
				permissionModel: "todoTask" // Uses same permission as tasks
			},
			{
				name: {
					en: "Categories",
					ru: "Категории",
					hy: "Կատեգորիաներ",
					tr: "Kategoriler",
					ar: "الفئات",
					zh: "类别",
					fa: "دسته‌ها",
					he: "קטגוריות",
					fr: "Catégories",
					es: "Categorías",
					pt: "Categorias"
				},
				icon: "menu/todo-categories.png",
				route: "todo/category",
				permissionModel: "todoCategory"
			},
			{
				id: "menu-12-add-task",
				name: {
					en: "Add Task",
					ru: "Добавить задачу",
					hy: "Ավելացնել առաջադրանք",
					tr: "Görev Ekle",
					ar: "إضافة مهمة",
					zh: "添加任务",
					fa: "افزودن کار",
					he: "הוסף משימה",
					fr: "Ajouter une tâche",
					es: "Añadir tarea",
					pt: "Adicionar tarefa"
				},
				icon: "menu/todo-tasks.png",
				route: "todo/task/add",
				permissionModel: "todoTask"
			},
			{
				name: {
					en: "My Tasks",
					ru: "Мои задачи",
					hy: "Իմ առաջադրանքները",
					tr: "Görevlerim",
					ar: "مهامي",
					zh: "我的任务",
					fa: "کارهای من",
					he: "המשימות שלי",
					fr: "Mes tâches",
					es: "Mis tareas",
					pt: "Minhas tarefas"
				},
				icon: "menu/todo-tasks.png",
				route: "todo/personal",
				permissionModel: "todoTask"
			},
			{
				name: {
					en: "Tags",
					ru: "Теги",
					hy: "Թեգեր",
					tr: "Etiketler",
					ar: "الوسوم",
					zh: "标签",
					fa: "برچسب‌ها",
					he: "תגיות",
					fr: "Tags",
					es: "Etiquetas",
					pt: "Tags"
				},
				icon: "menu/todo-tags.png",
				route: "todo/tag",
				permissionModel: "todoTag"
			},
			{
				name: {
					en: "Labels",
					ru: "Метки",
					hy: "Լեբելներ",
					tr: "Etiketler",
					ar: "الوسوم",
					zh: "标签",
					fa: "برچسب‌ها",
					he: "תגיות",
					fr: "Étiquettes",
					es: "Etiquetas",
					pt: "Etiquetas"
				},
				icon: "menu/todo-labels.png",
				route: "todo/label",
				permissionModel: "todoLabel"
			},
			{
				id: "menu-12-workload-matrix",
				name: {
					en: "Workload Matrix",
					ru: "Матрица нагрузки",
					hy: "Ծանրաբեռնվածության մատրիցա",
					tr: "İş Yükü Matrisi",
					ar: "مصفوفة عبء العمل",
					zh: "工作量矩阵",
					fa: "ماتریس حجم کاری",
					he: "מטריצת עומס עבודה",
					fr: "Matrice de charge de travail",
					es: "Matriz de carga de trabajo",
					pt: "Matriz de carga de trabalho"
				},
				icon: "menu/project-dashboard.png",
				permissionModel: "todoWorkloadMatrix",
				subMenu: [
					{
						id: "menu-12-workload-matrix-dashboard",
						name: {
							en: "Dashboard",
							ru: "Панель",
							hy: "Վահանակ",
							tr: "Gösterge Paneli",
							ar: "لوحة التحكم",
							zh: "仪表板",
							fa: "داشبورد",
							he: "לוח מחוונים",
							fr: "Tableau de bord",
							es: "Panel",
							pt: "Painel"
						},
						icon: "menu/project-dashboard.png",
						route: "todo/workload-matrix",
						permissionModel: "todoWorkloadMatrix"
					},
					{
						id: "menu-12-workload-matrix-board-groups",
						name: {
							en: "Board Groups",
							ru: "Группы досок",
							hy: "Տախտակների խմբեր",
							tr: "Pano Grupları",
							ar: "مجموعات اللوحات",
							zh: "看板组",
							fa: "گروه‌های تابلو",
							he: "קבוצות לוחות",
							fr: "Groupes de tableaux",
							es: "Grupos de tableros",
							pt: "Grupos de quadros"
						},
						icon: "menu/todo-boards.png",
						route: "todo/workload-matrix/board-groups",
						matchSubRoutes: true,
						permissionModel: "todoBoardGroup"
					},
					{
						id: "menu-12-workload-matrix-category-groups",
						name: {
							en: "Category Groups",
							ru: "Группы категорий",
							hy: "Կատեգորիաների խմբեր",
							tr: "Kategori Grupları",
							ar: "مجموعات الفئات",
							zh: "类别组",
							fa: "گروه‌های دسته‌بندی",
							he: "קבוצות קטגוריות",
							fr: "Groupes de catégories",
							es: "Grupos de categorías",
							pt: "Grupos de categorias"
						},
						icon: "menu/todo-categories.png",
						route: "todo/workload-matrix/category-groups",
						matchSubRoutes: true,
						permissionModel: "todoCategoryGroup"
					}
				]
			}
		]
	},
	{
		id: "menu-knowledge",
		title: {
			en: "Knowledge Base",
			ru: "База знаний",
			hy: "Գիտելիքների բազա",
			tr: "Bilgi Bankası",
			ar: "قاعدة المعرفة",
			zh: "知识库",
			fa: "پایگاه دانش",
			he: "מאגר ידע",
			fr: "Base de connaissances",
			es: "Base de conocimiento",
			pt: "Base de conhecimento"
		},
		color: "#e79800",
		route: "knowledge",
		icon: "menu/knowledge-base.png",
		permissionModel: "knowledge",
		menu: [
			{
				id: "menu-knowledge-0",
				name: { en: "Dashboard", ru: "Панель", hy: "Վահանակ" },
				icon: "menu/knowledge-base-dashboard.png",
				route: "knowledge",
				permissionModel: "knowledge"
			},
			{
				id: "menu-knowledge-1",
				name: { en: "Articles", ru: "Статьи", hy: "Հոդվածներ" },
				icon: "menu/knowledge-base-articles.png",
				route: "knowledge/articles",
				permissionModel: "knowledge"
			},
			{
				id: "menu-knowledge-2",
				name: { en: "Categories", ru: "Категории", hy: "Կատեգորիաներ" },
				icon: "menu/knowledge-base-categories.png",
				route: "knowledge/categories",
				permissionModel: "knowledge"
			},
			{
				id: "menu-knowledge-3",
				name: { en: "Auto Generated Docs", ru: "Автодокументация", hy: "Ինքնաստեղծ փաստաթղթեր" },
				icon: "menu/knowledge-base-auto-generated-docs.png",
				route: "knowledge/auto-docs",
				permissionModel: "knowledge"
			},
			{
				id: "menu-knowledge-4",
				name: { en: "Search", ru: "Поиск", hy: "Որոնում" },
				icon: "menu/knowledge-base-search.png",
				route: "knowledge/search",
				permissionModel: "knowledge"
			}
		]
	},
	{
		id: "menu-forms",
		title: {
			en: "Forms & Surveys",
			ru: "Формы и опросы",
			hy: "Ձևեր և հարցումներ",
			tr: "Formlar ve Anketler",
			ar: "النماذج والاستطلاعات",
			zh: "表单与调查",
			fa: "فرم‌ها و نظرسنجی‌ها",
			he: "טפסים וסקרים",
			fr: "Formulaires et enquêtes",
			es: "Formularios y encuestas",
			pt: "Formulários e pesquisas"
		},
		color: "#6a27c0",
		route: "forms",
		icon: "menu/forms-and-surveys.png",
		permissionModel: "forms",
		menu: [
			{
				id: "menu-forms-0",
				name: { en: "All Forms", ru: "Все формы", hy: "Բոլոր ձևերը" },
				icon: "menu/forms-and-surveys-all-forms.png",
				route: "forms",
				permissionModel: "forms"
			},
			{
				id: "menu-forms-1",
				name: { en: "Active Surveys", ru: "Активные опросы", hy: "Ակտիվ հարցումներ" },
				icon: "menu/forms-and-surveys-active-surveys.png",
				route: "forms/active",
				permissionModel: "forms"
			},
			{
				id: "menu-forms-2",
				name: { en: "Create Form", ru: "Создать форму", hy: "Ստեղծել ձև" },
				icon: "menu/forms-and-surveys-create-form.png",
				route: "forms/create",
				permissionModel: "forms",
				requiresAnyPermission: ["forms.create"]
			}
		]
	},
	{
		id: "menu-14",
		title: {
			en: "Security",
			ru: "Безопасность",
			hy: "Անվտանգություն",
			tr: "Güvenlik",
			ar: "الأمان",
			zh: "安全",
			fa: "امنیت",
			he: "אבטחה",
			fr: "Sécurité",
			es: "Seguridad",
			pt: "Segurança"
		},
		color: "#0d52e8",
		route: "security",
		icon: "menu/security.png",
		menu: [
			{
				id: "menu-14-0",
				name: {
					en: "Overview",
					ru: "Обзор",
					hy: "Ամփոփ",
					tr: "Genel Bakış",
					ar: "نظرة عامة",
					zh: "概览",
					fa: "نمای کلی",
					he: "סקירה",
					fr: "Aperçu",
					es: "Resumen",
					pt: "Visão geral"
				},
				icon: "menu/security-overview.png",
				route: "security/overview",
				permissionModel: "securityOverview"
			},
			{
				id: "menu-14-0a",
				name: {
					en: "Clients",
					ru: "Клиенты",
					hy: "Հաճախորդներ",
					tr: "İstemciler",
					ar: "العملاء",
					zh: "客户端",
					fa: "کلاینت‌ها",
					he: "לקוחות",
					fr: "Clients",
					es: "Clientes",
					pt: "Clientes"
				},
				icon: "menu/security-clients.png",
				route: "security/clients",
				permissionModel: "securityClients"
			},
			{
				id: "menu-14-1",
				name: {
					en: "Devices",
					ru: "Устройства",
					hy: "Սարքեր",
					tr: "Cihazlar",
					ar: "الأجهزة",
					zh: "设备",
					fa: "دستگاه‌ها",
					he: "מכשירים",
					fr: "Appareils",
					es: "Dispositivos",
					pt: "Dispositivos"
				},
				icon: "menu/security-devices.png",
				route: "security/devices",
				permissionModel: "securityDevices"
			},
			{
				id: "menu-14-1s",
				name: {
					en: "Surveillance System",
					ru: "Система наблюдения",
					hy: "Տեսահսկման համակարգ",
					tr: "Gözetim Sistemi",
					ar: "نظام المراقبة",
					zh: "监控系统",
					fa: "سامانه نظارت",
					he: "מערכת מעקב",
					fr: "Système de surveillance",
					es: "Sistema de vigilancia",
					pt: "Sistema de vigilância"
				},
				icon: "menu/security-surveillance-system.png",
				subMenu: [
					{
						id: "menu-14-1s-0",
						name: {
							en: "Dashboard",
							ru: "Панель",
							hy: "Վահանակ",
							tr: "Pano",
							ar: "لوحة التحكم",
							zh: "仪表板",
							fa: "داشبورد",
							he: "לוח בקרה",
							fr: "Tableau de bord",
							es: "Panel",
							pt: "Painel"
						},
						icon: "menu/security-dashboard.png",
						route: "security/surveillance-system/dashboard",
						permissionModel: "surveillanceDashboard"
					},
					{
						id: "menu-14-1s-1",
						name: {
							en: "NVR",
							ru: "Видеорегистратор",
							hy: "NVR",
							tr: "NVR",
							ar: "مسجل NVR",
							zh: "网络录像机",
							fa: "ضبط‌کننده شبکه",
							he: "NVR",
							fr: "NVR",
							es: "NVR",
							pt: "NVR"
						},
						icon: "menu/security-nvr.png",
						route: "security/nvr",
						permissionModel: "nvr"
					},
					{
						id: "menu-14-1s-2",
						name: {
							en: "Cameras",
							ru: "Камеры",
							hy: "Տեսախցիկներ",
							tr: "Kameralar",
							ar: "كاميرات",
							zh: "摄像头",
							fa: "دوربین‌ها",
							he: "מצלמות",
							fr: "Caméras",
							es: "Cámaras",
							pt: "Câmeras"
						},
						icon: "menu/security-cameras.png",
						route: "security/cameras",
						permissionModel: "pc"
					}
				]
			},
			{
				id: "menu-14-2",
				name: {
					en: "Alerts",
					ru: "Оповещения",
					hy: "Ծանուցումներ",
					tr: "Uyarılar",
					ar: "التنبيهات",
					zh: "警报",
					fa: "هشدارها",
					he: "התראות",
					fr: "Alertes",
					es: "Alertas",
					pt: "Alertas"
				},
				icon: "menu/security-alerts.png",
				route: "security/alerts",
				permissionModel: "securityAlerts"
			},
			{
				id: "menu-14-3",
				name: {
					en: "Sessions",
					ru: "Сессии",
					hy: "Սեսիաներ",
					tr: "Oturumlar",
					ar: "الجلسات",
					zh: "会话",
					fa: "جلسات",
					he: "פגישות",
					fr: "Sessions",
					es: "Sesiones",
					pt: "Sessões"
				},
				icon: "menu/security-sessions.png",
				route: "security/sessions",
				permissionModel: "securitySessions"
			},
			{
				id: "menu-14-4",
				name: {
					en: "Media",
					ru: "Медиа",
					hy: "Մեդիա",
					tr: "Medya",
					ar: "الوسائط",
					zh: "媒体",
					fa: "رسانه",
					he: "מדיה",
					fr: "Médias",
					es: "Medios",
					pt: "Mídia"
				},
				icon: "menu/security-media.png",
				route: "security/media",
				permissionModel: "securityMedia"
			},
			{
				id: "menu-14-5",
				name: {
					en: "Policies",
					ru: "Политики",
					hy: "Քաղաքականություններ",
					tr: "Politikalar",
					ar: "السياسات",
					zh: "策略",
					fa: "سیاست‌ها",
					he: "מדיניות",
					fr: "Politiques",
					es: "Políticas",
					pt: "Políticas"
				},
				icon: "menu/security-policies.png",
				route: "security/policies",
				permissionModel: "securityPolicies"
			},
			{
				id: "menu-14-6",
				name: {
					en: "IP Policy",
					ru: "IP политика",
					hy: "IP քաղաքականություն",
					tr: "IP Politikası",
					ar: "سياسة IP",
					zh: "IP 策略",
					fa: "سیاست IP",
					he: "מדיניות IP",
					fr: "Politique IP",
					es: "Política IP",
					pt: "Política de IP"
				},
				icon: "menu/security-ip-policy.png",
				route: "security/ip-policy",
				permissionModel: "securityIpPolicy"
			}
		]
	},
	// Company Automation Center
	{
		id: "menu-cac",
		title: {
			en: "Automation",
			ru: "Автоматизация",
			hy: "Ավտոմատացում",
			tr: "Otomasyon",
			ar: "الأتمتة",
			zh: "自动化",
			fa: "اتوماسیون",
			he: "אוטומציה",
			fr: "Automatisation",
			es: "Automatización",
			pt: "Automação"
		},
		color: "#1896fc",
		route: "automation",
		icon: "menu/automation.png",
		menu: [
			{
				id: "menu-cac-0",
				name: {
					en: "Dashboard",
					ru: "Информационная панель",
					hy: "Էկրան",
					tr: "Kontrol Paneli",
					ar: "لوحة التحكم",
					zh: "仪表板",
					fa: "داشبورد",
					he: "לוח בקרה",
					fr: "Tableau de bord",
					es: "Panel de control",
					pt: "Painel de controle"
				},
				icon: "menu/automation-dashboard.png",
				route: "automation/dashboard",
				permissionModel: "automation"
			},
			{
				id: "menu-cac-1",
				name: {
					en: "Rules",
					ru: "Правила",
					hy: "Կանոններ",
					tr: "Kurallar",
					ar: "القواعد",
					zh: "规则",
					fa: "قوانین",
					he: "כללים",
					fr: "Règles",
					es: "Reglas",
					pt: "Regras"
				},
				icon: "menu/automation-rules.png",
				route: "automation/rules",
				permissionModel: "automation"
			},
			{
				id: "menu-cac-2",
				name: {
					en: "Logs",
					ru: "Журналы",
					hy: "Մատյաններ",
					tr: "Günlükler",
					ar: "السجلات",
					zh: "日志",
					fa: "گزارشات",
					he: "רישומים",
					fr: "Journaux",
					es: "Registros",
					pt: "Registros"
				},
				icon: "menu/automation-logs.png",
				route: "automation/logs",
				permissionModel: "automation"
			}
		]
	},
	{
		id: "menu-office",
		title: {
			en: "Office",
			ru: "Офис",
			hy: "Գրասենյակ",
			tr: "Ofis",
			ar: "المكتب",
			zh: "办公室",
			fa: "دفتر",
			he: "משרד",
			fr: "Bureau",
			es: "Oficina",
			pt: "Escritório"
		},
		color: "#167493",
		route: "office",
		icon: "menu/office.png",
		permissionModel: "office",
		menu: [
			{
				id: "menu-office-overview",
				name: { en: "Overview", ru: "Обзор", hy: "Ընդհանուր" },
				icon: "menu/office-dashboard.png",
				permissionModel: "office",
				officeAudience: "management",
				subMenu: [
					{ id: "menu-office-dashboard", name: { en: "Dashboard", ru: "Панель управления", hy: "Վահանակ" }, icon: "menu/office-dashboard.png", route: "office/dashboard", permissionModel: "office", accessPermissionModel: "office.dashboard" }
				]
			},
			{
				id: "menu-office-spaces",
				name: { en: "Spaces", ru: "Пространства", hy: "Տարածքներ" },
				icon: "menu/office-offices-and-locations.png",
				permissionModel: "office",
				officeAudience: "management",
				subMenu: [
					{
						id: "menu-office-locations",
						name: { en: "Locations", ru: "Локации", hy: "Վայրեր" },
						icon: "menu/office-offices-and-locations.png",
						route: "office/infrastructure/locations",
						permissionModel: "office",
						accessPermissionModel: "office.locations"
					},
					{
						id: "menu-office-floors-and-maps",
						name: { en: "Floors & Maps", ru: "Этажи и карты", hy: "Հարկեր և քարտեզներ" },
						icon: "menu/office-floors.png",
						route: "office/spaces/floors-maps",
						permissionModel: "office",
						accessPermissionModel: "office.floors"
					},
					{
						id: "menu-office-rooms",
						name: { en: "Rooms", ru: "Комнаты", hy: "Սենյակներ" },
						icon: "menu/office-rooms.png",
						route: "office/infrastructure/rooms",
						permissionModel: "office",
						accessPermissionModel: "office.rooms"
					},
					{
						id: "menu-office-desks",
						name: { en: "Desks", ru: "Рабочие места", hy: "Աշխատատեղեր" },
						icon: "menu/office-desks.png",
						route: "office/infrastructure/desks",
						permissionModel: "office",
						accessPermissionModel: "office.desks"
					}
				]
			},
			{
				id: "menu-office-bookings",
				name: { en: "Bookings", ru: "Бронирования", hy: "Ամրագրումներ" },
				icon: "menu/office-book-a-room.png",
				permissionModel: "office",
				officeAudience: "management",
				subMenu: [
					{ id: "menu-office-reservations", name: { en: "Reservations", ru: "Бронирования", hy: "Ամրագրումներ" }, icon: "menu/office-book-a-room.png", route: "office/bookings/reservations", permissionModel: "office", accessPermissionModel: "office.bookings", badgeKey: "pendingApproval" },
					{ id: "menu-office-resources", name: { en: "Resources & Amenities", ru: "Ресурсы и удобства", hy: "Ռեսուրսներ և հարմարություններ" }, icon: "menu/office-resources.png", route: "office/reservations/resources", permissionModel: "office", accessPermissionModel: "office.resources" },
					{ id: "menu-office-booking-policies", name: { en: "Booking Policies", ru: "Политики бронирования", hy: "Ամրագրման կանոններ" }, icon: "menu/office-dashboard.png", route: "office/reservations/booking-policies", permissionModel: "office", accessPermissionModel: "office.bookingPolicies", requiresAnyPermission: ["office.bookingPolicies.read", "office.bookingPolicies.create", "office.bookingPolicies.update"] }
				]
			},
			{
				id: "menu-office-assets",
				name: { en: "Assets", ru: "Активы", hy: "Գույք" },
				icon: "menu/office-resources.png",
				permissionModel: "office",
				officeAudience: "management",
				subMenu: [
					{ id: "menu-office-inventory-all", name: { en: "All Inventory", ru: "Весь инвентарь", hy: "Ամբողջ գույքը" }, icon: "menu/office-resources.png", route: "office/inventory", permissionModel: "office", accessPermissionModel: "office.inventory" },
					{ id: "menu-office-inventory-assets", name: { en: "Fixed Assets", ru: "Основные средства", hy: "Հիմնական միջոցներ" }, icon: "menu/office-resources.png", route: "office/inventory/assets", permissionModel: "office", accessPermissionModel: "office.inventory" },
					{ id: "menu-office-inventory-consumables", name: { en: "Consumables", ru: "Расходные материалы", hy: "Սպառվող նյութեր" }, icon: "menu/office-resources.png", route: "office/inventory/consumables", permissionModel: "office", accessPermissionModel: "office.inventory" },
					{ id: "menu-office-inventory-stock", name: { en: "Stock Locations", ru: "Места хранения", hy: "Պահեստավորման վայրեր" }, icon: "menu/office-offices-and-locations.png", route: "office/inventory/stock", permissionModel: "office", accessPermissionModel: "office.inventory" }
				]
			},
			{
				id: "menu-office-operations",
				name: { en: "Operations", ru: "Операции", hy: "Գործառույթներ" },
				icon: "menu/office-dashboard.png",
				permissionModel: "office",
				officeAudience: "management",
				subMenu: [
					{ id: "menu-office-maintenance", name: { en: "Maintenance", ru: "Обслуживание", hy: "Սպասարկում" }, icon: "menu/office-dashboard.png", route: "office/operations/maintenance", permissionModel: "office", accessPermissionModel: "office.inventoryMaintenance", badgeKey: "underMaintenance" },
					{ id: "menu-office-service-requests", name: { en: "Service Requests", ru: "Заявки на обслуживание", hy: "Սպասարկման հայտեր" }, icon: "menu/office-dashboard.png", route: "office/operations/service-requests", permissionModel: "office", accessPermissionModel: "office.serviceRequests", badgeKey: "openServiceRequests" }
				]
			},
			{
				id: "menu-office-settings",
				name: { en: "Settings", ru: "Настройки", hy: "Կարգավորումներ" },
				icon: "settings.png",
				settingsFor: ["office"],
				permissionModel: "office",
				officeAudience: "management",
				subMenu: [
					{ id: "menu-office-settings-general", name: { en: "Office Settings", ru: "Настройки офиса", hy: "Գրասենյակի կարգավորումներ" }, icon: "settings.png", route: "office/settings", permissionModel: "office", accessPermissionModel: "office.inventorySettings", requiresAnyPermission: ["office.inventorySettings.read", "office.inventorySettings.update", "office.update"] },
					{ id: "menu-office-settings-notifications", name: { en: "Notifications", ru: "Уведомления", hy: "Ծանուցումներ" }, icon: "menu/office-dashboard.png", route: "office/settings/notifications", permissionModel: "office", accessPermissionModel: "office.locations", requiresAnyPermission: ["office.locations.update", "office.update"] },
					{ id: "menu-office-settings-permissions", name: { en: "Permissions", ru: "Разрешения", hy: "Թույլտվություններ" }, icon: "user-shield-alt-1-svgrepo-com.svg", route: "office/settings/permissions", permissionModel: "role", requiresAnyPermission: ["role.read"] }
				]
			},
			{ id: "menu-office-employee-dashboard", name: { en: "Dashboard", ru: "Панель управления", hy: "Վահանակ" }, icon: "menu/office-dashboard.png", route: "office/dashboard", permissionModel: "office", accessPermissionModel: "office.dashboard", officeAudience: "employee" },
			{ id: "menu-office-my-bookings", name: { en: "My Bookings", ru: "Мои бронирования", hy: "Իմ ամրագրումները" }, icon: "menu/office-book-a-room.png", route: "office/bookings/reservations?mine=true", permissionModel: "office", accessPermissionModel: "office.bookings", officeAudience: "employee" },
			{ id: "menu-office-employee-map", name: { en: "Office Map", ru: "Карта офиса", hy: "Գրասենյակի քարտեզ" }, icon: "menu/office-floor-map.png", route: "office/infrastructure/floor-map", permissionModel: "office", accessPermissionModel: "office.map", officeAudience: "employee" },
			{ id: "menu-office-report-problem", name: { en: "Report a Problem", ru: "Сообщить о проблеме", hy: "Հայտնել խնդրի մասին" }, icon: "menu/office-dashboard.png", route: "office/operations/service-requests?new=true", permissionModel: "office", accessPermissionModel: "office.serviceRequests", officeAudience: "employee" }
		]
	}
];

const headerMenu = [
	{
		id: "menu-1-calendar",
		title: {
			en: "Calendar",
			ru: "Календарь",
			hy: "Օրացույց",
			tr: "Takvim",
			ar: "التقويم",
			zh: "日历",
			fa: "تقویم",
			he: "לוח שנה",
			fr: "Calendrier",
			es: "Calendario",
			pt: "Calendário"
		},
		route: "calendar",
		icon: "calendar.png",
		permissionModel: "calendarEvent",
		menu: [
			{
				id: "menu-calendar-0",
				name: {
					en: "Calendar",
					ru: "Календарь",
					hy: "Օրացույց",
					tr: "Takvim",
					ar: "التقويم",
					zh: "日历",
					fa: "تقویم",
					he: "לוח שנה",
					fr: "Calendrier",
					es: "Calendario",
					pt: "Calendário"
				},
				icon: "calendar-alt-svgrepo-com.svg",
				route: "calendar",
				permissionModel: "calendarEvent"
			},
			{
				id: "menu-calendar-1",
				name: {
					en: "Calendars",
					ru: "Календари",
					hy: "Օրացույցներ",
					tr: "Takvimler",
					ar: "التقويمات",
					zh: "日历列表",
					fa: "تقویم‌ها",
					he: "לוחות שנה",
					fr: "Calendriers",
					es: "Calendarios",
					pt: "Calendários"
				},
				icon: "calendar-plus-alt-svgrepo-com.svg",
				route: "calendar/calendars",
				permissionModel: "calendarCalendar"
			},
			{
				id: "menu-calendar-booking-links",
				name: {
					en: "Booking Links", ru: "Ссылки для записи", hy: "Ամրագրման հղումներ",
					tr: "Rezervasyon Bağlantıları", ar: "روابط الحجز", zh: "预约链接",
					fa: "پیوندهای رزرو", he: "קישורי הזמנה", fr: "Liens de réservation",
					es: "Enlaces de reserva", pt: "Links de reserva"
				},
				icon: "calendar-plus-alt-svgrepo-com.svg",
				route: "calendar/booking-links",
				permissionModel: "calendarEvent"
			},
			{
				id: "menu-calendar-settings",
				settingsFor: ["calendar"],
				name: {
					en: "Settings",
					ru: "Настройки",
					hy: "Կարգավորումներ",
					tr: "Ayarlar",
					ar: "الإعدادات",
					zh: "设置",
					fa: "تنظیمات",
					he: "הגדרות",
					fr: "Paramètres",
					es: "Configuración",
					pt: "Configurações"
				},
				icon: "settings.png",
				route: "calendar/settings",
				permissionModel: "calendarEvent"
			},
			{
				id: "menu-calendar-overlay-custom",
				name: {
					en: "Custom Calendars",
					ru: "Пользовательские календари",
					hy: "Անհատական օրացույցներ",
					tr: "Özel Takvimler",
					ar: "تقويمات مخصصة",
					zh: "自定义日历",
					fa: "تقویم‌های سفارشی",
					he: "לוחות שנה מותאמים",
					fr: "Calendriers personnalisés",
					es: "Calendarios personalizados",
					pt: "Calendários personalizados"
				},
				icon: "calendar-alt-svgrepo-com.svg",
				route: "calendar",
				permissionModel: "calendarOverlayCustomCalendars"
			},
			{
				id: "menu-calendar-overlay-people",
				name: {
					en: "People Calendars",
					ru: "Календари сотрудников",
					hy: "Աշխատակիցների օրացույցներ",
					tr: "Kişi Takvimleri",
					ar: "تقويمات الأشخاص",
					zh: "人员日历",
					fa: "تقویم‌های افراد",
					he: "לוחות שנה של אנשים",
					fr: "Calendriers des personnes",
					es: "Calendarios de personas",
					pt: "Calendários de pessoas"
				},
				icon: "calendar-alt-svgrepo-com.svg",
				route: "calendar",
				permissionModel: "calendarOverlayPeople"
			},
			{
				id: "menu-calendar-overlay-it",
				name: {
					en: "IT Calendars",
					ru: "ИТ Календари",
					hy: "ՏՏ Օրացույցներ",
					tr: "IT Takvimleri",
					ar: "تقويمات IT",
					zh: "IT日历",
					fa: "تقویم‌های IT",
					he: "לוחות שנה של IT",
					fr: "Calendriers IT",
					es: "Calendarios IT",
					pt: "Calendários IT"
				},
				icon: "calendar-alt-svgrepo-com.svg",
				route: "calendar",
				permissionModel: "calendarOverlayIt"
			},
			{
				id: "menu-calendar-overlay-tasks",
				name: {
					en: "Tasks",
					ru: "Задачи",
					hy: "Առաջադրանքներ",
					tr: "Görevler",
					ar: "المهام",
					zh: "任务",
					fa: "وظایف",
					he: "משימות",
					fr: "Tâches",
					es: "Tareas",
					pt: "Tarefas"
				},
				icon: "calendar-alt-svgrepo-com.svg",
				route: "calendar",
				permissionModel: "calendarOverlayTasks"
			},
			{
				id: "menu-calendar-overlay-tickets",
				name: {
					en: "Tickets",
					ru: "Тикеты",
					hy: "Տոմսեր",
					tr: "Ticketler",
					ar: "التذاكر",
					zh: "工单",
					fa: "تیکت‌ها",
					he: "קריאות",
					fr: "Tickets",
					es: "Tickets",
					pt: "Tickets"
				},
				icon: "menu/ticket-system.png",
				route: "calendar",
				permissionModel: "calendarOverlayTickets"
			},
			{
				id: "menu-calendar-overlay-reminders",
				name: {
					en: "Reminders",
					ru: "Напоминания",
					hy: "Հիշեցումներ",
					tr: "Hatirlaticilar",
					ar: "التذكيرات",
					zh: "提醒",
					fa: "یادآورها",
					he: "תזכורות",
					fr: "Rappels",
					es: "Recordatorios",
					pt: "Lembretes"
				},
				icon: "calendar-alt-svgrepo-com.svg",
				route: "calendar",
				permissionModel: "calendarOverlayReminders"
			}
		]
	},
	{
		id: "menu-1-office-map",
		title: {
			en: "Office Map",
			ru: "Карта офиса",
			hy: "Օֆիսի քարտեզ",
			tr: "Ofis Haritası",
			ar: "خريطة المكتب",
			zh: "办公地图",
			fa: "نقشه دفتر",
			he: "מפת משרד",
			fr: "Carte de bureau",
			es: "Mapa de la oficina",
			pt: "Mapa do escritório"
		},
		route: "office-map",
		icon: "map-svgrepo-com.svg",
		permissionModel: "officeMap",
		menu: [
			{
				id: "menu-office-map-header-0",
				name: {
					en: "Office Map",
					ru: "Карта офиса",
					hy: "Օֆիսի քարտեզ",
					tr: "Ofis Haritası",
					ar: "خريطة المكتب",
					zh: "办公地图",
					fa: "نقشه دفتر",
					he: "מפת משרד",
					fr: "Carte de bureau",
					es: "Mapa de la oficina",
					pt: "Mapa do escritório"
				},
				icon: "map-svgrepo-com.svg",
				route: "office-map",
				permissionModel: "officeMap"
			},
			{
				id: "menu-office-map-header-1",
				name: {
					en: "Office Map 3D",
					ru: "Карта офиса 3D",
					hy: "Օֆիսի քարտեզ 3D",
					tr: "Ofis Haritası 3D",
					ar: "خريطة المكتب 3D",
					zh: "办公室地图 3D",
					fa: "نقشه دفتر 3D",
					he: "מפת משרד 3D",
					fr: "Carte de bureau 3D",
					es: "Mapa de oficina 3D",
					pt: "Mapa do escritório 3D"
				},
				icon: "map-svgrepo-com.svg",
				route: "office-map/3d",
				permissionModel: "office360"
			},
			{
				id: "menu-office-map-header-2",
				name: {
					en: "Office 360",
					ru: "Office 360",
					hy: "Office 360",
					tr: "Office 360",
					ar: "Office 360",
					zh: "Office 360",
					fa: "Office 360",
					he: "Office 360",
					fr: "Office 360",
					es: "Office 360",
					pt: "Office 360"
				},
				icon: "map-svgrepo-com.svg",
				route: "office-map/360",
				permissionModel: "office360"
			}
		]
	},
	{
		id: "menu-1-world-map",
		title: {
			en: "World Map",
			ru: "Карта мира",
			hy: "Աշխարհի քարտեզ",
			tr: "Dünya Haritası",
			ar: "خريطة العالم",
			zh: "世界地图",
			fa: "نقشه جهان",
			he: "מפת העולם",
			fr: "Carte du monde",
			es: "Mapa mundial",
			pt: "Mapa do mundo"
		},
		route: "world-map",
		icon: "world-map.svg",
		permissionModel: "worldMap",
		menu: [
			{
				id: "menu-world-map-header-0",
				name: {
					en: "World Map",
					ru: "Карта мира",
					hy: "Աշխարհի քարտեզ",
					tr: "Dünya Haritası",
					ar: "خريطة العالم",
					zh: "世界地图",
					fa: "نقشه جهان",
					he: "מפת העולם",
					fr: "Carte du monde",
					es: "Mapa mundial",
					pt: "Mapa do mundo"
				},
				icon: "world-map.svg",
				route: "world-map",
				permissionModel: "worldMap"
			}
		]
	},
	{
		id: "menu-2",
		title: {
			en: "AI Assistant",
			ru: "ИИ Помощник",
			hy: "AI Օգնական",
			tr: "AI Asistan",
			ar: "مساعد الذكاء الاصطناعي",
			zh: "AI助手",
			fa: "دستیار هوش مصنوعی",
			he: "עוזר AI",
			fr: "Assistant IA",
			es: "Asistente IA",
			pt: "Assistente IA"
		},
		route: "ai",
		icon: "ai-platform-svgrepo-com.svg",
		permissionModel: "ai",
		menu: [
			{
				id: "menu-4-0",
				name: {
					en: "Assistant",
					ru: "Ассистент",
					hy: "Օգնական",
					tr: "Asistan",
					ar: "المساعد",
					zh: "助手",
					fa: "دستیار",
					he: "עוזר",
					fr: "Assistant",
					es: "Asistente",
					pt: "Assistente"
				},
				icon: "message-circle-chat-svgrepo-com.svg",
				route: "ai",
				permissionModel: "ai"
			}
		]
	},
	{
		id: "menu-3",
		title: {
			en: "System",
			ru: "Система",
			hy: "Համակարգ",
			tr: "Sistem",
			ar: "النظام",
			zh: "系统",
			fa: "سیستم",
			he: "מערכת",
			fr: "Système",
			es: "Sistema",
			pt: "Sistema"
		},
		route: "system",
		icon: "settings.png",
		menu: [
			{
				id: "menu-13-0",
				name: {
					en: "Users",
					ru: "Пользователи",
					hy: "Օգտատերեր",
					tr: "Kullanıcılar",
					ar: "المستخدمون",
					zh: "用户",
					fa: "کاربران",
					he: "משתמשים",
					fr: "Utilisateurs",
					es: "Usuarios",
					pt: "Usuários"
				},
				icon: "users-svgrepo-com.svg",
				route: "system/user",
				permissionModel: "user"
			},
			{
				id: "menu-13-1",
				name: {
					en: "Roles",
					ru: "Роли",
					hy: "Դերեր",
					tr: "Roller",
					ar: "الأدوار",
					zh: "角色",
					fa: "نقش‌ها",
					he: "תפקידים",
					fr: "Rôles",
					es: "Roles",
					pt: "Funções"
				},
				icon: "user-shield-alt-1-svgrepo-com.svg",
				route: "system/role",
				permissionModel: "role"
			},
			{
				id: "menu-13-platform-admin",
				name: {
					en: "Platform Administration",
					ru: "Управление платформами",
					hy: "Հարթակների կառավարում",
					tr: "Platform Yönetimi",
					ar: "إدارة المنصات",
					zh: "平台管理",
					fa: "مدیریت پلتفرم‌ها",
					he: "ניהול פלטפורמות",
					fr: "Administration des plateformes",
					es: "Administración de plataformas",
					pt: "Administração de plataformas"
				},
				icon: "settings.png",
				route: "system/platforms",
				permissionModel: "platformAdmin"
			},
			{
				id: "menu-13-data-retention",
				name: {
					en: "Data Retention",
					ru: "Хранение данных",
					hy: "Տվյալների պահպանում",
					tr: "Veri Saklama",
					ar: "الاحتفاظ بالبيانات",
					zh: "数据保留",
					fa: "نگهداری داده‌ها",
					he: "שמירת נתונים",
					fr: "Conservation des données",
					es: "Retención de datos",
					pt: "Retenção de dados"
				},
				icon: "settings.png",
				route: "system/data-retention",
				permissionModel: "dataRetention"
			},
			{
				id: "menu-13-2",
				settingsFor: ["hr"],
				name: {
					en: "HR Settings",
					ru: "Настройки HR",
					hy: "HR կարգավորումներ",
					tr: "İK Ayarları",
					ar: "إعدادات الموارد البشرية",
					zh: "人力资源设置",
					fa: "تنظیمات منابع انسانی",
					he: "הגדרות משאבי אנוש",
					fr: "Paramètres RH",
					es: "Configuración de RRHH",
					pt: "Configurações de RH"
				},
				icon: "settings.png",
				route: "system/hr-settings",
				permissionModel: "hrSettings"
			},
			{
				id: "menu-13-3",
				settingsFor: ["finance"],
				name: {
					en: "Finance Settings",
					ru: "Настройки финансов",
					hy: "Ֆինանսների կարգավորումներ",
					tr: "Finans Ayarları",
					ar: "إعدادات المالية",
					zh: "财务设置",
					fa: "تنظیمات مالی",
					he: "הגדרות כספים",
					fr: "Paramètres financiers",
					es: "Configuración financiera",
					pt: "Configurações financeiras"
				},
				icon: "settings.png",
				route: "system/finance-settings",
				permissionModel: "financeSettings"
			},
			{
				id: "menu-13-4",
				settingsFor: ["chat"],
				name: {
					en: "Chat Settings",
					ru: "Настройки чата",
					hy: "Չատի կարգավորումներ",
					tr: "Sohbet Ayarları",
					ar: "إعدادات الدردشة",
					zh: "聊天设置",
					fa: "تنظیمات چت",
					he: "הגדרות צ'אט",
					fr: "Paramètres du chat",
					es: "Configuración del chat",
					pt: "Configurações do chat"
				},
				icon: "settings.png",
				route: "system/chat-settings",
				permissionModel: "chatSettings"
			},
			{
				id: "menu-13-files-settings",
				settingsFor: ["files"],
				name: {
					en: "Files Settings",
					ru: "Настройки файлов",
					hy: "Ֆայլերի կարգավորումներ",
					tr: "Dosya Ayarları",
					ar: "إعدادات الملفات",
					zh: "文件设置",
					fa: "تنظیمات فایل‌ها",
					he: "הגדרות קבצים",
					fr: "Paramètres des fichiers",
					es: "Configuración de archivos",
					pt: "Configurações de arquivos"
				},
				icon: "settings.png",
				route: "system/files-settings",
				permissionModel: "files"
			},
			{
				id: "menu-13-5",
				settingsFor: ["ticket-system"],
				name: {
					en: "Ticket Settings",
					ru: "Настройки тикетов",
					hy: "Տոմսերի կարգավորումներ",
					tr: "Bilet Ayarları",
					ar: "إعدادات التذاكر",
					zh: "工单设置",
					fa: "تنظیمات تیکت",
					he: "הגדרות כרטיסים",
					fr: "Paramètres des tickets",
					es: "Configuración de tickets",
					pt: "Configurações de tickets"
				},
				icon: "settings.png",
				route: "system/ticket-settings",
				permissionModel: "ticket"
			},
			{
				id: "menu-13-6",
				settingsFor: ["ai"],
				name: {
					en: "AI Settings",
					ru: "Настройки ИИ",
					hy: "AI կարգավորումներ",
					tr: "AI Ayarları",
					ar: "إعدادات الذكاء الاصطناعي",
					zh: "AI设置",
					fa: "تنظیمات هوش مصنوعی",
					he: "הגדרות AI",
					fr: "Paramètres IA",
					es: "Configuración de IA",
					pt: "Configurações de IA"
				},
				icon: "user-shield-alt-1-svgrepo-com.svg",
				route: "system/ai-settings",
				permissionModel: "ai"
			},
			{
				id: "menu-13-7",
				settingsFor: ["knowledge"],
				name: {
					en: "Knowledge Settings",
					ru: "Настройки базы знаний",
					hy: "Գիտելիքների բազայի կարգավորումներ",
					tr: "Bilgi Bankası Ayarları",
					ar: "إعدادات قاعدة المعرفة",
					zh: "知识库设置",
					fa: "تنظیمات پایگاه دانش",
					he: "הגדרות מאגר ידע",
					fr: "Paramètres de la base de connaissances",
					es: "Configuración de la base de conocimiento",
					pt: "Configurações da base de conhecimento"
				},
				icon: "settings.png",
				route: "knowledge/settings",
				permissionModel: "knowledge"
			},
			{
				id: "menu-13-8",
				settingsFor: ["security"],
				name: {
					en: "Security Settings",
					ru: "Настройки безопасности",
					hy: "Անվտանգության կարգավորումներ",
					tr: "Güvenlik Ayarları",
					ar: "إعدادات الأمان",
					zh: "安全设置",
					fa: "تنظیمات امنیتی",
					he: "הגדרות אבטחה",
					fr: "Paramètres de sécurité",
					es: "Configuración de seguridad",
					pt: "Configurações de segurança"
				},
				icon: "settings.png",
				route: "security/settings",
				permissionModel: "securitySettings"
			}
		]
	}
];

const notificationSettings = {
	position: "top-right",
	autoClose: 2500,
	hideProgressBar: false,
	closeOnClick: true,
	pauseOnHover: true,
	draggable: true,
	progress: undefined,
	save: function (state, lang) {
		if (state === "success") {
			return {
				title: T("saved", lang),
				message: T("yourFileHasBeenSaved", lang),
				autoClose: this.autoClose,
				color: "green"
			};
		}
		if (state === "error") {
			return {
				title: T("error", lang),
				message: T("yourFileHasBeenNotSaved", lang),
				color: "red"
			};
		}
	},
	delete: function (state, lang) {
		if (state === "success") {
			return {
				title: T("deleted", lang),
				message: T("yourFileHasBeenDeleted", lang),
				autoClose: this.autoClose,
				color: "green"
			};
		}
		if (state === "error") {
			return {
				title: T("error", lang),
				message: T("yourFileHasBeenNotSaved", lang),
				color: "red"
			};
		}
	}
};

const routerSpeeds = [
	{
		label: "100 MB/s",
		value: "100 MB/s"
	},
	{
		label: "300 MB/s",
		value: "300 MB/s"
	},
	{
		label: "500 MB/s",
		value: "500 MB/s"
	},
	{
		label: "1000 MB/s",
		value: "1000 MB/s"
	}
];

const rams = [
	{
		label: "4GB",
		value: "4GB"
	},
	{
		label: "8GB",
		value: "8GB"
	},
	{
		label: "16GB",
		value: "16GB"
	},
	{
		label: "32GB",
		value: "32GB"
	},
	{
		label: "64GB",
		value: "64GB"
	},
	{
		label: "128GB",
		value: "128GB"
	}
];

const roms = [
	{
		label: "32GB",
		value: "32GB"
	},
	{
		label: "64GB",
		value: "64GB"
	},
	{
		label: "SSD 120GB",
		value: "SSD 120GB"
	},
	{
		label: "SSD 240GB",
		value: "SSD 240GB"
	},
	{
		label: "SSD 500GB",
		value: "SSD 500GB"
	},
	{
		label: "SSD 1TB",
		value: "SSD 1TB"
	},
	{
		label: "SSD 2TB",
		value: "SSD 2TB"
	},
	{
		label: "HDD 250GB",
		value: "HDD 250GB"
	},
	{
		label: "HDD 320GB",
		value: "HDD 320GB"
	},
	{
		label: "HDD 500GB",
		value: "HDD 500GB"
	},
	{
		label: "HDD 1TB",
		value: "HDD 1TB"
	},
	{
		label: "HDD 2TB",
		value: "HDD 2TB"
	}
];

function sortAndRemoveDuplicates(arr) {
	// Create a map to store the last item for each index
	const indexMap = new Map();

	// Iterate over the array and update the map with the latest item for each index
	arr.forEach((item) => {
		indexMap.set(item.index, item); // will overwrite if index already exists
	});

	// Convert the map values back to an array and sort by index
	return Array.from(indexMap.values()).sort((a, b) => a.index - b.index);
}

function hasPagePermission(userObject, pageName) {
	if (isUserAnanunaki(userObject)) {
		return true;
	}
	if (!userObject || !pageName) {
		return false;
	}
	return (
		hasPermission(userObject, `${pageName}.read`) || hasPermission(userObject, `${pageName}.view`)
	);
}

const formattedNumber = (number) =>
	new Intl.NumberFormat("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(number) + "$";

const countries = [
	{
		code: "AF",
		en: "Afghanistan",
		ru: "Афганистан",
		hy: "Աֆղանստան",
		tr: "Afganistan",
		ar: "أفغانستان",
		zh: "阿فغانستان",
		fa: "افغانستان",
		he: "אפגניסטן",
		fr: "Afghanistan",
		es: "Afganistán",
		pt: "Afeganistão"
	},
	{
		code: "AL",
		en: "Albania",
		ru: "Албания",
		hy: "Ալբանիա",
		tr: "Arnavutluk",
		ar: "ألبانيا",
		zh: "阿尔巴尼亚",
		fa: "آلبانی",
		he: "אלבניה",
		fr: "Albanie",
		es: "Albania",
		pt: "Albânia"
	},
	{
		code: "DZ",
		en: "Algeria",
		ru: "Алжир",
		hy: "Ալժիր",
		tr: "Cezayir",
		ar: "الجزائر",
		zh: "阿尔及利亚",
		fa: "الجزایر",
		he: "אלג'יריה",
		fr: "Algérie",
		es: "Argelia",
		pt: "Argélia"
	},
	{
		code: "AD",
		en: "Andorra",
		ru: "Андорра",
		hy: "Անդորրա",
		tr: "Andorra",
		ar: "أندورا",
		zh: "安道尔",
		fa: "آندورا",
		he: "אנדורה",
		fr: "Andorre",
		es: "Andorra",
		pt: "Andorra"
	},
	{
		code: "AO",
		en: "Angola",
		ru: "Ангола",
		hy: "Անգոլա",
		tr: "Angola",
		ar: "أنغولا",
		zh: "安哥拉",
		fa: "آنگولا",
		he: "אנגולה",
		fr: "Angola",
		es: "Angola",
		pt: "Angola"
	},
	{
		code: "AG",
		en: "Antigua and Barbuda",
		ru: "Антигуа и Барбуда",
		hy: "Անտիգուա և Բարբուդա",
		tr: "Antigua ve Barbuda",
		ar: "أنتيغوا وبربودا",
		zh: "安提瓜和巴布达",
		fa: "آنتیگوا و باربودا",
		he: "אנטיגואה וברבודה",
		fr: "Antigua-et-Barbuda",
		es: "Antigua y Barbuda",
		pt: "Antígua e Barbuda"
	},
	{
		code: "AR",
		en: "Argentina",
		ru: "Аргентина",
		hy: "Արգենտինա",
		tr: "Arjantin",
		ar: "الأرجنتين",
		zh: "阿根廷",
		fa: "آرژانتین",
		he: "ארגנטינה",
		fr: "Argentine",
		es: "Argentina",
		pt: "Argentina"
	},
	{
		code: "AM",
		en: "Armenia",
		ru: "Армения",
		hy: "Հայաստան",
		tr: "Ermenistan",
		ar: "أرمينيا",
		zh: "亚美尼亚",
		fa: "ارمنستان",
		he: "ארמניה",
		fr: "Arménie",
		es: "Armenia",
		pt: "Armênia"
	},
	{
		code: "AU",
		en: "Australia",
		ru: "Австралия",
		hy: "Ավստրալիա",
		tr: "Avustralya",
		ar: "أستراليا",
		zh: "澳大利亚",
		fa: "استرالیا",
		he: "אוסטרליה",
		fr: "Australie",
		es: "Australia",
		pt: "Ավստրալիա"
	},
	{
		code: "AT",
		en: "Austria",
		ru: "Австрия",
		hy: "Ավստրիա",
		tr: "Avusturya",
		ar: "النمسا",
		zh: "奥地利",
		fa: "اتریش",
		he: "אוסטריה",
		fr: "Autriche",
		es: "Austria",
		pt: "Áustria"
	},
	{
		code: "AZ",
		en: "Azerbaijan",
		ru: "Азербайджан",
		hy: "Ադրբեջան",
		tr: "Azerbaycan",
		ar: "أذربيجان",
		zh: "阿塞拜疆",
		fa: "آذربایجان",
		he: "אזרבייג'ן",
		fr: "Azerbaïdjan",
		es: "Azerbaiyán",
		pt: "Azerbaijão"
	},
	{
		code: "BS",
		en: "Bahamas",
		ru: "Багамы",
		hy: "Բահամներ",
		tr: "Bahamalar",
		ar: "جزر البهاما",
		zh: "巴哈马",
		fa: "باهاما",
		he: "איי בהאמה",
		fr: "Bahamas",
		es: "Bahamas",
		pt: "Bahamas"
	},
	{
		code: "BH",
		en: "Bahrain",
		ru: "Бахрейн",
		hy: "Բահրեյն",
		tr: "Bahreyn",
		ar: "البحرين",
		zh: "巴林",
		fa: "بحرین",
		he: "בחריין",
		fr: "Bahreïn",
		es: "Baréin",
		pt: "Bahrein"
	},
	{
		code: "BD",
		en: "Bangladesh",
		ru: "Бангладеш",
		hy: "Բանգլադեշ",
		tr: "Bangladeş",
		ar: "بنغلاديش",
		zh: "孟加拉国",
		fa: "بنگلادش",
		he: "בנגלדש",
		fr: "Bangladesh",
		es: "Bangladés",
		pt: "Bangladesh"
	},
	{
		code: "BB",
		en: "Barbados",
		ru: "Барбадос",
		hy: "Բարբադոս",
		tr: "Barbados",
		ar: "بربادوس",
		zh: "巴巴多斯",
		fa: "باربادوس",
		he: "ברבדוס",
		fr: "Barbade",
		es: "Barbados",
		pt: "Barbados"
	},
	{
		code: "BY",
		en: "Belarus",
		ru: "Беларусь",
		hy: "Բելառուս",
		tr: "Belarus",
		ar: "بيلاروسيا",
		zh: "白俄罗斯",
		fa: "بلاروس",
		he: "בלארוס",
		fr: "Biélorussie",
		es: "Bielorrusia",
		pt: "Bielorrússia"
	},
	{
		code: "BE",
		en: "Belgium",
		ru: "Бельгия",
		hy: "Բելգիա",
		tr: "Belçika",
		ar: "بلجيكا",
		zh: "比利时",
		fa: "بلژیک",
		he: "בלגיה",
		fr: "Belgique",
		es: "Bélgica",
		pt: "Bélgica"
	},
	{
		code: "BZ",
		en: "Belize",
		ru: "Белиз",
		hy: "Բելիզ",
		tr: "Belize",
		ar: "بليز",
		zh: "伯利兹",
		fa: "بلیز",
		he: "בליז",
		fr: "Belize",
		es: "Belice",
		pt: "Belize"
	},
	{
		code: "BJ",
		en: "Benin",
		ru: "Бенин",
		hy: "Բենին",
		tr: "Benin",
		ar: "بنين",
		zh: "贝宁",
		fa: "بنین",
		he: "בנין",
		fr: "Bénin",
		es: "Benín",
		pt: "Benim"
	},
	{
		code: "BT",
		en: "Bhutan",
		ru: "Бутан",
		hy: "Բութան",
		tr: "Bhutan",
		ar: "بوتان",
		zh: "不丹",
		fa: "بوتان",
		he: "בהוטן",
		fr: "Bhoutan",
		es: "Bután",
		pt: "Butão"
	},
	{
		code: "BO",
		en: "Bolivia",
		ru: "Боливия",
		hy: "Բոլիվիա",
		tr: "Bolivya",
		ar: "بوليفيا",
		zh: "玻利维亚",
		fa: "بولیوی",
		he: "בוליביה",
		fr: "Bolivie",
		es: "Bolivia",
		pt: "Bolívia"
	},
	{
		code: "BA",
		en: "Bosnia and Herzegovina",
		ru: "Босния и Герцеговина",
		hy: "Բոսնիա և Հերցեգովինա",
		tr: "Bosna-Hersek",
		ar: "البوسنة والهرسك",
		zh: "波斯尼亚和黑塞哥维نا",
		fa: "بوسنی و هرزگوین",
		he: "בוסניה והרצגובינה",
		fr: "Bosnie-Herzégovine",
		es: "Bosnia y Herzegovina",
		pt: "Bósnia e Herzegovina"
	},
	{
		code: "BW",
		en: "Botswana",
		ru: "Ботсвана",
		hy: "Բոտսվանա",
		tr: "Botsvana",
		ar: "بوتسوانا",
		zh: "博茨瓦纳",
		fa: "بوتسوانا",
		he: "בוטסואנה",
		fr: "Botswana",
		es: "Botsuana",
		pt: "Botsuana"
	},
	{
		code: "BR",
		en: "Brazil",
		ru: "Бразилия",
		hy: "Բրազիլիա",
		tr: "Brezilya",
		ar: "البرازيل",
		zh: "巴西",
		fa: "برزیل",
		he: "ברזיל",
		fr: "Brésil",
		es: "Brasil",
		pt: "Brasil"
	},
	{
		code: "BN",
		en: "Brunei",
		ru: "Бруней",
		hy: "Բրունեյ",
		tr: "Brunei",
		ar: "بروناي",
		zh: "文莱",
		fa: "برونئی",
		he: "ברוניי",
		fr: "Brunei",
		es: "Brunéi",
		pt: "Brunei"
	},
	{
		code: "BG",
		en: "Bulgaria",
		ru: "Болгария",
		hy: "Բուլղարիա",
		tr: "Bulgaristan",
		ar: "بلغاريا",
		zh: "保加利亚",
		fa: "بلغارستان",
		he: "בולגריה",
		fr: "Bulgarie",
		es: "Bulgaria",
		pt: "Bulgária"
	},
	{
		code: "BF",
		en: "Burkina Faso",
		ru: "Буркина-Фасо",
		hy: "Բուրկինա Ֆասո",
		tr: "Burkina Faso",
		ar: "بوركينا فاسو",
		zh: "布基纳法索",
		fa: "بورکینافاسو",
		he: "בורקינה פאסו",
		fr: "Burkina Faso",
		es: "Burkina Faso",
		pt: "Burkina Faso"
	},
	{
		code: "BI",
		en: "Burundi",
		ru: "Бурунди",
		hy: "Բուրունդի",
		tr: "Burundi",
		ar: "بوروندي",
		zh: "布隆迪",
		fa: "بوروندی",
		he: "בורונדי",
		fr: "Burundi",
		es: "Burundi",
		pt: "Burundi"
	},
	{
		code: "CV",
		en: "Cabo Verde",
		ru: "Кабо-Верде",
		hy: "Կաբո Վերդե",
		tr: "Cabo Verde",
		ar: "الرأس الأخضر",
		zh: "佛得角",
		fa: "کیپ ورد",
		he: "כף ורדה",
		fr: "Cap-Vert",
		es: "Cabo Verde",
		pt: "Cabo Verde"
	},
	{
		code: "KH",
		en: "Cambodia",
		ru: "Камбоджа",
		hy: "Կամբոջա",
		tr: "Kamboçya",
		ar: "كمبوديا",
		zh: "柬埔寨",
		fa: "کامبوج",
		he: "קמבודיה",
		fr: "Cambodge",
		es: "Camboya",
		pt: "Camboja"
	},
	{
		code: "CM",
		en: "Cameroon",
		ru: "Камерун",
		hy: "Կամերուն",
		tr: "Kamerun",
		ar: "الكاميرون",
		zh: "喀麦隆",
		fa: "کامرون",
		he: "קמרון",
		fr: "Cameroun",
		es: "Camerún",
		pt: "Camarões"
	},
	{
		code: "CA",
		en: "Canada",
		ru: "Канада",
		hy: "Կանադա",
		tr: "Kanada",
		ar: "كندا",
		zh: "加拿大",
		fa: "کانادا",
		he: "קנדה",
		fr: "Canada",
		es: "Canadá",
		pt: "Canadá"
	},
	{
		code: "CF",
		en: "Central African Republic",
		ru: "Центральноафриканская Республика",
		hy: "Կենտրոնաաֆրիկյան Հանրապետություն",
		tr: "Orta Afrika Cumhuriyeti",
		ar: "جمهورية أفريقيا الوسطى",
		zh: "中非共和国",
		fa: "جمهوری آفریقای مرکزی",
		he: "הרפובליקה המרכז-אפריקאית",
		fr: "République centrafricaine",
		es: "República Centroafricana",
		pt: "República Centro-Africana"
	},
	{
		code: "TD",
		en: "Chad",
		ru: "Чад",
		hy: "Չադ",
		tr: "Çad",
		ar: "تشاد",
		zh: "乍得",
		fa: "چاد",
		he: "צ'אד",
		fr: "Tchad",
		es: "Chad",
		pt: "Chade"
	},
	{
		code: "CL",
		en: "Chile",
		ru: "Чили",
		hy: "Չիլի",
		tr: "Şili",
		ar: "تشيلي",
		zh: "智利",
		fa: "شیلی",
		he: "צ'ילה",
		fr: "Chili",
		es: "Chile",
		pt: "Chile"
	},
	{
		code: "CN",
		en: "China",
		ru: "Китай",
		hy: "Չինաստան",
		tr: "Çin",
		ar: "الصين",
		zh: "中国",
		fa: "چین",
		he: "סין",
		fr: "Chine",
		es: "China",
		pt: "China"
	},
	{
		code: "CO",
		en: "Colombia",
		ru: "Колумбия",
		hy: "Կոլումբիա",
		tr: "Kolombiya",
		ar: "كولومبيا",
		zh: "哥伦比亚",
		fa: "کلمبیا",
		he: "קולומביה",
		fr: "Colombie",
		es: "Colombia",
		pt: "Colômbia"
	},
	{
		code: "KM",
		en: "Comoros",
		ru: "Коморы",
		hy: "Կոմորյան կղզիներ",
		tr: "Komorlar",
		ar: "جزر القمر",
		zh: "科摩罗",
		fa: "کومور",
		he: "קומורו",
		fr: "Comores",
		es: "Comoras",
		pt: "Comores"
	},
	{
		code: "CG",
		en: "Congo",
		ru: "Конго",
		hy: "Կոնգո",
		tr: "Kongo",
		ar: "الكونغو",
		zh: "刚果",
		fa: "کنگو",
		he: "קונגו",
		fr: "Congo",
		es: "Congo",
		pt: "Congo"
	},
	{
		code: "CD",
		en: "Congo (Democratic Republic)",
		ru: "Демократическая Республика Конго",
		hy: "Կոնգոյի Դեմոկրատական Հանրապետություն",
		tr: "Kongo Demokratik Cumhuriyeti",
		ar: "جمهورية الكونغو الديمقراطية",
		zh: "刚果民主共和国",
		fa: "جمهوری دموکراتیک کنگو",
		he: "הרפובליקה הדמוקרטית של קונגו",
		fr: "République démocratique du Congo",
		es: "República Democrática del Congo",
		pt: "República Democrática do Congo"
	},
	{
		code: "CR",
		en: "Costa Rica",
		ru: "Коста-Рика",
		hy: "Կոստա Ռիկա",
		tr: "Kosta Rika",
		ar: "كوستاريكا",
		zh: "哥斯达黎加",
		fa: "کاستاریکا",
		he: "קוסטה ריקה",
		fr: "Costa Rica",
		es: "Costa Rica",
		pt: "Costa Rica"
	},
	{
		code: "CI",
		en: "Côte d'Ivoire",
		ru: "Кот-д'Ивуар",
		hy: "Կոտ դ'Իվուար",
		tr: "Fildişi Sahili",
		ar: "ساحل العاج",
		zh: "科特迪瓦",
		fa: "ساحل عاج",
		he: "חוף השנהב",
		fr: "Côte d'Ivoire",
		es: "Costa de Marfil",
		pt: "Costa do Marfim"
	},
	{
		code: "HR",
		en: "Croatia",
		ru: "Хорватия",
		hy: "Խորվաթիա",
		tr: "Hırvatistan",
		ar: "كرواتيا",
		zh: "克罗地亚",
		fa: "کرواسی",
		he: "קרואטיה",
		fr: "Croatie",
		es: "Croacia",
		pt: "Croácia"
	},
	{
		code: "CU",
		en: "Cuba",
		ru: "Куба",
		hy: "Կուբա",
		tr: "Küba",
		ar: "كوبا",
		zh: "古巴",
		fa: "کوبا",
		he: "קובה",
		fr: "Cuba",
		es: "Cuba",
		pt: "Cuba"
	},
	{
		code: "CY",
		en: "Cyprus",
		ru: "Кипр",
		hy: "Կիպրոս",
		tr: "Kıbrıs",
		ar: "قبرص",
		zh: "塞浦路斯",
		fa: "قبرس",
		he: "קפריסין",
		fr: "Chypre",
		es: "Chipre",
		pt: "Chipre"
	},
	{
		code: "CZ",
		en: "Czechia",
		ru: "Чехия",
		hy: "Չեխիա",
		tr: "Çekya",
		ar: "التشيك",
		zh: "捷克",
		fa: "چک",
		he: "צ'כיה",
		fr: "Tchéquie",
		es: "Chequia",
		pt: "Tchéquia"
	},
	{
		code: "DK",
		en: "Denmark",
		ru: "Дания",
		hy: "Դանիա",
		tr: "Danimarka",
		ar: "الدنمارك",
		zh: "丹麦",
		fa: "دانمارک",
		he: "דנמרק",
		fr: "Danemark",
		es: "Dinamarca",
		pt: "Dinamarca"
	},
	{
		code: "DJ",
		en: "Djibouti",
		ru: "Джибути",
		hy: "Ջիբութի",
		tr: "Cibuti",
		ar: "جيبوتي",
		zh: "吉布提",
		fa: "جیبوتی",
		he: "ג'יבוטי",
		fr: "Djibouti",
		es: "Yibuti",
		pt: "Djibuti"
	},
	{
		code: "DM",
		en: "Dominica",
		ru: "Доминика",
		hy: "Դոմինիկա",
		tr: "Dominika",
		ar: "دومينيكا",
		zh: "多米尼克",
		fa: "دومینیکا",
		he: "דומיניקה",
		fr: "Dominique",
		es: "Dominica",
		pt: "Dominica"
	},
	{
		code: "DO",
		en: "Dominican Republic",
		ru: "Доминиканская Республика",
		hy: "Դոմինիկյան Հանրապետություն",
		tr: "Dominik Cumhuriyeti",
		ar: "جمهورية الدومينيكان",
		zh: "多米尼加共和国",
		fa: "جمهوری دومینیکن",
		he: "הרפובליקה הדומיניקנית",
		fr: "République dominicaine",
		es: "República Dominicana",
		pt: "República Dominicana"
	},
	{
		code: "EC",
		en: "Ecuador",
		ru: "Эквадор",
		hy: "Էկվադոր",
		tr: "Ekvador",
		ar: "الإكوادور",
		zh: "厄瓜多尔",
		fa: "اکوادور",
		he: "אקוודור",
		fr: "Équateur",
		es: "Ecuador",
		pt: "Equador"
	},
	{
		code: "EG",
		en: "Egypt",
		ru: "Египет",
		hy: "Եգիպտոս",
		tr: "Mısır",
		ar: "مصر",
		zh: "埃及",
		fa: "مصر",
		he: "מצרים",
		fr: "Égypte",
		es: "Egipto",
		pt: "Egito"
	},
	{
		code: "SV",
		en: "El Salvador",
		ru: "Сальвадор",
		hy: "Սալվադոր",
		tr: "El Salvador",
		ar: "السلفادور",
		zh: "萨尔瓦多",
		fa: "السالوادور",
		he: "אל סלבדור",
		fr: "Salvador",
		es: "El Salvador",
		pt: "El Salvador"
	},
	{
		code: "GQ",
		en: "Equatorial Guinea",
		ru: "Экваториальная Гвинея",
		hy: "Հասարակածային Գվինեա",
		tr: "Ekvator Ginesi",
		ar: "غينيا الاستوائية",
		zh: "赤道几内亚",
		fa: "گینه استوایی",
		he: "גינאה המשוונית",
		fr: "Guinée équatoriale",
		es: "Guinea Ecuatorial",
		pt: "Guiné Equatorial"
	},
	{
		code: "ER",
		en: "Eritrea",
		ru: "Эритрея",
		hy: "Էրիթրեա",
		tr: "Eritre",
		ar: "إريتريا",
		zh: "厄立特里亚",
		fa: "اریتره",
		he: "אריתריאה",
		fr: "Érythrée",
		es: "Eritrea",
		pt: "Eritreia"
	},
	{
		code: "EE",
		en: "Estonia",
		ru: "Эстония",
		hy: "Էստոնիա",
		tr: "Estonya",
		ar: "إستونيا",
		zh: "爱沙尼亚",
		fa: "استونی",
		he: "אסטוניה",
		fr: "Estonie",
		es: "Estonia",
		pt: "Estônia"
	},
	{
		code: "SZ",
		en: "Eswatini",
		ru: "Эсватини",
		hy: "Էսվատինի",
		tr: "Esvatini",
		ar: "إسواتيني",
		zh: "斯威士兰",
		fa: "اسواتینی",
		he: "אסוואטיני",
		fr: "Eswatini",
		es: "Suazilandia",
		pt: "Essuatíni"
	},
	{
		code: "ET",
		en: "Ethiopia",
		ru: "Эфиопия",
		hy: "Եթովպիա",
		tr: "Etiyopya",
		ar: "إثيوبيا",
		zh: "埃塞俄比亚",
		fa: "اتیوپی",
		he: "אתיופיה",
		fr: "Éthiopie",
		es: "Etiopía",
		pt: "Etiópia"
	},
	{
		code: "FJ",
		en: "Fiji",
		ru: "Фиджи",
		hy: "Ֆիջի",
		tr: "Fiji",
		ar: "فيجي",
		zh: "斐济",
		fa: "فیجی",
		he: "פיג'י",
		fr: "Fidji",
		es: "Fiyi",
		pt: "Fiji"
	},
	{
		code: "FI",
		en: "Finland",
		ru: "Финляндия",
		hy: "Ֆինլանդիա",
		tr: "Finlandiya",
		ar: "فنلندا",
		zh: "芬兰",
		fa: "فنلاند",
		he: "פינלנד",
		fr: "Finlande",
		es: "Finlandia",
		pt: "Finlândia"
	},
	{
		code: "FR",
		en: "France",
		ru: "Франция",
		hy: "Ֆրանսիա",
		tr: "Fransa",
		ar: "فرنسا",
		zh: "法国",
		fa: "فرانسه",
		he: "צרפת",
		fr: "France",
		es: "Francia",
		pt: "França"
	},
	{
		code: "GA",
		en: "Gabon",
		ru: "Габон",
		hy: "Գաբոն",
		tr: "Gabon",
		ar: "الغابون",
		zh: "加蓬",
		fa: "گابن",
		he: "גבון",
		fr: "Gabon",
		es: "Gabón",
		pt: "Gabão"
	},
	{
		code: "GM",
		en: "Gambia",
		ru: "Гамбия",
		hy: "Գամբիա",
		tr: "Gambiya",
		ar: "غامبيا",
		zh: "冈比亚",
		fa: "گامبیا",
		he: "גמביה",
		fr: "Gambie",
		es: "Gambia",
		pt: "Gâmbia"
	},
	{
		code: "GE",
		en: "Georgia",
		ru: "Грузия",
		hy: "Վրաստան",
		tr: "Gürcistan",
		ar: "جورجيا",
		zh: "格鲁吉亚",
		fa: "گرجستان",
		he: "גאורגיה",
		fr: "Géorgie",
		es: "Georgia",
		pt: "Geórgia"
	},
	{
		code: "DE",
		en: "Germany",
		ru: "Германия",
		hy: "Գերմանիա",
		tr: "Almanya",
		ar: "ألمانيا",
		zh: "德国",
		fa: "آلمان",
		he: "גרמניה",
		fr: "Allemagne",
		es: "Alemania",
		pt: "Alemanha"
	},
	{
		code: "GH",
		en: "Ghana",
		ru: "Гана",
		hy: "Գանա",
		tr: "Gana",
		ar: "غانا",
		zh: "加纳",
		fa: "غنا",
		he: "גանա",
		fr: "Ghana",
		es: "Ghana",
		pt: "Gana"
	},
	{
		code: "GR",
		en: "Greece",
		ru: "Греция",
		hy: "Հունաստան",
		tr: "Yunanistan",
		ar: "اليونان",
		zh: "希腊",
		fa: "یونان",
		he: "יוון",
		fr: "Grèce",
		es: "Grecia",
		pt: "Գրեկիա"
	},
	{
		code: "GD",
		en: "Grenada",
		ru: "Гренада",
		hy: "Գրենադա",
		tr: "Grenada",
		ar: "غرينادا",
		zh: "格林纳达",
		fa: "گرنادا",
		he: "גרנדה",
		fr: "Grenade",
		es: "Granada",
		pt: "Granada"
	},
	{
		code: "GT",
		en: "Guatemala",
		ru: "Гватемала",
		hy: "Գվատեմալա",
		tr: "Guatemala",
		ar: "غواتيمالا",
		zh: "危地马拉",
		fa: "گواتمالا",
		he: "גואטמלה",
		fr: "Guatemala",
		es: "Guatemala",
		pt: "Guatemala"
	},
	{
		code: "GN",
		en: "Guinea",
		ru: "Гвинея",
		hy: "Գվինեա",
		tr: "Gine",
		ar: "غينيا",
		zh: "几内亚",
		fa: "گینه",
		he: "גינאה",
		fr: "Guinée",
		es: "Guinea",
		pt: "Guiné"
	},
	{
		code: "GW",
		en: "Guinea-Bissau",
		ru: "Гвинея-Бисау",
		hy: "Գվինեա-Բիսաու",
		tr: "Gine-Bissau",
		ar: "غينيا بيساو",
		zh: "几内亚比绍",
		fa: "گینه بیسائو",
		he: "גינאה-ביסאו",
		fr: "Guinée-Bissau",
		es: "Guinea-Bisáu",
		pt: "Guiné-Bissau"
	},
	{
		code: "GY",
		en: "Guyana",
		ru: "Гайана",
		hy: "Գայանա",
		tr: "Guyana",
		ar: "غيانا",
		zh: "圭亚那",
		fa: "گویان",
		he: "גיאנה",
		fr: "Guyana",
		es: "Guyana",
		pt: "Guiana"
	},
	{
		code: "HT",
		en: "Haiti",
		ru: "Гаити",
		hy: "Հայիթի",
		tr: "Haiti",
		ar: "هايتي",
		zh: "海地",
		fa: "هائیتی",
		he: "האיטי",
		fr: "Haïti",
		es: "Haití",
		pt: "Haiti"
	},
	{
		code: "HN",
		en: "Honduras",
		ru: "Гондурас",
		hy: "Հոնդուրաս",
		tr: "Honduras",
		ar: "هندوراس",
		zh: "洪都拉斯",
		fa: "هندوراس",
		he: "הונדורס",
		fr: "Honduras",
		es: "Honduras",
		pt: "Honduras"
	},
	{
		code: "HU",
		en: "Hungary",
		ru: "Венгрия",
		hy: "Հունգարիա",
		tr: "Macaristan",
		ar: "المجر",
		zh: "匈牙利",
		fa: "مجارستان",
		he: "הונגריה",
		fr: "Hongrie",
		es: "Hungría",
		pt: "Hungria"
	},
	{
		code: "IS",
		en: "Iceland",
		ru: "Исландия",
		hy: "Իսլանդիա",
		tr: "İzlanda",
		ar: "آيسلندا",
		zh: "冰岛",
		fa: "ایسلند",
		he: "איסלנד",
		fr: "Islande",
		es: "Islandia",
		pt: "Islândia"
	},
	{
		code: "IN",
		en: "India",
		ru: "Индия",
		hy: "Հնդկաստան",
		tr: "Hindistan",
		ar: "الهند",
		zh: "印度",
		fa: "هند",
		he: "הודו",
		fr: "Inde",
		es: "India",
		pt: "Índia"
	},
	{
		code: "ID",
		en: "Indonesia",
		ru: "Индонезия",
		hy: "Ինդոնեզիա",
		tr: "Endonezya",
		ar: "إندونيسيا",
		zh: "印度尼西亚",
		fa: "اندونزی",
		he: "אינדונזיה",
		fr: "Indonésie",
		es: "Indonesia",
		pt: "Indonésia"
	},
	{
		code: "IR",
		en: "Iran",
		ru: "Иран",
		hy: "Իրան",
		tr: "İran",
		ar: "إيران",
		zh: "伊朗",
		fa: "ایران",
		he: "איראן",
		fr: "Iran",
		es: "Irán",
		pt: "Irã"
	},
	{
		code: "IQ",
		en: "Iraq",
		ru: "Ирак",
		hy: "Իրաք",
		tr: "Irak",
		ar: "العراق",
		zh: "伊拉克",
		fa: "عراق",
		he: "עיראק",
		fr: "Irak",
		es: "Irak",
		pt: "Iraque"
	},
	{
		code: "IE",
		en: "Ireland",
		ru: "Ирландия",
		hy: "Իռլանդիա",
		tr: "İrlanda",
		ar: "أيرلندا",
		zh: "爱尔兰",
		fa: "ایرلند",
		he: "אירלנד",
		fr: "Irlande",
		es: "Irlanda",
		pt: "Irlanda"
	},
	{
		code: "IL",
		en: "Israel",
		ru: "Израиль",
		hy: "Իսրայել",
		tr: "İsrail",
		ar: "إسرائيل",
		zh: "以色列",
		fa: "اسرائیل",
		he: "ישראל",
		fr: "Israël",
		es: "Israel",
		pt: "Israel"
	},
	{
		code: "IT",
		en: "Italy",
		ru: "Италия",
		hy: "Իտալիա",
		tr: "İtalya",
		ar: "إيطاليا",
		zh: "意大利",
		fa: "ایتالیا",
		he: "איטליה",
		fr: "Italie",
		es: "Italia",
		pt: "Itália"
	},
	{
		code: "JM",
		en: "Jamaica",
		ru: "Ямайка",
		hy: "Ճամայկա",
		tr: "Jamaika",
		ar: "جامايكا",
		zh: "牙买加",
		fa: "جامائیکا",
		he: "ג'מייקה",
		fr: "Jamaïque",
		es: "Jamaica",
		pt: "Jamaica"
	},
	{
		code: "JP",
		en: "Japan",
		ru: "Япония",
		hy: "Ճապոնիա",
		tr: "Japonya",
		ar: "اليابان",
		zh: "日本",
		fa: "ژاپن",
		he: "יפן",
		fr: "Japon",
		es: "Japón",
		pt: "Japão"
	},
	{
		code: "JO",
		en: "Jordan",
		ru: "Иордания",
		hy: "Հորդանան",
		tr: "Ürdün",
		ar: "الأردن",
		zh: "约旦",
		fa: "اردن",
		he: "ירדן",
		fr: "Jordanie",
		es: "Jordania",
		pt: "Jordânia"
	},
	{
		code: "KZ",
		en: "Kazakhstan",
		ru: "Казахстан",
		hy: "Ղազախստան",
		tr: "Kazakistan",
		ar: "كازախستان",
		zh: "哈萨克斯坦",
		fa: "قزاقستان",
		he: "קזחסטן",
		fr: "Kazakhstan",
		es: "Kazajistán",
		pt: "Cazaquistão"
	},
	{
		code: "KE",
		en: "Kenya",
		ru: "Кения",
		hy: "Քենիա",
		tr: "Kenya",
		ar: "كينيا",
		zh: "肯尼亚",
		fa: "کنیا",
		he: "קניה",
		fr: "Kenya",
		es: "Kenia",
		pt: "Quênia"
	},
	{
		code: "KI",
		en: "Kiribati",
		ru: "Кирибати",
		hy: "Կիրիբատի",
		tr: "Kiribati",
		ar: "كيريباتي",
		zh: "基里巴斯",
		fa: "کیریباتی",
		he: "קיריבטי",
		fr: "Kiribati",
		es: "Kiribati",
		pt: "Kiribati"
	},
	{
		code: "KP",
		en: "North Korea",
		ru: "Северная Корея",
		hy: "Հյուսիսային Կորեա",
		tr: "Kuzey Kore",
		ar: "كوريا الشمالية",
		zh: "朝鲜",
		fa: "کره شمالی",
		he: "קוריאה הצפונית",
		fr: "Corée du Nord",
		es: "Corea del Norte",
		pt: "Coreia do Norte"
	},
	{
		code: "KR",
		en: "South Korea",
		ru: "Южная Корея",
		hy: "Հարավային Կորեա",
		tr: "Güney Kore",
		ar: "كوريا الجنوبية",
		zh: "韩国",
		fa: "کره جنوبی",
		he: "קוריאה הדרומית",
		fr: "Corée du Sud",
		es: "Corea del Sur",
		pt: "Coreia do Sul"
	},
	{
		code: "KW",
		en: "Kuwait",
		ru: "Кувейт",
		hy: "Քուվեյթ",
		tr: "Kuveyt",
		ar: "الكويت",
		zh: "科威特",
		fa: "کویت",
		he: "כווית",
		fr: "Koweït",
		es: "Kuwait",
		pt: "Kuwait"
	},
	{
		code: "KG",
		en: "Kyrgyzstan",
		ru: "Киргизия",
		hy: "Ղրղզստան",
		tr: "Kırgızistan",
		ar: "قيرغيزستان",
		zh: "吉尔吉斯斯坦",
		fa: "قرقیزستان",
		he: "קירגיזסטן",
		fr: "Kirghizistan",
		es: "Kirguistán",
		pt: "Quirguistão"
	},
	{
		code: "LA",
		en: "Laos",
		ru: "Лаос",
		hy: "Լաոս",
		tr: "Laos",
		ar: "لاوس",
		zh: "老挝",
		fa: "لائوس",
		he: "לאוס",
		fr: "Laos",
		es: "Laos",
		pt: "Laos"
	},
	{
		code: "LV",
		en: "Latvia",
		ru: "Латвия",
		hy: "Լատվիա",
		tr: "Letonya",
		ar: "لاتفيا",
		zh: "拉脱维亚",
		fa: "لتونی",
		he: "לטביה",
		fr: "Lettonie",
		es: "Letonia",
		pt: "Letônia"
	},
	{
		code: "LB",
		en: "Lebanon",
		ru: "Ливан",
		hy: "Լիբանան",
		tr: "Lübnan",
		ar: "لبنان",
		zh: "黎巴嫩",
		fa: "لبنان",
		he: "לבנון",
		fr: "Liban",
		es: "Líbano",
		pt: "Líbano"
	},
	{
		code: "LS",
		en: "Lesotho",
		ru: "Лесото",
		hy: "Լեսոթո",
		tr: "Lesotho",
		ar: "ليسوتو",
		zh: "莱索托",
		fa: "لسوتو",
		he: "לסוטו",
		fr: "Lesotho",
		es: "Lesoto",
		pt: "Lesoto"
	},
	{
		code: "LR",
		en: "Liberia",
		ru: "Либерия",
		hy: "Լիբերիա",
		tr: "Liberya",
		ar: "ليبيريا",
		zh: "利比里亚",
		fa: "لیبریا",
		he: "ליבריה",
		fr: "Liberia",
		es: "Liberia",
		pt: "Libéria"
	},
	{
		code: "LY",
		en: "Libya",
		ru: "Ливия",
		hy: "Լիբիա",
		tr: "Libya",
		ar: "ليبيا",
		zh: "利比亚",
		fa: "لیبی",
		he: "לוב",
		fr: "Libye",
		es: "Libia",
		pt: "Líbia"
	},
	{
		code: "LI",
		en: "Liechtenstein",
		ru: "Лихтенштейн",
		hy: "Լիխտենշտեյն",
		tr: "Lihtenştayn",
		ar: "ليختنشتاين",
		zh: "列支敦士登",
		fa: "لیختن‌اشتاین",
		he: "ליכטנשטיין",
		fr: "Liechtenstein",
		es: "Liechtenstein",
		pt: "Liechtenstein"
	},
	{
		code: "LT",
		en: "Lithuania",
		ru: "Литва",
		hy: "Լիտվա",
		tr: "Litvanya",
		ar: "ليتوانيا",
		zh: "立陶宛",
		fa: "لیتوانی",
		he: "ליטא",
		fr: "Lituanie",
		es: "Lituania",
		pt: "Lituânia"
	},
	{
		code: "LU",
		en: "Luxembourg",
		ru: "Люксембург",
		hy: "Լյուքսեմբուրգ",
		tr: "Lüksemburg",
		ar: "لوكسمبورغ",
		zh: "卢森堡",
		fa: "لوکزامبورگ",
		he: "לוקסמבורג",
		fr: "Luxembourg",
		es: "Luxemburgo",
		pt: "Luxemburgo"
	},
	{
		code: "MG",
		en: "Madagascar",
		ru: "Мадагаскар",
		hy: "Մադագասկար",
		tr: "Madagaskar",
		ar: "مدغشقر",
		zh: "马达加斯加",
		fa: "ماداگاسکار",
		he: "מדגסקר",
		fr: "Madagascar",
		es: "Madagascar",
		pt: "Madagáscar"
	},
	{
		code: "MW",
		en: "Malawi",
		ru: "Малави",
		hy: "Մալավի",
		tr: "Malavi",
		ar: "مالاوي",
		zh: "马拉维",
		fa: "مالاوی",
		he: "מלאווי",
		fr: "Malawi",
		es: "Malaui",
		pt: "Malawi"
	},
	{
		code: "MY",
		en: "Malaysia",
		ru: "Малайзия",
		hy: "Մալայզիա",
		tr: "Malezya",
		ar: "ماليزيا",
		zh: "马来西亚",
		fa: "مالزی",
		he: "מלזיה",
		fr: "Malaisie",
		es: "Malasia",
		pt: "Malásia"
	},
	{
		code: "MV",
		en: "Maldives",
		ru: "Мальдивы",
		hy: "Մալդիվներ",
		tr: "Maldivler",
		ar: "جزر المالديف",
		zh: "马尔代夫",
		fa: "مالدیو",
		he: "האיים המלדיביים",
		fr: "Maldives",
		es: "Maldivas",
		pt: "Maldivas"
	},
	{
		code: "ML",
		en: "Mali",
		ru: "Мали",
		hy: "Մալի",
		tr: "Mali",
		ar: "مالي",
		zh: "马里",
		fa: "مالی",
		he: "מאלי",
		fr: "Mali",
		es: "Malí",
		pt: "Mali"
	},
	{
		code: "MT",
		en: "Malta",
		ru: "Мальта",
		hy: "Մալթա",
		tr: "Malta",
		ar: "مالطا",
		zh: "马耳他",
		fa: "مالت",
		he: "מלטה",
		fr: "Malte",
		es: "Malta",
		pt: "Malta"
	},
	{
		code: "MH",
		en: "Marshall Islands",
		ru: "Маршалловы Острова",
		hy: "Մարշալյան կղզիներ",
		tr: "Marshall Adaları",
		ar: "جزر مارشال",
		zh: "马绍尔群岛",
		fa: "جزایر مارشال",
		he: "איי מרשל",
		fr: "Îles Marshall",
		es: "Islas Marshall",
		pt: "Ilhas Marshall"
	},
	{
		code: "MR",
		en: "Mauritania",
		ru: "Мавритания",
		hy: "Մավրիտանիա",
		tr: "Moritanya",
		ar: "موريتانيا",
		zh: "毛里塔尼亚",
		fa: "موریتانی",
		he: "מאוריטניה",
		fr: "Mauritanie",
		es: "Mauritania",
		pt: "Mauritânia"
	},
	{
		code: "MU",
		en: "Mauritius",
		ru: "Маврикий",
		hy: "Մավրիկիոս",
		tr: "Mauritius",
		ar: "موريشيوس",
		zh: "毛里求斯",
		fa: "موریس",
		he: "מאוריציוס",
		fr: "Maurice",
		es: "Mauricio",
		pt: "Maurícia"
	},
	{
		code: "MX",
		en: "Mexico",
		ru: "Мексика",
		hy: "Մեքսիկա",
		tr: "Meksika",
		ar: "المكسيك",
		zh: "墨西哥",
		fa: "مکزیک",
		he: "מקסיקו",
		fr: "Mexique",
		es: "México",
		pt: "México"
	},
	{
		code: "FM",
		en: "Micronesia",
		ru: "Микронезия",
		hy: "Միկրոնեզիա",
		tr: "Mikronezya",
		ar: "ميكرونيزيا",
		zh: "密克罗尼西亚",
		fa: "میکرونزی",
		he: "מיקרונזיה",
		fr: "Micronésie",
		es: "Micronesia",
		pt: "Micronésia"
	},
	{
		code: "MD",
		en: "Moldova",
		ru: "Молдова",
		hy: "Մոլդովա",
		tr: "Moldova",
		ar: "مولدوفا",
		zh: "摩尔多瓦",
		fa: "مولداوی",
		he: "מולדובה",
		fr: "Moldavie",
		es: "Moldavia",
		pt: "Moldávia"
	},
	{
		code: "MC",
		en: "Monaco",
		ru: "Монако",
		hy: "Մոնակո",
		tr: "Monako",
		ar: "موناكو",
		zh: "摩纳哥",
		fa: "موناکو",
		he: "מונקו",
		fr: "Monaco",
		es: "Mónaco",
		pt: "Mônaco"
	},
	{
		code: "MN",
		en: "Mongolia",
		ru: "Монголия",
		hy: "Մոնղոլիա",
		tr: "Moğolistan",
		ar: "منغوليا",
		zh: "蒙古",
		fa: "مغولستان",
		he: "מונגוליה",
		fr: "Mongolie",
		es: "Mongolia",
		pt: "Mongólia"
	},
	{
		code: "ME",
		en: "Montenegro",
		ru: "Черногория",
		hy: "Չեռնոգորիա",
		tr: "Karadağ",
		ar: "الجبل الأسود",
		zh: "黑山",
		fa: "مونته‌نگرو",
		he: "מונטנגרו",
		fr: "Monténégro",
		es: "Montenegro",
		pt: "Montenegro"
	},
	{
		code: "MA",
		en: "Morocco",
		ru: "Марокко",
		hy: "Մարոկկո",
		tr: "Fas",
		ar: "المغرب",
		zh: "摩洛哥",
		fa: "مراکش",
		he: "מרוקו",
		fr: "Maroc",
		es: "Marruecos",
		pt: "Marrocos"
	},
	{
		code: "MZ",
		en: "Mozambique",
		ru: "Мозамбик",
		hy: "Մոզամբիկ",
		tr: "Mozambik",
		ar: "موزمبيق",
		zh: "莫桑比克",
		fa: "موزامبیک",
		he: "מוזמביק",
		fr: "Mozambique",
		es: "Mozambique",
		pt: "Moçambique"
	},
	{
		code: "MM",
		en: "Myanmar",
		ru: "Мьянма",
		hy: "Մյանմա",
		tr: "Myanmar",
		ar: "ميانمار",
		zh: "缅甸",
		fa: "میانمار",
		he: "מיאנמר",
		fr: "Myanmar",
		es: "Myanmar",
		pt: "Mianmar"
	},
	{
		code: "NA",
		en: "Namibia",
		ru: "Намибия",
		hy: "Նամիբիա",
		tr: "Namibya",
		ar: "ناميبيا",
		zh: "纳米比亚",
		fa: "نامیبیا",
		he: "נמיביה",
		fr: "Namibie",
		es: "Namibia",
		pt: "Namíbia"
	},
	{
		code: "NR",
		en: "Nauru",
		ru: "Науру",
		hy: "Նաուրու",
		tr: "Nauru",
		ar: "ناورو",
		zh: "瑙鲁",
		fa: "نائورو",
		he: "נאורו",
		fr: "Nauru",
		es: "Nauru",
		pt: "Nauru"
	},
	{
		code: "NP",
		en: "Nepal",
		ru: "Непал",
		hy: "Նեպալ",
		tr: "Nepal",
		ar: "نيبال",
		zh: "尼泊尔",
		fa: "نپال",
		he: "נפאל",
		fr: "Népal",
		es: "Nepal",
		pt: "Nepal"
	},
	{
		code: "NL",
		en: "Netherlands",
		ru: "Нидерланды",
		hy: "Նիդերլանդներ",
		tr: "Hollanda",
		ar: "هولندا",
		zh: "荷兰",
		fa: "هلند",
		he: "הולנד",
		fr: "Pays-Bas",
		es: "Países Bajos",
		pt: "Países Baixos"
	},
	{
		code: "NZ",
		en: "New Zealand",
		ru: "Новая Зеландия",
		hy: "Նոր Զելանդիա",
		tr: "Yeni Zelanda",
		ar: "نيوزيلندا",
		zh: "新西兰",
		fa: "نیوزیلند",
		he: "ניו זילנד",
		fr: "Nouvelle-Zélande",
		es: "Nueva Zelanda",
		pt: "Nova Zelândia"
	},
	{
		code: "NI",
		en: "Nicaragua",
		ru: "Никарагуа",
		hy: "Նիկարագուա",
		tr: "Nikaragua",
		ar: "نيكاراغوا",
		zh: "尼加拉瓜",
		fa: "نیکاراگوئه",
		he: "ניקרגואה",
		fr: "Nicaragua",
		es: "Nicaragua",
		pt: "Nicarágua"
	},
	{
		code: "NE",
		en: "Niger",
		ru: "Нигер",
		hy: "Նիգեր",
		tr: "Nijer",
		ar: "النيجر",
		zh: "尼日尔",
		fa: "نیجر",
		he: "ניז'ר",
		fr: "Niger",
		es: "Níger",
		pt: "Níger"
	},
	{
		code: "NG",
		en: "Nigeria",
		ru: "Нигерия",
		hy: "Նիգերիա",
		tr: "Nijerya",
		ar: "نيجيريا",
		zh: "尼日利亚",
		fa: "نیجریه",
		he: "ניגריה",
		fr: "Nigeria",
		es: "Nigeria",
		pt: "Nigéria"
	},
	{
		code: "MK",
		en: "North Macedonia",
		ru: "Северная Македония",
		hy: "Հյուսիսային Մակեդոնիա",
		tr: "Kuzey Makedonya",
		ar: "مقدونيا الشمالية",
		zh: "北马其顿",
		fa: "مقدونیه شمالی",
		he: "מקדוניה הצפונית",
		fr: "Macédoine du Nord",
		es: "Macedonia del Norte",
		pt: "Macedônia do Norte"
	},
	{
		code: "NO",
		en: "Norway",
		ru: "Норвегия",
		hy: "Նորվեգիա",
		tr: "Norveç",
		ar: "النرويج",
		zh: "挪威",
		fa: "نروژ",
		he: "נורווגיה",
		fr: "Norvège",
		es: "Noruega",
		pt: "Noruega"
	},
	{
		code: "OM",
		en: "Oman",
		ru: "Оман",
		hy: "Օման",
		tr: "Umman",
		ar: "عمان",
		zh: "阿曼",
		fa: "عمان",
		he: "עומאן",
		fr: "Oman",
		es: "Omán",
		pt: "Omã"
	},
	{
		code: "PK",
		en: "Pakistan",
		ru: "Пакистан",
		hy: "Պակիստան",
		tr: "Pakistan",
		ar: "باكستان",
		zh: "巴基斯坦",
		fa: "پاکستان",
		he: "פקיסטן",
		fr: "Pakistan",
		es: "Pakistán",
		pt: "Paquistão"
	},
	{
		code: "PW",
		en: "Palau",
		ru: "Палау",
		hy: "Պալաու",
		tr: "Palau",
		ar: "بالاو",
		zh: "帕劳",
		fa: "پالائو",
		he: "פלאו",
		fr: "Palaos",
		es: "Palaos",
		pt: "Palau"
	},
	{
		code: "PS",
		en: "Palestine",
		ru: "Палестина",
		hy: "Պաղեստին",
		tr: "Filistin",
		ar: "فلسطين",
		zh: "巴勒斯坦",
		fa: "فلسطین",
		he: "פלסטין",
		fr: "Palestine",
		es: "Palestina",
		pt: "Palestina"
	},
	{
		code: "PA",
		en: "Panama",
		ru: "Панама",
		hy: "Պանամա",
		tr: "Panama",
		ar: "بنما",
		zh: "巴拿马",
		fa: "پاناما",
		he: "פנמה",
		fr: "Panama",
		es: "Panamá",
		pt: "Panamá"
	},
	{
		code: "PG",
		en: "Papua New Guinea",
		ru: "Папуа — Новая Гвинея",
		hy: "Պապուա Նոր Գվինեա",
		tr: "Papua Yeni Gine",
		ar: "بابوا غينيا الجديدة",
		zh: "巴布亚新几内亚",
		fa: "پاپوآ گینه نو",
		he: "פפואה גינאה החדשה",
		fr: "Papouasie-Nouvelle-Guinée",
		es: "Papúa Nueva Guinea",
		pt: "Papua-Nova Guiné"
	},
	{
		code: "PY",
		en: "Paraguay",
		ru: "Парагвай",
		hy: "Պարագվայ",
		tr: "Paraguay",
		ar: "باراغواي",
		zh: "巴拉圭",
		fa: "پاراگوئه",
		he: "פרגוואי",
		fr: "Paraguay",
		es: "Paraguay",
		pt: "Paraguai"
	},
	{
		code: "PE",
		en: "Peru",
		ru: "Перу",
		hy: "Պերու",
		tr: "Peru",
		ar: "بيرو",
		zh: "秘鲁",
		fa: "پرو",
		he: "פרו",
		fr: "Pérou",
		es: "Perú",
		pt: "Peru"
	},
	{
		code: "PH",
		en: "Philippines",
		ru: "Филиппины",
		hy: "Ֆիլիպիններ",
		tr: "Filipinler",
		ar: "الفلبين",
		zh: "菲律宾",
		fa: "فیلیپین",
		he: "הפיליפינים",
		fr: "Philippines",
		es: "Filipinas",
		pt: "Filipinas"
	},
	{
		code: "PL",
		en: "Poland",
		ru: "Польша",
		hy: "Լեհաստան",
		tr: "Polonya",
		ar: "بولندا",
		zh: "波兰",
		fa: "لهستان",
		he: "פולין",
		fr: "Pologne",
		es: "Polonia",
		pt: "Polônia"
	},
	{
		code: "PT",
		en: "Portugal",
		ru: "Португалия",
		hy: "Պորտուգալիա",
		tr: "Portekiz",
		ar: "البرتغال",
		zh: "葡萄牙",
		fa: "پرتغال",
		he: "פורטוגל",
		fr: "Portugal",
		es: "Portugal",
		pt: "Portugal"
	},
	{
		code: "QA",
		en: "Qatar",
		ru: "Катар",
		hy: "Կատար",
		tr: "Katar",
		ar: "قطر",
		zh: "卡塔尔",
		fa: "قطر",
		he: "קטאר",
		fr: "Qatar",
		es: "Catar",
		pt: "Catar"
	},
	{
		code: "RO",
		en: "Romania",
		ru: "Румыния",
		hy: "Ռումինիա",
		tr: "Romanya",
		ar: "رومانيا",
		zh: "罗马尼亚",
		fa: "رومانی",
		he: "רומניה",
		fr: "Roumanie",
		es: "Rumania",
		pt: "Romênia"
	},
	{
		code: "RU",
		en: "Russia",
		ru: "Россия",
		hy: "Ռուսաստան",
		tr: "Rusya",
		ar: "روسيا",
		zh: "俄罗斯",
		fa: "روسیه",
		he: "רוסיה",
		fr: "Russie",
		es: "Rusia",
		pt: "Rússia"
	},
	{
		code: "RW",
		en: "Rwanda",
		ru: "Руанда",
		hy: "Ռուանդա",
		tr: "Ruanda",
		ar: "رواندا",
		zh: "卢旺达",
		fa: "رواندا",
		he: "רואנדה",
		fr: "Rwanda",
		es: "Ruanda",
		pt: "Ruanda"
	},
	{
		code: "KN",
		en: "Saint Kitts and Nevis",
		ru: "Сент-Китс и Невис",
		hy: "Սենթ Քիթս և Նևիս",
		tr: "Saint Kitts ve Nevis",
		ar: "سانت كيتس ونيفيس",
		zh: "圣基茨和尼维斯",
		fa: "سنت کیتس و نویس",
		he: "סנט קיטס ונוויס",
		fr: "Saint-Kitts-et-Nevis",
		es: "San Cristóbal y Nieves",
		pt: "São Cristóvão e Nevis"
	},
	{
		code: "LC",
		en: "Saint Lucia",
		ru: "Сент-Люсия",
		hy: "Սենթ Լյուսիա",
		tr: "Saint Lucia",
		ar: "سانت لوسيا",
		zh: "圣卢西亚",
		fa: "سنت لوسیا",
		he: "סנט לוסיה",
		fr: "Sainte-Lucie",
		es: "Santa Lucía",
		pt: "Santa Lúcia"
	},
	{
		code: "VC",
		en: "Saint Vincent and the Grenadines",
		ru: "Сент-Винсент и Гренадины",
		hy: "Սենթ Վինսենթ և Գրենադիններ",
		tr: "Saint Vincent ve Grenadinler",
		ar: "سانت فنسنت والغرينادين",
		zh: "圣文森特和格林纳丁斯",
		fa: "سنت وینسنت و گرنادین‌ها",
		he: "סנט וינסנט והגרנדינים",
		fr: "Saint-Vincent-et-les-Grenadines",
		es: "San Vicente y las Granadinas",
		pt: "São Vicente e Granadinas"
	},
	{
		code: "WS",
		en: "Samoa",
		ru: "Самоа",
		hy: "Սամոա",
		tr: "Samoa",
		ar: "ساموا",
		zh: "萨摩亚",
		fa: "ساموآ",
		he: "סמואה",
		fr: "Samoa",
		es: "Samoa",
		pt: "Samoa"
	},
	{
		code: "SM",
		en: "San Marino",
		ru: "Сан-Марино",
		hy: "Սան Մարինո",
		tr: "San Marino",
		ar: "سان مارينو",
		zh: "圣马力诺",
		fa: "سان مارینو",
		he: "סן מרינו",
		fr: "Saint-Marin",
		es: "San Marino",
		pt: "San Marino"
	},
	{
		code: "ST",
		en: "Sao Tome and Principe",
		ru: "Сан-Томе и Принсипи",
		hy: "Սան Տոմե և Պրինսիպի",
		tr: "São Tomé ve Príncipe",
		ar: "ساو تومي وبرينسيبي",
		zh: "圣多美和普林西比",
		fa: "سائوتومه و پرنسیپ",
		he: "סאו טומה ופרינסיפה",
		fr: "Sao Tomé-et-Principe",
		es: "Santo Tomé y Príncipe",
		pt: "São Tome e Príncipe"
	},
	{
		code: "SA",
		en: "Saudi Arabia",
		ru: "Саудовская Аравия",
		hy: "Սաուդյան Արաբիա",
		tr: "Suudi Arabistan",
		ar: "المملكة العربية السعودية",
		zh: "沙特阿拉伯",
		fa: "عربستان سعودی",
		he: "ערב הסעודית",
		fr: "Arabie saoudite",
		es: "Arabia Saudita",
		pt: "Arábia Saudita"
	},
	{
		code: "SN",
		en: "Senegal",
		ru: "Сенегал",
		hy: "Սենեգալ",
		tr: "Senegal",
		ar: "السنغال",
		zh: "塞内加尔",
		fa: "سنگال",
		he: "סנגל",
		fr: "Sénégal",
		es: "Senegal",
		pt: "Senegal"
	},
	{
		code: "RS",
		en: "Serbia",
		ru: "Сербия",
		hy: "Սերբիա",
		tr: "Sırbistan",
		ar: "صربيا",
		zh: "塞尔维亚",
		fa: "صربستان",
		he: "סרביה",
		fr: "Serbie",
		es: "Serbia",
		pt: "Sérvia"
	},
	{
		code: "SC",
		en: "Seychelles",
		ru: "Сейшельские Острова",
		hy: "Սեյշելյան կղզիներ",
		tr: "Seyşeller",
		ar: "سيشل",
		zh: "塞舌尔",
		fa: "سیشل",
		he: "איי סיישל",
		fr: "Seychelles",
		es: "Seychelles",
		pt: "Seicheles"
	},
	{
		code: "SL",
		en: "Sierra Leone",
		ru: "Сьерра-Леоне",
		hy: "Սիերա Լեոնե",
		tr: "Sierra Leone",
		ar: "سيراليون",
		zh: "塞拉利昂",
		fa: "سیرالئون",
		he: "סיירה לאון",
		fr: "Sierra Leone",
		es: "Sierra Leona",
		pt: "Serra Leoa"
	},
	{
		code: "SG",
		en: "Singapore",
		ru: "Сингапур",
		hy: "Սինգապուր",
		tr: "Singapur",
		ar: "سنغافورة",
		zh: "新加坡",
		fa: "سنگاپور",
		he: "סינגפור",
		fr: "Singapour",
		es: "Singapur",
		pt: "Singapura"
	},
	{
		code: "SK",
		en: "Slovakia",
		ru: "Словакия",
		hy: "Սլովակիա",
		tr: "Slovakya",
		ar: "سلوفاكيا",
		zh: "斯洛伐克",
		fa: "اسلواکی",
		he: "סלובקיה",
		fr: "Slovaquie",
		es: "Eslovaquia",
		pt: "Eslováquia"
	},
	{
		code: "SI",
		en: "Slovenia",
		ru: "Словения",
		hy: "Սլովենիա",
		tr: "Slovenya",
		ar: "سلوفينيا",
		zh: "斯لوفenia",
		fa: "اسلوونی",
		he: "סלובניה",
		fr: "Slovénie",
		es: "Eslovenia",
		pt: "Eslovênia"
	},
	{
		code: "SB",
		en: "Solomon Islands",
		ru: "Соломоновы Острова",
		hy: "Սոլոմոնյան կղզիներ",
		tr: "Solomon Adaları",
		ar: "جزر سليمان",
		zh: "所罗门群岛",
		fa: "جزایر سلیمان",
		he: "איי שלמה",
		fr: "Îles Salomon",
		es: "Islas Salomón",
		pt: "Ilhas Salomão"
	},
	{
		code: "SO",
		en: "Somalia",
		ru: "Сомали",
		hy: "Սոմալի",
		tr: "Somali",
		ar: "الصومال",
		zh: "索马里",
		fa: "سومالی",
		he: "סומליה",
		fr: "Somalie",
		es: "Somalia",
		pt: "Somália"
	},
	{
		code: "ZA",
		en: "South Africa",
		ru: "Южная Африка",
		hy: "Հարավային Աֆրիկա",
		tr: "Güney Afrika",
		ar: "جنوب أفريقيا",
		zh: "南非",
		fa: "آفریقای جنوبی",
		he: "דרום אפריקה",
		fr: "Afrique du Sud",
		es: "Sudáfrica",
		pt: "África do Sul"
	},
	{
		code: "SS",
		en: "South Sudan",
		ru: "Южный Судан",
		hy: "Հարավային Սուդան",
		tr: "Güney Sudan",
		ar: "جنوب السودان",
		zh: "南苏丹",
		fa: "سودان جنوبی",
		he: "דרום סודן",
		fr: "Soudan du Sud",
		es: "Sudán del Sur",
		pt: "Sudão do Sul"
	},
	{
		code: "ES",
		en: "Spain",
		ru: "Испания",
		hy: "Իսպանիա",
		tr: "İspanya",
		ar: "إسبانيا",
		zh: "西班牙",
		fa: "اسپانیا",
		he: "ספרד",
		fr: "Espagne",
		es: "España",
		pt: "Espanha"
	},
	{
		code: "LK",
		en: "Sri Lanka",
		ru: "Шри-Ланка",
		hy: "Շրի Լանկա",
		tr: "Sri Lanka",
		ar: "سريلانكا",
		zh: "斯里兰卡",
		fa: "سری‌لانکا",
		he: "סרי לנקה",
		fr: "Sri Lanka",
		es: "Sri Lanka",
		pt: "Sri Lanka"
	},
	{
		code: "SD",
		en: "Sudan",
		ru: "Судан",
		hy: "Սուդան",
		tr: "Sudan",
		ar: "السودان",
		zh: "苏丹",
		fa: "سودان",
		he: "סודן",
		fr: "Soudan",
		es: "Sudán",
		pt: "Sudão"
	},
	{
		code: "SR",
		en: "Suriname",
		ru: "Суринам",
		hy: "Սուրինամ",
		tr: "Surinam",
		ar: "سورينام",
		zh: "苏里南",
		fa: "سورینام",
		he: "סורינם",
		fr: "Suriname",
		es: "Surinam",
		pt: "Suriname"
	},
	{
		code: "SE",
		en: "Sweden",
		ru: "Швеция",
		hy: "Շվեդիա",
		tr: "İsveç",
		ar: "السويد",
		zh: "瑞典",
		fa: "سوئد",
		he: "שוודיה",
		fr: "Suède",
		es: "Suecia",
		pt: "Suécia"
	},
	{
		code: "CH",
		en: "Switzerland",
		ru: "Швейцария",
		hy: "Շվեյցարիա",
		tr: "İsviçre",
		ar: "سويسرا",
		zh: "瑞士",
		fa: "سوئیس",
		he: "שווייץ",
		fr: "Suisse",
		es: "Suiza",
		pt: "Suíça"
	},
	{
		code: "SY",
		en: "Syria",
		ru: "Сирия",
		hy: "Սիրիա",
		tr: "Suriye",
		ar: "سوريا",
		zh: "叙利亚",
		fa: "سوریه",
		he: "סוריה",
		fr: "Syrie",
		es: "Siria",
		pt: "Síria"
	},
	{
		code: "TW",
		en: "Taiwan",
		ru: "Тайвань",
		hy: "Թայվան",
		tr: "Tayvan",
		ar: "تايوان",
		zh: "台湾",
		fa: "تایوان",
		he: "טייוואן",
		fr: "Taïwan",
		es: "Taiwán",
		pt: "Taiwan"
	},
	{
		code: "TJ",
		en: "Tajikistan",
		ru: "Таджикистан",
		hy: "Տաջիկստան",
		tr: "Tacikistan",
		ar: "طاجيكستان",
		zh: "塔吉克斯坦",
		fa: "تاجیکستان",
		he: "טג'יקיסטן",
		fr: "Tadjikistan",
		es: "Tayikistán",
		pt: "Tajiquistão"
	},
	{
		code: "TZ",
		en: "Tanzania",
		ru: "Танзания",
		hy: "Տանզանիա",
		tr: "Tanzanya",
		ar: "تنزانيا",
		zh: "坦桑尼亚",
		fa: "تانزانیا",
		he: "טנזניה",
		fr: "Tanzanie",
		es: "Tanzania",
		pt: "Tanzânia"
	},
	{
		code: "TH",
		en: "Thailand",
		ru: "Таиланд",
		hy: "Թաիլանդ",
		tr: "Tayland",
		ar: "تايلاند",
		zh: "泰国",
		fa: "تایلند",
		he: "תאילנד",
		fr: "Thaïlande",
		es: "Tailandia",
		pt: "Tailândia"
	},
	{
		code: "TL",
		en: "Timor-Leste",
		ru: "Восточный Тимор",
		hy: "Թիմոր Լեստե",
		tr: "Doğu Timor",
		ar: "تيمور الشرقية",
		zh: "东帝汶",
		fa: "تیمور شرقی",
		he: "מזרח טימור",
		fr: "Timor oriental",
		es: "Timor Oriental",
		pt: "Timor-Leste"
	},
	{
		code: "TG",
		en: "Togo",
		ru: "Того",
		hy: "Տոգո",
		tr: "Togo",
		ar: "توغو",
		zh: "多哥",
		fa: "توگو",
		he: "טוגו",
		fr: "Togo",
		es: "Togo",
		pt: "Togo"
	},
	{
		code: "TO",
		en: "Tonga",
		ru: "Тонга",
		hy: "Տոնգա",
		tr: "Tonga",
		ar: "تونغا",
		zh: "汤加",
		fa: "تونگا",
		he: "טונגה",
		fr: "Tonga",
		es: "Tonga",
		pt: "Tonga"
	},
	{
		code: "TT",
		en: "Trinidad and Tobago",
		ru: "Тринидад и Тобаго",
		hy: "Տրինիդադ և Տոբագո",
		tr: "Trinidad ve Tobago",
		ar: "ترينيداد وتوباغو",
		zh: "特立尼达和多巴哥",
		fa: "ترینیداد و توباگو",
		he: "טרינידד וטובגו",
		fr: "Trinité-et-Tobago",
		es: "Trinidad y Tobago",
		pt: "Trinidad e Tobago"
	},
	{
		code: "TN",
		en: "Tunisia",
		ru: "Тунис",
		hy: "Թունիս",
		tr: "Tunus",
		ar: "تونس",
		zh: "突尼斯",
		fa: "تونس",
		he: "תוניסיה",
		fr: "Tunisie",
		es: "Túnez",
		pt: "Tunísia"
	},
	{
		code: "TR",
		en: "Turkey",
		ru: "Турция",
		hy: "Թուրքիա",
		tr: "Türkiye",
		ar: "تركيا",
		zh: "土耳其",
		fa: "ترکیه",
		he: "טורקיה",
		fr: "Turquie",
		es: "Turquía",
		pt: "Turquia"
	},
	{
		code: "TM",
		en: "Turkmenistan",
		ru: "Туркменистан",
		hy: "Թուրքմենստան",
		tr: "Türkmenistan",
		ar: "تركمانستان",
		zh: "土库曼斯坦",
		fa: "ترکمنستان",
		he: "טורקמניסטן",
		fr: "Turkménistan",
		es: "Turkmenistán",
		pt: "Turcomenistão"
	},
	{
		code: "TV",
		en: "Tuvalu",
		ru: "Тувалу",
		hy: "Տուվալու",
		tr: "Tuvalu",
		ar: "توفالو",
		zh: "图瓦卢",
		fa: "تووالو",
		he: "טובאלו",
		fr: "Tuvalu",
		es: "Tuvalu",
		pt: "Tuvalu"
	},
	{
		code: "UG",
		en: "Uganda",
		ru: "Уганда",
		hy: "Ուգանդա",
		tr: "Uganda",
		ar: "أوغندا",
		zh: "乌干达",
		fa: "اوگاندا",
		he: "אוגנדה",
		fr: "Ouganda",
		es: "Uganda",
		pt: "Uganda"
	},
	{
		code: "UA",
		en: "Ukraine",
		ru: "Украина",
		hy: "Ուկրաինա",
		tr: "Ukrayna",
		ar: "أوكرانيا",
		zh: "乌克兰",
		fa: "اوکراین",
		he: "אוקראינה",
		fr: "Ukraine",
		es: "Ucrania",
		pt: "Ucrânia"
	},
	{
		code: "AE",
		en: "United Arab Emirates",
		ru: "Объединённые Арабские Эмираты",
		hy: "Արաբական Միացյալ Էմիրություններ",
		tr: "Birleşik Arap Emirlikleri",
		ar: "الإمارات العربية المتحدة",
		zh: "阿拉伯联合酋长国",
		fa: "امارات متحده عربی",
		he: "איחוד האמירויות הערביות",
		fr: "Émirats arabes unis",
		es: "Emiratos Árabes Unidos",
		pt: "Emirados Árabes Unidos"
	},
	{
		code: "GB",
		en: "United Kingdom",
		ru: "Великобритания",
		hy: "Միացյալ Թագավորություն",
		tr: "Birleşik Krallık",
		ar: "المملكة المتحدة",
		zh: "英国",
		fa: "بریتانیا",
		he: "הממלכה המאוחדת",
		fr: "Royaume-Uni",
		es: "Reino Unido",
		pt: "Reino Unido"
	},
	{
		code: "US",
		en: "United States",
		ru: "Соединённые Штаты",
		hy: "Ամերիկայի Միացյալ Նահանգներ",
		tr: "Amerika Birleşik Devletleri",
		ar: "الولايات المتحدة",
		zh: "美国",
		fa: "ایالات متحده",
		he: "ארצות הברית",
		fr: "États-Unis",
		es: "Estados Unidos",
		pt: "Estados Unidos"
	},
	{
		code: "UY",
		en: "Uruguay",
		ru: "Уругвай",
		hy: "Ուրուգվայ",
		tr: "Uruguay",
		ar: "أوروغواي",
		zh: "乌拉圭",
		fa: "اروگوئه",
		he: "אורוגוואי",
		fr: "Uruguay",
		es: "Uruguay",
		pt: "Uruguai"
	},
	{
		code: "UZ",
		en: "Uzbekistan",
		ru: "Узбекистан",
		hy: "Ուզբեկստան",
		tr: "Özbekistan",
		ar: "أوزبكستان",
		zh: "乌兹别克斯坦",
		fa: "ازبکستان",
		he: "אוזבקיסטן",
		fr: "Ouzbékistan",
		es: "Uzbekistán",
		pt: "Uzbequistão"
	},
	{
		code: "VU",
		en: "Vanuatu",
		ru: "Вануату",
		hy: "Վանուատու",
		tr: "Vanuatu",
		ar: "فانواتو",
		zh: "瓦努阿图",
		fa: "وانواتو",
		he: "ונואטו",
		fr: "Vanuatu",
		es: "Vanuatu",
		pt: "Vanuatu"
	},
	{
		code: "VA",
		en: "Vatican City",
		ru: "Ватикан",
		hy: "Վատիկան",
		tr: "Vatikan",
		ar: "الفاتيكان",
		zh: "梵蒂冈",
		fa: "واتیکان",
		he: "הוותיקן",
		fr: "Vatican",
		es: "Ciudad del Vaticano",
		pt: "Vaticano"
	},
	{
		code: "VE",
		en: "Venezuela",
		ru: "Венесуэла",
		hy: "Վենեսուելա",
		tr: "Venezuela",
		ar: "فنزويلا",
		zh: "委内瑞拉",
		fa: "ونزوئلا",
		he: "ונצואלה",
		fr: "Venezuela",
		es: "Venezuela",
		pt: "Venezuela"
	},
	{
		code: "VN",
		en: "Vietnam",
		ru: "Вьетнам",
		hy: "Վիետնամ",
		tr: "Vietnam",
		ar: "فيتنام",
		zh: "越南",
		fa: "ویتنام",
		he: "וייטנאם",
		fr: "Viêt Nam",
		es: "Vietnam",
		pt: "Vietnã"
	},
	{
		code: "YE",
		en: "Yemen",
		ru: "Йемен",
		hy: "Եմեն",
		tr: "Yemen",
		ar: "اليمن",
		zh: "也门",
		fa: "یمن",
		he: "תימן",
		fr: "Yémen",
		es: "Yemen",
		pt: "Iêmen"
	},
	{
		code: "ZM",
		en: "Zambia",
		ru: "Замбия",
		hy: "Զամբիա",
		tr: "Zambiya",
		ar: "زامبيا",
		zh: "赞比亚",
		fa: "زامبیا",
		he: "זמביה",
		fr: "Zambie",
		es: "Zambia",
		pt: "Zâmbia"
	},
	{
		code: "ZW",
		en: "Zimbabwe",
		ru: "Зимбабве",
		hy: "Զիմբաբվե",
		tr: "Zimbabve",
		ar: "زيمبابوي",
		zh: "津巴布韦",
		fa: "زیمبابوه",
		he: "זימבבואה",
		fr: "Zimbabwe",
		es: "Zimbabue",
		pt: "Zimbábue"
	}
];

const IMPORTANT_FIELDS = {
	user: [{ key: "username", type: "string", required: true, unique: true }]
};

const filteredMenu = filterMenu(menu);
const filteredHeaderMenu = filterMenu(headerMenu);

const config = {
	api,
	theme: themeSettings,
	session,
	maps,
	countries,
	steps,
	brand,
	menu: filteredMenu,
	originalMenu: menu,
	headerMenu: filteredHeaderMenu,
	originalHeaderMenu: headerMenu,
	notificationSettings,
	routerSpeeds,
	rams,
	roms,
	sortAndRemoveDuplicates,
	hasPagePermission,
	formattedNumber,
	version: process.env.VERSION || "2.2.68",
	lastUpdate: "01/11/2026",
	IMPORTANT_FIELDS
};

export default config;
